import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DashboardV3Settings from '../components/settings/DashboardV3Settings';
import { useDashboardV3 } from '../contexts/DashboardV3Context';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Save, Download, Upload, Search, Edit, FileText, Database, HardDrive, Archive, Code, AlertTriangle, CheckCircle, Info, RefreshCw, Package, Building2, Eye, CalendarDays, Activity, ChevronDown, ChevronLeft, ChevronRight, XCircle, Check, User, Zap, ArrowUpDown, MapPin, Satellite, Truck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import AppHeader from '../components/AppHeader';
import { useNavigate } from 'react-router-dom';
import FileManager from '../components/FileManager';
import DashboardManager from '../components/DashboardManager';
import FutureFeatures from './FutureFeatures';
import UserOnboardingEmails from '../components/UserOnboardingEmails';
import AdditionalProductDataUploadModal from '../components/AdditionalProductDataUploadModal';
import { TransportSettingsTab } from '../components/TransportSettingsTab';
import { format } from 'date-fns';

// Import slanted tabs CSS
import '../styles/slanted-tabs.css';

// Import extracted settings components
import { BackupManager, StorageMonitor, PermissionsManager, PONumberStartSetting, CRMSecurity, PricingSettings, HOSupportSeedButton } from '../components/settings';
// CustomerDatabaseManager removed — GDPR retirement
import ProjectsDatabaseManager from '../components/ProjectsDatabaseManager';
import PhocasCustomerDatabase from '../components/PhocasCustomerDatabase';
import PhocasSupplierProfile from '../components/PhocasSupplierProfile';
import FilesAdmin from '../components/FilesAdmin';
import BranchManager from '../components/BranchManager';
import BranchDirectory from '../components/BranchDirectory';
import EmailMfaSettings from '../components/settings/EmailMfaSettings';
import MfaAdminPage from '../components/settings/MfaAdminPage';
import NotificationsManager from '../components/settings/NotificationsManager';
import NotificationBarSettings from '../components/settings/NotificationBarSettings';
import { ManageChangesSettings, TerraAssistantPanel } from '../components/settings';
import { ZohoIntegrationSettings } from '../components/ZohoIntegrationSettings';
import { McpCodeAccessSettings } from '../components/McpCodeAccessSettings';
import { McpDataAccessSettings } from '../components/McpDataAccessSettings';
import { TerraIntegrationSettings } from '../components/TerraIntegrationSettings';
import { BarbourIntegrationSettings } from '../components/BarbourIntegrationSettings';
import { EightXEightSettings } from '../components/EightXEightSettings';
import { SamsaraSettings } from '../components/SamsaraSettings';
import { BaseLocationMapPicker } from '../components/settings/BaseLocationMapPicker';
import { HistoricalDataCentre } from '../components/settings/HistoricalDataCentre';
import { EightXEightAnalyticsSettings } from '../components/EightXEightAnalyticsSettings';
import ZohoApiUsagePanel from '../components/ZohoApiUsagePanel';
import SoSSettingsPanel from '../components/SoSSettingsPanel';
import AutomationsPanel from '../components/AutomationsPanel';
import CloudflareIncomingSettings from '../components/CloudflareIncomingSettings';
import { TeamManagementSettings } from '../components/settings/TeamManagementSettings';
import ManageGlobalHeaderSettings from '../components/settings/ManageGlobalHeaderSettings';
import SpacesBuilderPanel from '../components/settings/SpacesBuilderPanel';


// The categories shown along the sub-header, and the tabs each one holds. Choosing a category shows its tabs along the top of the content, and opens the first one.
const SETTINGS_CATEGORIES = [
  { id: 'account', label: 'Account', tabs: ['general', 'dashboard-v3', 'permissions', 'crm-security', 'user-emails', 'email-mfa', 'mfa', 'teams'] },
  { id: 'user-interface', label: 'User Interface', tabs: ['ui', 'global-header', 'spaces-builder'] },
  { id: 'system', label: 'System', tabs: ['branches', 'automations', 'dashboard-manager', 'sales-back-office', 'adhoc'] },
  { id: 'transport', label: 'Transport', tabs: ['vehicles', 'transport-settings'] },
  { id: 'notifications', label: 'Notifications', tabs: ['notifications', 'notification-bar'] },
  { id: 'files', label: 'Files', tabs: ['files', 'code', 'backup', 'files-admin'] },
  { id: 'data', label: 'Data', tabs: ['database', 'stock-database', 'pricing', 'sos-settings', 'projects-database', 'phocas-customer-database', 'phocas-supplier-profile', 'customer-database'] },
  { id: 'admin', label: 'Admin', tabs: ['integrations', 'eight-x8-analytics', 'manage-changes', 'terra-assistant', 'usage', 'gdpr-protocol', 'cloudflare-incoming', 'future-features'] },
];
const categoryOfTab = (tab) => (SETTINGS_CATEGORIES.find((category) => category.tabs.includes(tab)) || SETTINGS_CATEGORIES[0]).id;

const Settings = ({ user, onUserUpdate, onImpersonate }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'general';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [settingsCategory, setSettingsCategory] = useState(() => categoryOfTab(initialTab));
  const [shownCategories, setShownCategories] = useState(() => SETTINGS_CATEGORIES.map((category) => category.id));
  const settingsNavRef = useRef(null);
  // The category follows the open tab (a link with ?tab=, or the phone drop-down, can open a tab in any category).
  useEffect(() => { setSettingsCategory(categoryOfTab(activeTab)); }, [activeTab]);
  // A category with no tab this person may see is not shown. The tabs are only drawn for the ones they may see, so the navigation is asked.
  useEffect(() => {
    const nav = settingsNavRef.current;
    if (!nav) return;
    const found = SETTINGS_CATEGORIES.filter((category) => nav.querySelector(`[data-cat="${category.id}"] [role="tab"]`)).map((category) => category.id);
    setShownCategories((previous) => (previous.join('|') === found.join('|') ? previous : found));
  });
  // Choosing another category opens the first tab this person may see in it (every tab's test id is its name followed by -tab).
  const selectSettingsCategory = (id) => {
    setSettingsCategory(id);
    if (categoryOfTab(activeTab) === id) return;
    const first = settingsNavRef.current && settingsNavRef.current.querySelector(`[data-cat="${id}"] [role="tab"]`);
    const testId = first && first.getAttribute('data-testid');
    if (testId) setActiveTab(testId.replace(/-tab$/, ''));
  };
  
  // Color constants
  const enconOrange = '#ee7203';
  const enconLightGrey = '#e2e8f0';
  const enconBlack = '#1a202c';
  const enconGrey = '#718096';
  
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDateDialog, setShowDateDialog] = useState(false);
  const [showVehicleDetailsDialog, setShowVehicleDetailsDialog] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showCosts, setShowCosts] = useState(false); // Simple show/hide toggle
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [vehicleDetailsDate, setVehicleDetailsDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Branch-specific vehicle settings
  const [vehicleBranch, setVehicleBranch] = useState('C30');
  const [vehiclesSubTab, setVehiclesSubTab] = useState('settings');
  const [branchVehicleSettings, setBranchVehicleSettings] = useState({
    C30: { base_postcode: 'B6 7AF', base_location_override: null, default_vehicles: [] },
    E70: { base_postcode: 'B70 0TX', base_location_override: null, default_vehicles: [] }
  });
  const [loadingBranchSettings, setLoadingBranchSettings] = useState(false);

  // Home page background theme — personal per-user preference
  const [homeTheme, setHomeTheme] = useState(user?.home_theme || 'light');
  const [savingHomeTheme, setSavingHomeTheme] = useState(false);

  const handleHomeThemeToggle = async (checked) => {
    const newTheme = checked ? 'dark' : 'light';
    setHomeTheme(newTheme);
    setSavingHomeTheme(true);
    try {
      await api.put(`${API}/auth/theme`, { home_theme: newTheme });
      onUserUpdate?.(prev => ({ ...prev, home_theme: newTheme }));
      toast.success(`Home page background set to ${newTheme === 'dark' ? 'Dark Gradient' : 'White'}`);
    } catch (error) {
      setHomeTheme(newTheme === 'dark' ? 'light' : 'dark'); // revert on failure
      toast.error('Failed to save home page theme preference');
    } finally {
      setSavingHomeTheme(false);
    }
  };

  // Global Sub-Header style — personal per-user preference (tri-state: null = "not set,
  // use the org default"). Mutually exclusive with each other: turning one on turns the
  // other off, both client-side and enforced again server-side in PUT /auth/header-style.
  const [subheaderDarkGradient, setSubheaderDarkGradient] = useState(user?.subheader_dark_gradient ?? null);
  const [subheaderOrangeBar, setSubheaderOrangeBar] = useState(user?.subheader_orange_bar ?? null);
  const [subheaderOrangeWhite, setSubheaderOrangeWhite] = useState(user?.subheader_orange_white ?? null);
  const [savingHeaderStyle, setSavingHeaderStyle] = useState(false);
  const handleSubheaderDarkGradientToggle = async (checked) => {
    const prevDark = subheaderDarkGradient;
    const prevOrange = subheaderOrangeBar;
    const prevOrangeWhite = subheaderOrangeWhite;
    setSubheaderDarkGradient(checked);
    if (checked) { setSubheaderOrangeBar(false); setSubheaderOrangeWhite(false); }
    setSavingHeaderStyle(true);
    try {
      const payload = { subheader_dark_gradient: checked };
      if (checked) { payload.subheader_orange_bar = false; payload.subheader_orange_white = false; }
      await api.put(`${API}/auth/header-style`, payload);
      onUserUpdate?.(prev => ({ ...prev, ...payload }));
      toast.success(checked ? 'Sub-header set to Dark Gradient' : 'Sub-header dark gradient turned off');
    } catch (error) {
      setSubheaderDarkGradient(prevDark);
      setSubheaderOrangeBar(prevOrange);
      setSubheaderOrangeWhite(prevOrangeWhite);
      toast.error('Failed to save sub-header style preference');
    } finally {
      setSavingHeaderStyle(false);
    }
  };
  const handleSubheaderOrangeBarToggle = async (checked) => {
    const prevDark = subheaderDarkGradient;
    const prevOrange = subheaderOrangeBar;
    const prevOrangeWhite = subheaderOrangeWhite;
    setSubheaderOrangeBar(checked);
    if (checked) { setSubheaderDarkGradient(false); setSubheaderOrangeWhite(false); }
    setSavingHeaderStyle(true);
    try {
      const payload = { subheader_orange_bar: checked };
      if (checked) { payload.subheader_dark_gradient = false; payload.subheader_orange_white = false; }
      await api.put(`${API}/auth/header-style`, payload);
      onUserUpdate?.(prev => ({ ...prev, ...payload }));
      toast.success(checked ? 'Sub-header set to Orange Bar' : 'Sub-header orange bar turned off');
    } catch (error) {
      setSubheaderDarkGradient(prevDark);
      setSubheaderOrangeBar(prevOrange);
      setSubheaderOrangeWhite(prevOrangeWhite);
      toast.error('Failed to save sub-header style preference');
    } finally {
      setSavingHeaderStyle(false);
    }
  };
  const handleSubheaderOrangeWhiteToggle = async (checked) => {
    const prevDark = subheaderDarkGradient;
    const prevOrange = subheaderOrangeBar;
    const prevOrangeWhite = subheaderOrangeWhite;
    setSubheaderOrangeWhite(checked);
    if (checked) { setSubheaderDarkGradient(false); setSubheaderOrangeBar(false); }
    setSavingHeaderStyle(true);
    try {
      const payload = { subheader_orange_white: checked };
      if (checked) { payload.subheader_dark_gradient = false; payload.subheader_orange_bar = false; }
      await api.put(`${API}/auth/header-style`, payload);
      onUserUpdate?.(prev => ({ ...prev, ...payload }));
      toast.success(checked ? 'Sub-header set to Orange to White' : 'Sub-header orange to white turned off');
    } catch (error) {
      setSubheaderDarkGradient(prevDark);
      setSubheaderOrangeBar(prevOrange);
      setSubheaderOrangeWhite(prevOrangeWhite);
      toast.error('Failed to save sub-header style preference');
    } finally {
      setSavingHeaderStyle(false);
    }
  };
  // Global Header (main white row, above the sub-header) dark gradient — personal per-user
  // preference, independent of the two sub-header toggles above. Same tri-state pattern.
  const [headerDarkGradient, setHeaderDarkGradient] = useState(user?.header_dark_gradient ?? null);
  const [savingHeaderDarkGradient, setSavingHeaderDarkGradient] = useState(false);
  const handleHeaderDarkGradientToggle = async (checked) => {
    const prev = headerDarkGradient;
    setHeaderDarkGradient(checked);
    setSavingHeaderDarkGradient(true);
    try {
      await api.put(`${API}/auth/header-style`, { header_dark_gradient: checked });
      onUserUpdate?.(prev2 => ({ ...prev2, header_dark_gradient: checked }));
      toast.success(checked ? 'Global Header set to Dark Gradient' : 'Global Header dark gradient turned off');
    } catch (error) {
      setHeaderDarkGradient(prev);
      toast.error('Failed to save header style preference');
    } finally {
      setSavingHeaderDarkGradient(false);
    }
  };
  // Default Sub-Header style for all users — Super Admin only. This is the starting style
  // every user without their own personal preference sees; it never overwrites a user who
  // has already made their own choice above.
  const [defaultHeaderStyle, setDefaultHeaderStyle] = useState({ default_subheader_dark_gradient: false, default_subheader_orange_bar: false, default_subheader_orange_white: false, default_header_dark_gradient: false });
  const [loadingDefaultHeaderStyle, setLoadingDefaultHeaderStyle] = useState(false);
  const [savingDefaultHeaderStyle, setSavingDefaultHeaderStyle] = useState(false);
  const loadDefaultHeaderStyle = async () => {
    setLoadingDefaultHeaderStyle(true);
    try {
      const res = await api.get(`${API}/settings/header-style-default`);
      setDefaultHeaderStyle(res.data);
    } catch (error) {
      toast.error('Failed to load default sub-header style');
    } finally {
      setLoadingDefaultHeaderStyle(false);
    }
  };
  useEffect(() => {
    if (user?.role === 'super_admin') loadDefaultHeaderStyle();
  }, [user?.role]);
  const handleDefaultHeaderDarkGradientToggle = async (checked) => {
    setSavingDefaultHeaderStyle(true);
    try {
      const res = await api.put(`${API}/settings/header-style-default`, { default_header_dark_gradient: checked });
      setDefaultHeaderStyle(prev => ({ ...prev, ...res.data }));
      toast.success('Default Global Header style updated for new/unset users');
    } catch (error) {
      toast.error('Failed to update default header style');
    } finally {
      setSavingDefaultHeaderStyle(false);
    }
  };
  const handleDefaultDarkGradientToggle = async (checked) => {
    setSavingDefaultHeaderStyle(true);
    try {
      const payload = { default_subheader_dark_gradient: checked };
      if (checked) { payload.default_subheader_orange_bar = false; payload.default_subheader_orange_white = false; }
      const res = await api.put(`${API}/settings/header-style-default`, payload);
      setDefaultHeaderStyle(prev => ({ ...prev, ...res.data }));
      toast.success('Default sub-header style updated for new/unset users');
    } catch (error) {
      toast.error('Failed to update default sub-header style');
    } finally {
      setSavingDefaultHeaderStyle(false);
    }
  };
  const handleDefaultOrangeBarToggle = async (checked) => {
    setSavingDefaultHeaderStyle(true);
    try {
      const payload = { default_subheader_orange_bar: checked };
      if (checked) { payload.default_subheader_dark_gradient = false; payload.default_subheader_orange_white = false; }
      const res = await api.put(`${API}/settings/header-style-default`, payload);
      setDefaultHeaderStyle(prev => ({ ...prev, ...res.data }));
      toast.success('Default sub-header style updated for new/unset users');
    } catch (error) {
      toast.error('Failed to update default sub-header style');
    } finally {
      setSavingDefaultHeaderStyle(false);
    }
  };
  const handleDefaultOrangeWhiteToggle = async (checked) => {
    setSavingDefaultHeaderStyle(true);
    try {
      const payload = { default_subheader_orange_white: checked };
      if (checked) { payload.default_subheader_dark_gradient = false; payload.default_subheader_orange_bar = false; }
      const res = await api.put(`${API}/settings/header-style-default`, payload);
      setDefaultHeaderStyle(prev => ({ ...prev, ...res.data }));
      toast.success('Default sub-header style updated for new/unset users');
    } catch (error) {
      toast.error('Failed to update default sub-header style');
    } finally {
      setSavingDefaultHeaderStyle(false);
    }
  };
  
  // UI Preview — feature-flag toggles for the in-progress full-site UI rebuilds (Modal UI v2,
  // Global Header/Nav UI v2). Super-admin only, hidden entirely from every other role. Each
  // toggle has a personal "just for me" switch (previewing while building) and a site-wide
  // "everyone" switch (releasing it once ready) — see PUT /auth/ui-preview and
  // GET/PUT /settings/ui-preview.
  const [modalUiV2Personal, setModalUiV2Personal] = useState(!!user?.modal_ui_v2_enabled);
  const [navUiV2Personal, setNavUiV2Personal] = useState(!!user?.nav_ui_v2_enabled);
  const [uiPreviewSiteWide, setUiPreviewSiteWide] = useState({ modal_ui_v2_site_wide: false, nav_ui_v2_site_wide: false });
  const [loadingUiPreview, setLoadingUiPreview] = useState(false);
  const [savingUiPreview, setSavingUiPreview] = useState(false);
  const { refreshSiteWide: refreshGlobalUiPreview } = useUIPreview();
  const loadUiPreviewSiteWide = async () => {
    setLoadingUiPreview(true);
    try {
      const res = await api.get(`${API}/settings/ui-preview`);
      setUiPreviewSiteWide(res.data);
    } catch (error) {
      toast.error('Failed to load site-wide UI preview settings');
    } finally {
      setLoadingUiPreview(false);
    }
  };
  useEffect(() => {
    if (user?.role === 'super_admin') loadUiPreviewSiteWide();
  }, [user?.role]);
  const handleModalUiV2PersonalToggle = async (checked) => {
    const prev = modalUiV2Personal;
    setModalUiV2Personal(checked);
    setSavingUiPreview(true);
    try {
      await api.put(`${API}/auth/ui-preview`, { modal_ui_v2_enabled: checked });
      onUserUpdate?.(p => ({ ...p, modal_ui_v2_enabled: checked }));
      toast.success(checked ? 'Modal UI v2 preview turned on for your account' : 'Modal UI v2 preview turned off for your account');
    } catch (error) {
      setModalUiV2Personal(prev);
      toast.error('Failed to save Modal UI v2 preference');
    } finally {
      setSavingUiPreview(false);
    }
  };
  const handleNavUiV2PersonalToggle = async (checked) => {
    const prev = navUiV2Personal;
    setNavUiV2Personal(checked);
    setSavingUiPreview(true);
    try {
      await api.put(`${API}/auth/ui-preview`, { nav_ui_v2_enabled: checked });
      onUserUpdate?.(p => ({ ...p, nav_ui_v2_enabled: checked }));
      toast.success(checked ? 'Nav/Header UI v2 preview turned on for your account' : 'Nav/Header UI v2 preview turned off for your account');
    } catch (error) {
      setNavUiV2Personal(prev);
      toast.error('Failed to save Nav/Header UI v2 preference');
    } finally {
      setSavingUiPreview(false);
    }
  };
  const handleModalUiV2SiteWideToggle = async (checked) => {
    setSavingUiPreview(true);
    try {
      const res = await api.put(`${API}/settings/ui-preview`, { modal_ui_v2_site_wide: checked });
      setUiPreviewSiteWide(prev => ({ ...prev, ...res.data }));
      refreshGlobalUiPreview();
      toast.success(checked ? 'Modal UI v2 is now live for everyone' : 'Modal UI v2 turned off site-wide');
    } catch (error) {
      toast.error('Failed to update site-wide Modal UI v2 setting');
    } finally {
      setSavingUiPreview(false);
    }
  };
  const handleNavUiV2SiteWideToggle = async (checked) => {
    setSavingUiPreview(true);
    try {
      const res = await api.put(`${API}/settings/ui-preview`, { nav_ui_v2_site_wide: checked });
      setUiPreviewSiteWide(prev => ({ ...prev, ...res.data }));
      refreshGlobalUiPreview();
      toast.success(checked ? 'Nav/Header UI v2 is now live for everyone' : 'Nav/Header UI v2 turned off site-wide');
    } catch (error) {
      toast.error('Failed to update site-wide Nav/Header UI v2 setting');
    } finally {
      setSavingUiPreview(false);
    }
  };

  // Home page version (Settings > User Interface). OG Dashboard is the saved copy of the home page as it was before the rebuild (pages/HubOG.jsx). While
  // "OG Dashboard" is on (it starts on) EVERYONE keeps it, whatever is being built. "Super admin view" starts on too: turn it off and only super admins see the new
  // home page (everyone else keeps OG Dashboard). Both are stored on the server (PUT /dashboard-v3/settings) and only a super admin can change them.
  const dashboardV3 = useDashboardV3();
  const [ogLocal, setOgLocal] = useState(null);
  const [savingOg, setSavingOg] = useState(false);
  const ogDashboardOn = (ogLocal || dashboardV3.settings || {}).og_dashboard !== false;
  const ogSuperAdminOn = (ogLocal || dashboardV3.settings || {}).og_dashboard_super_admin !== false;
  const handleOgToggle = async (key, checked, message) => {
    setSavingOg(true);
    try {
      const res = await api.put(`${API}/dashboard-v3/settings`, { [key]: checked });
      setOgLocal(res.data);
      dashboardV3.refresh();
      toast.success(message);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save the home page setting');
    } finally {
      setSavingOg(false);
    }
  };

  // The tab strip is frozen under the sub-header, so the page scrolls beneath it. How far down it sticks depends on the sub-header (where it sticks, and how
  // tall it is), so that is measured here and handed to the stylesheet (--v3-settings-stick).
  useEffect(() => {
    const nav = settingsNavRef.current;
    const slot = document.querySelector('.v3-toolbar-slot');
    if (!nav || !slot) return undefined;
    const publish = () => {
      const stuckAt = parseFloat(window.getComputedStyle(slot).top) || 0;
      nav.style.setProperty('--v3-settings-stick', `${Math.round(stuckAt + slot.offsetHeight)}px`);
    };
    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    if (observer) observer.observe(slot);
    window.addEventListener('resize', publish);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', publish);
    };
  }, []);

  // Check if user has access to both branches
  const hasAccessToBothBranches = user?.role === 'super_admin' || 
    (user?.branch_ids?.includes('C30') && user?.branch_ids?.includes('E70')) ||
    (user?.branch_id === 'C30' && user?.branch_ids?.includes('E70')) ||
    (user?.branch_id === 'E70' && user?.branch_ids?.includes('C30'));
  
  // Sales Back Office state
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [uploadingBudget, setUploadingBudget] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [growthRates, setGrowthRates] = useState({
    "Structural": 0.0,
    "Merchants": 0.0,
    "Technical": 0.0,
    "Neville Long": 0.0
  });

  // Database Management state
  const [selectedCollection, setSelectedCollection] = useState('');
  const [collectionInfo, setCollectionInfo] = useState(null);

  // M4 Images and Daily Backups state
  const [samsaraVehicles, setSamsaraVehicles] = useState([]);
  const [samsaraVehiclesLoaded, setSamsaraVehiclesLoaded] = useState(false);
  const fetchSamsaraVehicles = async () => {
    if (samsaraVehiclesLoaded) return;
    try {
      const res = await api.get(`${API}/admin/integrations/samsara/vehicles`);
      setSamsaraVehicles(res.data.vehicles || []);
    } catch (e) { /* Samsara not configured yet or non-super-admin — dropdown just stays empty, manual paste still works */ }
    finally { setSamsaraVehiclesLoaded(true); }
  };
  // Fetch once up-front (not just lazily on dropdown open) so a vehicle already
  // mapped to a Samsara ID shows its real plate/name in the closed dropdown
  // immediately, instead of rendering blank until that specific dropdown is opened.
  useEffect(() => { fetchSamsaraVehicles(); }, []);
  const [m4Images, setM4Images] = useState([]);
  const [m4ImagesModalOpen, setM4ImagesModalOpen] = useState(false);
  const [dailyBackups, setDailyBackups] = useState([]);
  const [dailyBackupsModalOpen, setDailyBackupsModalOpen] = useState(false);
  
  // Stock Management History state
  const [backupSubTab, setBackupSubTab] = useState('system'); // 'system' or 'stock-history'
  const [stockHistoryTab, setStockHistoryTab] = useState('history'); // 'history' or 'manual-saves'
  const [stockHistory, setStockHistory] = useState([]);
  const [loadingStockHistory, setLoadingStockHistory] = useState(false);
  const [stockHistoryFilter, setStockHistoryFilter] = useState('all'); // 'all', 'weekly', 'daily'
  const [manualSaves, setManualSaves] = useState([]);
  const [loadingManualSaves, setLoadingManualSaves] = useState(false);
  const manualSaveFileInputRef = useRef(null);

  // AI Usage Logs state
  const [aiUsageLogs, setAiUsageLogs] = useState([]);
  const [aiUsageStats, setAiUsageStats] = useState({
    total_logs: 0,
    total_estimated_credits: 0,
    total_estimated_gbp: 0,
    usage_by_feature: [],
    usage_by_user: [],
    usage_by_month: [],
    usage_by_model: [],
    available_ai_features: {}
  });
  const [loadingAiLogs, setLoadingAiLogs] = useState(false);
  const [aiLogPeriod, setAiLogPeriod] = useState(() => {
    const now = new Date();
    return { type: 'month', year: now.getFullYear(), month: now.getMonth() + 1 };
  });
  const [periodPickerOpen, setPeriodPickerOpen] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  
  // Non-AI Usage Logs state
  const [nonAiUsageLogs, setNonAiUsageLogs] = useState([]);
  const [nonAiUsageStats, setNonAiUsageStats] = useState({
    total_logs: 0,
    usage_by_feature: [],
    usage_by_user: [],
    usage_by_month: [],
    available_non_ai_features: {}
  });
  const [loadingNonAiLogs, setLoadingNonAiLogs] = useState(false);
  const [showStorageMonitor, setShowStorageMonitor] = useState(false);

  // Stock Database state
  const [showUploadMasterModal, setShowUploadMasterModal] = useState(false);
  const [showUploadAdditionalModal, setShowUploadAdditionalModal] = useState(false);
  const [additionalStockFiles, setAdditionalStockFiles] = useState([]);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showUngroupedModal, setShowUngroupedModal] = useState(false);
  const [masterStockCount, setMasterStockCount] = useState(0);
  const [showMasterDatabaseSection, setShowMasterDatabaseSection] = useState(false);
  const [debouncedAnalysisSearch, setDebouncedAnalysisSearch] = useState('');

  // Debounce search for supplier groups modal
  const [productGroups, setProductGroups] = useState([]);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [stockFiles, setStockFiles] = useState([]);
  const [stockValuations, setStockValuations] = useState([]);
  const [showValuationsModal, setShowValuationsModal] = useState(false);
  const [ungroupedCount, setUngroupedCount] = useState(0);
  const [ungroupedProducts, setUngroupedProducts] = useState([]);
  const [newProduct, setNewProduct] = useState({ code: '', name: '' });
  const [uploading, setUploading] = useState(false);
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [inventoryResults, setInventoryResults] = useState([]);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    category: 'structural',
    product_group_ids: []
  });
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [showAllGroups, setShowAllGroups] = useState(false);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [productGroupSearchQuery, setProductGroupSearchQuery] = useState('');
  const [supplierModalGroupSearch, setSupplierModalGroupSearch] = useState('');
  const [showMarketSectorsModal, setShowMarketSectorsModal] = useState(false);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [newMarketSector, setNewMarketSector] = useState('structural');
  
  // NEW Supplier Groups state (groups multiple Product Analysis Groups)
  const [manualSupplierGroups, setManualSupplierGroups] = useState([]);
  const [showNewSupplierGroupModal, setShowNewSupplierGroupModal] = useState(false);
  const [newSupplierGroupSearchQuery, setNewSupplierGroupSearchQuery] = useState('');
  const [newManualSupplierGroup, setNewManualSupplierGroup] = useState({
    name: '',
    category: 'structural',
    product_analysis_group_ids: [],
    crm_supplier_name: ''
  });
  const [editingManualSupplierGroup, setEditingManualSupplierGroup] = useState(null);
  const [analysisGroupSearchInModal, setAnalysisGroupSearchInModal] = useState('');
  const [showAssignedAnalysisGroups, setShowAssignedAnalysisGroups] = useState(false); // Toggle for showing already-assigned groups

  // Additional Product Data state (M4 Product Codes with weights/keywords)
  const [additionalProductDataCount, setAdditionalProductDataCount] = useState(0);
  const [additionalProductData, setAdditionalProductData] = useState([]);
  const [additionalProductDataSearch, setAdditionalProductDataSearch] = useState('');
  const [additionalProductDataPage, setAdditionalProductDataPage] = useState(1);
  const [additionalProductDataTotalPages, setAdditionalProductDataTotalPages] = useState(1);
  const [showAdditionalProductModal, setShowAdditionalProductModal] = useState(false);
  const [selectedAdditionalProduct, setSelectedAdditionalProduct] = useState(null);
  const [additionalProductEditWeight, setAdditionalProductEditWeight] = useState('');
  const [additionalProductEditKeywords, setAdditionalProductEditKeywords] = useState('');
  const [additionalProductEditCostFactor, setAdditionalProductEditCostFactor] = useState('');
  const [additionalProductSafetyWord, setAdditionalProductSafetyWord] = useState('');
  const [uploadingAdditionalProducts, setUploadingAdditionalProducts] = useState(false);
  const [showAdditionalProductUploadModal, setShowAdditionalProductUploadModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAnalysisSearch(analysisGroupSearchInModal);
    }, 300);
    return () => clearTimeout(timer);
  }, [analysisGroupSearchInModal]);


  const loadCollectionInfo = async (collectionName) => {
    if (!collectionName) {
      setCollectionInfo(null);
      return;
    }
    try {
      const response = await api.get(`${API}/admin/database/info/${collectionName}`);
      setCollectionInfo(response.data);
    } catch (error) {
      console.error('Error loading collection info:', error);
      toast.error('Failed to load collection info');
    }
  };
  
  // Load Stock Management History
  const loadStockHistory = async (filter = 'all') => {
    setLoadingStockHistory(true);
    try {
      const response = await api.get(`${API}/stock-management/history`, {
        params: { filter, limit: 200 }
      });
      setStockHistory(response.data.history || []);
    } catch (error) {
      console.error('Error loading stock history:', error);
      toast.error('Failed to load stock management history');
    } finally {
      setLoadingStockHistory(false);
    }
  };
  
  // Load Manual Saves
  const loadManualSaves = async () => {
    setLoadingManualSaves(true);
    try {
      const response = await api.get(`${API}/stock-management/manual-saves`);
      setManualSaves(response.data.saves || []);
    } catch (error) {
      console.error('Error loading manual saves:', error);
      toast.error('Failed to load manual saves');
    } finally {
      setLoadingManualSaves(false);
    }
  };
  
  // Download Manual Save
  const downloadManualSave = async (saveId, saveName) => {
    try {
      const response = await api.get(`${API}/stock-management/manual-save/${saveId}`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock-save-${saveName.replace(/[^a-z0-9]/gi, '-')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Save downloaded successfully');
    } catch (error) {
      console.error('Error downloading save:', error);
      toast.error('Failed to download save');
    }
  };
  
  // Upload Manual Save
  const uploadManualSave = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`${API}/stock-management/manual-save/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        toast.success(`Uploaded: ${response.data.name}`);
        loadManualSaves();
      }
    } catch (error) {
      console.error('Error uploading save:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload save');
    }
  };
  
  // Restore Manual Save
  const restoreManualSave = async (saveId, saveName) => {
    if (!confirm(`Are you sure you want to restore "${saveName}"? This will replace current data.`)) {
      return;
    }
    try {
      const response = await api.post(`${API}/stock-management/manual-save/restore/${saveId}`);
      if (response.data.success) {
        toast.success(`Restored ${response.data.items_restored} items from save`);
      }
    } catch (error) {
      console.error('Error restoring save:', error);
      toast.error('Failed to restore save');
    }
  };
  
  // Delete Manual Save
  const deleteManualSave = async (saveId) => {
    if (!confirm('Are you sure you want to delete this save?')) {
      return;
    }
    try {
      await api.delete(`${API}/stock-management/manual-save/${saveId}`);
      toast.success('Save deleted');
      loadManualSaves();
    } catch (error) {
      console.error('Error deleting save:', error);
      toast.error('Failed to delete save');
    }
  };
  
  // Restore from History Entry
  const restoreFromHistoryEntry = async (entryId, description) => {
    if (!confirm(`Are you sure you want to restore to "${description}"? This will revert changes.`)) {
      return;
    }
    try {
      await api.post(`${API}/stock-management/history/restore-entry`, { entry_id: entryId });
      toast.success('Successfully restored');
      loadStockHistory(stockHistoryFilter);
    } catch (error) {
      console.error('Error restoring from history:', error);
      toast.error('Failed to restore');
    }
  };
  
  // Restore to a save point
  const restoreStockSavePoint = async (savePointId, type) => {
    if (!confirm('Are you sure you want to restore to this save point? This will revert all changes made after this point.')) {
      return;
    }
    try {
      await api.post(`${API}/stock-management/history/restore`, {
        save_point_id: savePointId,
        type: type
      });
      toast.success('Successfully restored to save point');
      loadStockHistory(stockHistoryFilter);
    } catch (error) {
      console.error('Error restoring save point:', error);
      toast.error('Failed to restore to save point');
    }
  };

  // Stock Database Functions (MUST BE BEFORE useEffect)
  const loadStockDatabase = async () => {
    try {
      const [masterRes, groupsRes, suppliersRes, filesRes, valuationsRes, manualSuppliersRes, additionalProductCountRes] = await Promise.all([
        api.get(`${API}/stock-database/count`),
        api.get(`${API}/stock-database/product-groups`),
        api.get(`${API}/stock-database/supplier-groups`), // Product Analysis Groups
        api.get(`${API}/stock-database/files`),
        api.get(`${API}/stock-database/valuations`).catch(() => ({ data: { valuations: [] } })),
        api.get(`${API}/stock-database/manual-supplier-groups`).catch(() => ({ data: { groups: [] } })), // NEW Supplier Groups
        api.get(`${API}/stock-database/additional-product-data/count`).catch(() => ({ data: { count: 0 } })) // Additional Product Data count
      ]);
      
      setMasterStockCount(masterRes.data.count || 0);
      setProductGroups(groupsRes.data.groups || []);
      setSupplierGroups(suppliersRes.data.suppliers || []); // Product Analysis Groups
      setStockFiles(filesRes.data.files || []);
      setStockValuations(valuationsRes.data.valuations || []);
      setUngroupedCount(groupsRes.data.ungrouped_count || 0);
      setManualSupplierGroups(manualSuppliersRes.data.groups || []); // NEW Supplier Groups
      setAdditionalProductDataCount(additionalProductCountRes.data.count || 0);
      
      // Also load additional stock files
      loadAdditionalStockFiles();
    } catch (error) {
      console.error('Error loading stock database:', error);
      toast.error('Failed to load stock database');
    }
  };

  // Load additional stock CSV files list
  const loadAdditionalStockFiles = async () => {
    try {
      const response = await api.get(`${API}/stock-database/additional-files`);
      setAdditionalStockFiles(response.data || []);
    } catch (error) {
      console.error('Error loading additional stock files:', error);
    }
  };

  // Alias for refreshing stock database data
  const loadStockDatabaseData = loadStockDatabase;

  // Load additional product data with pagination and search
  const loadAdditionalProductData = async (page = 1, search = '') => {
    try {
      const response = await api.get(`${API}/stock-database/additional-product-data`, {
        params: { page, limit: 50, search }
      });
      setAdditionalProductData(response.data.products || []);
      setAdditionalProductDataPage(response.data.page || 1);
      setAdditionalProductDataTotalPages(response.data.total_pages || 1);
      setAdditionalProductDataCount(response.data.total || 0);
    } catch (error) {
      console.error('Error loading additional product data:', error);
    }
  };

  // Upload additional product data Excel file
  // Download additional product data as JSON
  const handleDownloadAdditionalProductData = async () => {
    try {
      const response = await api.get(`${API}/stock-database/additional-product-data/download`);
      const dataStr = JSON.stringify(response.data.products, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `additional_product_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${response.data.count} products`);
    } catch (error) {
      console.error('Error downloading additional product data:', error);
      toast.error('Failed to download product data');
    }
  };

  // Update additional product data (weight/keywords)
  const handleUpdateAdditionalProduct = async () => {
    if (!selectedAdditionalProduct) return;
    
    try {
      const updateData = {
        additional_keywords: additionalProductEditKeywords
      };
      
      // Only include weight update if safety word is provided
      if (additionalProductSafetyWord.toUpperCase() === 'OVERRIDE' && additionalProductEditWeight !== '') {
        updateData.prod_net_wt = parseFloat(additionalProductEditWeight) || 0;
        updateData.safety_word = additionalProductSafetyWord;
      }

      if (additionalProductEditCostFactor !== '') {
        updateData.cost_factor = parseFloat(additionalProductEditCostFactor) || 0;
      }
      
      await api.put(`${API}/stock-database/additional-product-data/${selectedAdditionalProduct.prod_code}`, updateData);
      toast.success('Product updated');
      setShowAdditionalProductModal(false);
      loadAdditionalProductData(additionalProductDataPage, additionalProductDataSearch);
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  // Fetch AI usage logs - MUST BE BEFORE useEffect
  const fetchAiUsageLogs = async (period) => {
    setLoadingAiLogs(true);
    try {
      const p = period || aiLogPeriod;
      const params = new URLSearchParams({ limit: 500, period_type: p.type });
      if (p.year) params.append('period_year', p.year);
      if (p.month) params.append('period_month', p.month);
      const response = await api.get(`${API}/ai-usage/logs?${params.toString()}`);
      setAiUsageLogs(response.data.logs || []);
      setAiUsageStats({
        total_logs: response.data.total_logs || 0,
        total_estimated_credits: response.data.total_estimated_credits || 0,
        total_estimated_gbp: response.data.total_estimated_gbp || 0,
        usage_by_feature: response.data.usage_by_feature || [],
        usage_by_user: response.data.usage_by_user || [],
        usage_by_month: response.data.usage_by_month || [],
        usage_by_model: response.data.usage_by_model || [],
        available_ai_features: response.data.available_ai_features || {}
      });
    } catch (error) {
      console.error('Error fetching AI usage logs:', error);
    } finally {
      setLoadingAiLogs(false);
    }
  };
  
  // Fetch Non-AI usage logs - MUST BE BEFORE useEffect
  const fetchNonAiUsageLogs = async () => {
    setLoadingNonAiLogs(true);
    try {
      const response = await api.get(`${API}/non-ai-usage/logs?limit=500`);
      setNonAiUsageLogs(response.data.logs || []);
      setNonAiUsageStats({
        total_logs: response.data.total_logs || 0,
        usage_by_feature: response.data.usage_by_feature || [],
        usage_by_user: response.data.usage_by_user || [],
        usage_by_month: response.data.usage_by_month || [],
        available_non_ai_features: response.data.available_non_ai_features || {}
      });
    } catch (error) {
      console.error('Error fetching non-AI usage logs:', error);
    } finally {
      setLoadingNonAiLogs(false);
    }
  };

  useEffect(() => {
    loadSettings();
    if (user?.role === 'super_admin') {
      loadFinancialYears();
      loadStockDatabase();
    }
  }, [user]);

  // Auto-load additional product data when count is available
  useEffect(() => {
    if (additionalProductDataCount > 0 && additionalProductData.length === 0) {
      loadAdditionalProductData(1, '');
    }
  }, [additionalProductDataCount]);

  // Auto-load usage data when Usage tab is active
  useEffect(() => {
    if (activeTab === 'usage' && user?.role === 'super_admin') {
      if (Object.keys(aiUsageStats.available_ai_features).length === 0) {
        fetchAiUsageLogs(aiLogPeriod);
      }
      if (Object.keys(nonAiUsageStats.available_non_ai_features).length === 0) {
        fetchNonAiUsageLogs();
      }
    }
  }, [activeTab, user?.role]);

  // Re-fetch when period changes
  useEffect(() => {
    if (activeTab === 'usage' && user?.role === 'super_admin') {
      fetchAiUsageLogs(aiLogPeriod);
    }
  }, [aiLogPeriod]);

  const loadSettings = async () => {
    try {
      const response = await api.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Load branch-specific vehicle settings
  const loadBranchVehicleSettings = async (branchId) => {
    setLoadingBranchSettings(true);
    try {
      const response = await api.get(`${API}/settings/vehicles/${branchId}`);
      setBranchVehicleSettings(prev => ({
        ...prev,
        [branchId]: {
          base_postcode: response.data.base_postcode || (branchId === 'E70' ? 'B70 0TX' : 'B6 7AF'),
          base_location_override: response.data.base_location_override || null,
          default_vehicles: response.data.default_vehicles || []
        }
      }));
    } catch (error) {
      console.error(`Error loading ${branchId} vehicle settings:`, error);
      // Set defaults on error
      setBranchVehicleSettings(prev => ({
        ...prev,
        [branchId]: {
          base_postcode: branchId === 'E70' ? 'B70 0TX' : 'B6 7AF',
          base_location_override: null,
          default_vehicles: []
        }
      }));
    } finally {
      setLoadingBranchSettings(false);
    }
  };

  // Save branch-specific vehicle settings
  const saveBranchVehicleSettings = async (branchId) => {
    setSaving(true);
    try {
      const branchSettings = branchVehicleSettings[branchId];
      await api.put(`${API}/settings/vehicles/${branchId}`, {
        base_postcode: branchSettings.base_postcode,
        base_location_override: branchSettings.base_location_override || null,
        default_vehicles: branchSettings.default_vehicles,
        _effective_date: vehicleDetailsDate
      });
      toast.success(`${branchId} vehicle settings saved! Changes apply from ${vehicleDetailsDate}`);
      // Reload vehicles to show updated data
      await loadBranchVehicleSettings(branchId);
    } catch (error) {
      console.error(`Error saving ${branchId} vehicle settings:`, error);
      toast.error(`Failed to save ${branchId} vehicle settings`);
    } finally {
      setSaving(false);
    }
  };

  // Update branch vehicle field
  const updateBranchVehicleField = (branchId, field, value) => {
    setBranchVehicleSettings(prev => ({
      ...prev,
      [branchId]: {
        ...prev[branchId],
        [field]: value
      }
    }));
  };

  // Update individual vehicle in branch settings
  const updateBranchVehicle = (branchId, index, field, value) => {
    setBranchVehicleSettings(prev => {
      const newVehicles = [...(prev[branchId]?.default_vehicles || [])];
      newVehicles[index] = { ...newVehicles[index], [field]: value };
      return {
        ...prev,
        [branchId]: {
          ...prev[branchId],
          default_vehicles: newVehicles
        }
      };
    });
  };

  // Add default vehicle to branch
  const addBranchDefaultVehicle = (branchId) => {
    const newVehicle = {
      id: `vehicle_${Date.now()}`,
      vehicle_registration: '',
      vehicle_type: 'truck',
      driver_name: '',
      driver_phone: '',
      max_weight: 1000,
      color_code: 'blue',
      contract_hire: 0,
      maintenance_insurance: 0,
      moffet: 0,
      cost_per_mile: 0,
      driver_cost: 0,
      // Off by default — a freshly-added registry vehicle is meant to be picked from
      // the per-day "swap vehicle" dropdown on the board (Edit Vehicle Details) when
      // needed, not silently added as a brand-new permanent column on every day
      // alongside the existing default vehicles. Turn this on explicitly only if it's
      // meant to be a genuinely new permanent slot.
      is_default: false
    };
    setBranchVehicleSettings(prev => ({
      ...prev,
      [branchId]: {
        ...prev[branchId],
        default_vehicles: [...(prev[branchId]?.default_vehicles || []), newVehicle]
      }
    }));
  };

  // Delete vehicle from branch
  const deleteBranchVehicle = (branchId, index) => {
    setBranchVehicleSettings(prev => {
      const newVehicles = [...(prev[branchId]?.default_vehicles || [])];
      newVehicles.splice(index, 1);
      return {
        ...prev,
        [branchId]: {
          ...prev[branchId],
          default_vehicles: newVehicles
        }
      };
    });
  };

  // Load branch settings when tab changes
  useEffect(() => {
    if (vehicleBranch) {
      loadBranchVehicleSettings(vehicleBranch);
    }
  }, [vehicleBranch]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put(`${API}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Financial Years Management
  const loadFinancialYears = async () => {
    try {
      const response = await api.get(`${API}/financial-years`);
      const years = response.data.financial_years || [];
      setFinancialYears(years);
      
      // Select the current year by default
      const currentYear = years.find(y => y.is_current) || years[0];
      if (currentYear) {
        setSelectedYear(currentYear);
        setGrowthRates(currentYear.growth_rates || {
          "Structural": 0.0,
          "Merchants": 0.0,
          "Technical": 0.0,
          "Neville Long": 0.0
        });
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
      toast.error('Failed to load financial years');
    }
  };

  const createNextFinancialYear = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const nextYearStart = `${currentYear + 1}-08-01`;
      const nextYearEnd = `${currentYear + 2}-07-31`;
      
      const response = await api.post(
        `${API}/financial-years`,
        null,
        { params: { year_start: nextYearStart, year_end: nextYearEnd } }
      );
      
      toast.success(`Financial year created: ${response.data.financial_year.year_label}`);
      await loadFinancialYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create financial year');
    }
  };

  const updateGrowthRates = async () => {
    if (!selectedYear) {
      toast.error('Please select a financial year');
      return;
    }
    
    try {
      await api.put(
        `${API}/financial-years/${selectedYear.id}/growth-rates`,
        growthRates
      );
      toast.success('Growth rates updated successfully');
      await loadFinancialYears();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update growth rates');
    }
  };

  const uploadBudgetPDF = async (file) => {
    if (!selectedYear) {
      toast.error('Please select a financial year first');
      return;
    }
    
    if (!file || !file.name.endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    setUploadingBudget(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      toast.info('Uploading budget PDF and analyzing with AI...');
      setUploadProgress(30);
      
      const response = await api.post(
        `${API}/financial-years/${selectedYear.id}/budget-upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );
      
      setUploadProgress(100);
      toast.success(`Budget uploaded successfully! Extracted ${response.data.analysis_summary.kpis_count} KPIs and ${response.data.analysis_summary.business_units_count} business units.`);
      
      await loadFinancialYears();
      
    } catch (error) {
      console.error('Budget upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload budget');
    } finally {
      setUploadingBudget(false);
      setUploadProgress(0);
    }
  };


  const updateField = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const addDefaultVehicle = () => {
    const newVehicle = {
      id: `vehicle_${Date.now()}`,
      vehicle_registration: '',
      vehicle_type: 'truck',
      driver_name: '',
      driver_phone: '',
      max_weight: 1000.0,
      color_code: 'blue',
    };
    setSettings({
      ...settings,
      default_vehicles: [...(settings?.default_vehicles || []), newVehicle],
    });
  };

  const updateVehicle = (index, field, value) => {
    const updatedVehicles = [...settings.default_vehicles];
    updatedVehicles[index] = { ...updatedVehicles[index], [field]: value };
    setSettings({ ...settings, default_vehicles: updatedVehicles });
  };

  const deleteVehicle = (index) => {
    const updatedVehicles = settings.default_vehicles.filter((_, i) => i !== index);
    setSettings({ ...settings, default_vehicles: updatedVehicles });
  };

  const saveVehicleCostsWithDate = async () => {
    if (selectedVehicleIndex === null) return;
    
    const vehicle = settings.default_vehicles[selectedVehicleIndex];
    
    try {
      setSaving(true);
      
      // Save the settings first
      await api.put(`${API}/settings`, settings);
      
      // Create cost history record with effective date
      const costHistoryRecord = {
        vehicle_registration: vehicle.vehicle_registration,
        effective_date: effectiveDate,
        contract_hire: vehicle.contract_hire || 0,
        maintenance_insurance: vehicle.maintenance_insurance || 0,
        moffet: vehicle.moffet || 0,
        cost_per_mile: vehicle.cost_per_mile || 0,
        driver_cost: vehicle.driver_cost || 0,
      };
      
      await api.post(`${API}/cost-history`, costHistoryRecord);
      
      // Trigger recalculation for dates >= effective date
      toast.success(`Cost changes saved! Recalculating from ${effectiveDate}...`, { duration: 5000 });
      
      // Optional: You could trigger a backend job to recalculate all affected dates
      // For now, we'll rely on the cost history system to apply costs correctly
      
      setShowDateDialog(false);
      setSelectedVehicleIndex(null);
      toast.success('Vehicle costs updated successfully');
      
    } catch (error) {
      console.error('Error saving vehicle costs:', error);
      toast.error('Failed to save vehicle costs');
    } finally {
      setSaving(false);
    }
  };

  const openSaveDialog = (index) => {
    setSelectedVehicleIndex(index);
    setEffectiveDate(format(new Date(), 'yyyy-MM-dd'));
    setShowDateDialog(true);
  };

  const openVehicleDetailsDialog = (index) => {
    setSelectedVehicleIndex(index);
    setVehicleDetailsDate(format(new Date(), 'yyyy-MM-dd'));
    setShowVehicleDetailsDialog(true);
  };

  const saveVehicleDetails = async () => {
    if (selectedVehicleIndex === null) return;
    
    try {
      setSaving(true);
      // Send the effective date along with settings
      await api.put(`${API}/settings`, {
        ...settings,
        _effective_date: vehicleDetailsDate
      });
      toast.success(`Vehicle details saved with effective date: ${vehicleDetailsDate}`);
      setShowVehicleDetailsDialog(false);
      setSelectedVehicleIndex(null);
    } catch (error) {
      console.error('Error saving vehicle details:', error);
      toast.error('Failed to save vehicle details');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await api.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setShowChangePasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Check loading FIRST before access check
  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading settings...</div>
      </div>
    );
  }

  // Access check AFTER loading completes and user is available
  // Only check access if user is NOT super_admin
  if (user.role !== 'super_admin' && !user.can_access_settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>You don't have permission to access settings</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Stock Database Functions (continued)
  const addNewProduct = async () => {
    if (!newProduct.code || !newProduct.name) {
      toast.error('Please enter both code and name');
      return;
    }

    try {
      await api.post(`${API}/stock-database/products`, newProduct);
      toast.success('Product added successfully');
      setNewProduct({ code: '', name: '' });
      await loadStockDatabase();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error(error.response?.data?.detail || 'Failed to add product');
    }
  };

  const viewGroupDetails = (group) => {
    // Will implement detailed view in modal
    toast.info(`Viewing ${group.name}`);
  };

  const editSupplierGroup = (supplier) => {
    setNewSupplier({
      name: supplier.name,
      category: supplier.category,
      product_group_ids: supplier.product_group_ids || []
    });
    setEditingSupplier(supplier);
    setShowSupplierModal(true);
  };

  const deleteSupplierGroup = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier group?')) {
      return;
    }

    try {
      await api.delete(`${API}/stock-database/supplier-groups/${supplierId}`);
      toast.success('Supplier group deleted');
      await loadStockDatabase();
    } catch (error) {
      console.error('Error deleting supplier group:', error);
      toast.error('Failed to delete supplier group');
    }
  };

  const deleteStockFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await api.delete(`${API}/stock-database/files/${fileId}`);
      toast.success('File deleted');
      await loadStockDatabase();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const handleMasterUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${API}/stock-database/upload-master`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success(`${response.data.products_added} products added, ${response.data.groups_created} groups created`);
      setShowUploadMasterModal(false);
      await loadStockDatabase();
    } catch (error) {
      console.error('Error uploading master stock:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };

  const searchInventory = async () => {
    if (!inventorySearchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      const response = await api.get(`${API}/stock-database/search`, {
        params: { query: inventorySearchQuery }
      });
      setInventoryResults(response.data.products || []);
      
      if (response.data.products.length === 0) {
        toast.info('No products found');
      }
    } catch (error) {
      console.error('Error searching inventory:', error);
      toast.error('Search failed');
    }
  };

  const toggleProductGroup = (groupId) => {
    setNewSupplier(prev => ({
      ...prev,
      product_group_ids: prev.product_group_ids.includes(groupId)
        ? prev.product_group_ids.filter(id => id !== groupId)
        : [...prev.product_group_ids, groupId]
    }));
  };
  
  const handleExportGroups = async () => {
    try {
      toast.loading('Exporting stock database data...', { id: 'export-groups' });
      
      const response = await api.get(`${API}/stock-database/export-groups`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `stock_database_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully!', { id: 'export-groups' });
    } catch (error) {
      console.error('Error exporting groups:', error);
      toast.error(error.response?.data?.detail || 'Export failed', { id: 'export-groups' });
    }
  };

  const handleImportGroups = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      toast.loading('Importing stock database data...', { id: 'import-groups' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`${API}/stock-database/import-groups`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const results = response.data.imported || {};
      const details = [
        results.product_groups > 0 ? `${results.product_groups} product groups` : null,
        results.product_analysis_groups > 0 ? `${results.product_analysis_groups} analysis groups` : null,
        results.manual_supplier_groups > 0 ? `${results.manual_supplier_groups} supplier groups` : null,
        results.master_stock > 0 ? `${results.master_stock} master stock items` : null,
        results.pi_count_schedules > 0 ? `${results.pi_count_schedules} schedules` : null,
        results.settings > 0 ? `${results.settings} settings` : null
      ].filter(Boolean).join(', ');
      
      toast.success(`Import successful! Imported: ${details || 'No data'}`, { id: 'import-groups', duration: 5000 });
      await loadStockDatabase();
    } catch (error) {
      console.error('Error importing groups:', error);
      toast.error(error.response?.data?.detail || 'Import failed', { id: 'import-groups' });
    } finally {
      event.target.value = '';
    }
  };

  const autoGenerateAllSupplierGroups = async () => {
    try {
      toast.loading('Generating supplier groups...', { id: 'auto-gen' });
      
      const response = await api.post(`${API}/stock-database/auto-generate-suppliers`);
      
      if (response.data.created_count === 0 && response.data.total_groups === 0) {
        toast.warning('No supplier groups created. Please ensure stock valuation CSV is uploaded.', { id: 'auto-gen' });
      } else {
        toast.success(`Created ${response.data.created_count} supplier groups from ${response.data.total_groups} unique suppliers`, { id: 'auto-gen' });
      }
      await loadStockDatabase();
    } catch (error) {
      console.error('Error auto-generating suppliers:', error);
      toast.error(error.response?.data?.detail || 'Auto-generation failed', { id: 'auto-gen' });
    }
  };

  const toggleSupplierSelection = (supplierId) => {
    setSelectedSupplierIds(prev => 
      prev.includes(supplierId)
        ? prev.filter(id => id !== supplierId)
        : [...prev, supplierId]
    );
  };

  const updateMarketSectors = async () => {
    if (selectedSupplierIds.length === 0) {
      toast.error('Please select at least one supplier group');
      return;
    }

    try {
      await api.put(`${API}/stock-database/supplier-groups/update-sectors`, {
        supplier_ids: selectedSupplierIds,
        category: newMarketSector
      });
      
      toast.success(`Updated ${selectedSupplierIds.length} supplier groups to ${newMarketSector}`);
      setShowMarketSectorsModal(false);
      setSelectedSupplierIds([]);
      setNewMarketSector('structural');
      await loadStockDatabase();
    } catch (error) {
      console.error('Error updating market sectors:', error);
      toast.error(error.response?.data?.detail || 'Failed to update market sectors');
    }
  };

  const autoGenerateSupplierName = async () => {
    if (newSupplier.product_group_ids.length === 0) {
      toast.error('Please select at least one product analysis group first');
      return;
    }
    
    try {
      // Get the selected product analysis groups
      const selectedGroups = productGroups.filter(g => newSupplier.product_group_ids.includes(g.id));
      
      // Count categories to determine majority
      const categoryCount = { structural: 0, industrial: 0, admin: 0 };
      
      // Extract all group names for common prefix detection
      const groupNames = selectedGroups.map(g => g.name || '');
      
      // Find common prefix in group names (e.g., "SG Cavit" + "SG Loft Roll" -> "SG")
      const findCommonPrefix = (names) => {
        if (names.length === 0) return '';
        if (names.length === 1) return names[0].split(' ').slice(0, 2).join(' ');
        
        // Split all names into words
        const wordArrays = names.map(n => n.split(' '));
        const minLength = Math.min(...wordArrays.map(w => w.length));
        
        // Find matching words from the start
        let commonWords = [];
        for (let i = 0; i < minLength; i++) {
          const word = wordArrays[0][i].toUpperCase();
          if (wordArrays.every(arr => arr[i] && arr[i].toUpperCase() === word)) {
            commonWords.push(wordArrays[0][i]);
          } else {
            break;
          }
        }
        
        return commonWords.length > 0 ? commonWords.join(' ') : '';
      };
      
      // Get common prefix from group names
      let generatedName = findCommonPrefix(groupNames);
      
      // If no common prefix found, try to get from the first group's name
      if (!generatedName && selectedGroups[0]?.name) {
        const words = selectedGroups[0].name.split(' ');
        generatedName = words.slice(0, 2).join(' ');
      }
      
      // If still no name, use prefix-based fallback
      if (!generatedName) {
        const prefix = selectedGroups[0]?.prefix?.substring(0, 2) || '';
        generatedName = prefix ? `${prefix} Group` : 'New Supplier Group';
      }
      
      // Auto-detect market sector based on majority of selected groups
      // This requires fetching the category info from master stock
      const masterRes = await api.get(`${API}/stock-database/master-stock-by-groups`, {
        params: { group_ids: newSupplier.product_group_ids.join(',') }
      }).catch(() => ({ data: { products: [] } }));
      
      const masterProducts = masterRes.data?.products || [];
      
      // Count structural vs industrial based on product analysis descriptions
      for (const product of masterProducts) {
        const desc = (product.analysis_group || product.product_analysis_desc || '').toLowerCase();
        if (desc.includes('structural') || desc.includes('section') || desc.includes('board')) {
          categoryCount.structural++;
        } else if (desc.includes('industrial') || desc.includes('hvac') || desc.includes('pipe')) {
          categoryCount.industrial++;
        }
      }
      
      // Determine majority category
      let majorityCategory = 'structural'; // default
      if (categoryCount.industrial > categoryCount.structural) {
        majorityCategory = 'industrial';
      }
      
      // Update supplier with generated name and auto-detected category
      setNewSupplier(prev => ({ 
        ...prev, 
        name: generatedName,
        category: majorityCategory
      }));
      
      toast.success(`Auto-generated: "${generatedName}" (${majorityCategory})`);
      
    } catch (error) {
      console.error('Error auto-generating name:', error);
      // Fallback to simple generation
      const selectedGroups = productGroups.filter(g => newSupplier.product_group_ids.includes(g.id));
      const firstGroup = selectedGroups[0];
      const prefix = firstGroup?.prefix?.substring(0, 2) || '';
      setNewSupplier(prev => ({ ...prev, name: prefix ? `${prefix} Group` : 'New Supplier Group' }));
    }
  };

  const createSupplierGroup = async () => {
    if (!newSupplier.name || !newSupplier.name.trim()) {
      toast.error('Please enter supplier name');
      return;
    }
    if (!newSupplier.category) {
      toast.error('Please select a category');
      return;
    }
    if (newSupplier.product_group_ids.length === 0) {
      toast.error('Please select at least one product group');
      return;
    }

    try {
      if (editingSupplier) {
        // Update existing supplier
        const response = await api.put(`${API}/stock-database/supplier-groups/${editingSupplier.id}`, newSupplier);
        toast.success('Supplier group updated successfully');
      } else {
        // Create new supplier
        const response = await api.post(`${API}/stock-database/supplier-groups`, newSupplier);
        if (response.status === 200) {
          toast.success('Supplier group created successfully');
        }
      }
      setShowSupplierModal(false);
      setNewSupplier({ name: '', category: 'structural', product_group_ids: [] });
      setEditingSupplier(null);
      setShowAllGroups(false);
      await loadStockDatabase();
    } catch (error) {
      console.error('Error with supplier group:', error);
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const loadUngroupedProducts = async () => {
    try {
      const response = await api.get(`${API}/stock-database/ungrouped`);
      setUngroupedProducts(response.data.products || []);
      setShowUngroupedModal(true);
    } catch (error) {
      console.error('Error loading ungrouped products:', error);
      toast.error('Failed to load ungrouped products');
    }
  };

  const assignGroup = async (productId, groupId) => {
    try {
      await api.put(`${API}/stock-database/products/${productId}/group`, null, {
        params: { group_id: groupId }
      });
      toast.success('Group assigned');
      await loadUngroupedProducts();
      await loadStockDatabase();
    } catch (error) {
      console.error('Error assigning group:', error);
      toast.error('Failed to assign group');
    }
  };

  // NEW Manual Supplier Groups Functions
  const autoGenerateManualSupplierName = () => {
    if (newManualSupplierGroup.product_analysis_group_ids.length === 0) {
      toast.error('Please select at least one Product Analysis Group first');
      return;
    }
    
    // Get selected analysis groups
    const selectedAnalysisGroups = supplierGroups.filter(g => 
      newManualSupplierGroup.product_analysis_group_ids.includes(g.id)
    );
    
    // Find common prefix in names (e.g., "SG Cavit" + "SG Loft Roll" -> "SG")
    const names = selectedAnalysisGroups.map(g => g.name);
    
    const findCommonPrefix = (namesArray) => {
      if (namesArray.length === 0) return '';
      if (namesArray.length === 1) return namesArray[0];
      
      const wordArrays = namesArray.map(n => n.split(' '));
      const minLength = Math.min(...wordArrays.map(w => w.length));
      
      let commonWords = [];
      for (let i = 0; i < minLength; i++) {
        const word = wordArrays[0][i].toUpperCase();
        if (wordArrays.every(arr => arr[i] && arr[i].toUpperCase() === word)) {
          commonWords.push(wordArrays[0][i]);
        } else {
          break;
        }
      }
      
      return commonWords.length > 0 ? commonWords.join(' ') : '';
    };
    
    let generatedName = findCommonPrefix(names);
    
    // If no common prefix found, use first group name as fallback
    if (!generatedName) {
      generatedName = selectedAnalysisGroups[0]?.name || 'New Supplier Group';
    }
    
    // Auto-detect category based on majority
    let structuralCount = 0;
    let industrialCount = 0;
    
    selectedAnalysisGroups.forEach(g => {
      if (g.category === 'structural') structuralCount++;
      else if (g.category === 'industrial') industrialCount++;
    });
    
    const majorityCategory = structuralCount >= industrialCount ? 'structural' : 'industrial';
    
    setNewManualSupplierGroup(prev => ({
      ...prev,
      name: generatedName,
      category: majorityCategory
    }));
    
    toast.success(`Auto-generated: "${generatedName}" (${majorityCategory})`);
  };

  const saveManualSupplierGroup = async () => {
    if (!newManualSupplierGroup.name.trim()) {
      toast.error('Please enter a supplier group name');
      return;
    }
    
    if (newManualSupplierGroup.product_analysis_group_ids.length === 0) {
      toast.error('Please select at least one Product Analysis Group');
      return;
    }
    
    try {
      if (editingManualSupplierGroup) {
        await api.put(`${API}/stock-database/manual-supplier-groups/${editingManualSupplierGroup.id}`, newManualSupplierGroup);
        toast.success('Supplier Group updated successfully');
      } else {
        await api.post(`${API}/stock-database/manual-supplier-groups`, newManualSupplierGroup);
        toast.success('Supplier Group created successfully');
      }
      
      setShowNewSupplierGroupModal(false);
      setEditingManualSupplierGroup(null);
      setNewManualSupplierGroup({ name: '', category: 'structural', product_analysis_group_ids: [], crm_supplier_name: '' });
      await loadStockDatabase();
    } catch (error) {
      console.error('Error saving supplier group:', error);
      toast.error(error.response?.data?.detail || 'Failed to save supplier group');
    }
  };

  const deleteManualSupplierGroup = async () => {
    if (!editingManualSupplierGroup) return;
    
    if (!confirm('Are you sure you want to delete this Supplier Group?')) return;
    
    try {
      await api.delete(`${API}/stock-database/manual-supplier-groups/${editingManualSupplierGroup.id}`);
      toast.success('Supplier Group deleted');
      setShowNewSupplierGroupModal(false);
      setEditingManualSupplierGroup(null);
      setNewManualSupplierGroup({ name: '', category: 'structural', product_analysis_group_ids: [], crm_supplier_name: '' });
      await loadStockDatabase();
    } catch (error) {
      console.error('Error deleting supplier group:', error);
      toast.error('Failed to delete supplier group');
    }
  };

  // Determine which tabs the user can see
  const canViewTab = (tabName) => {
    if (user?.role === 'super_admin') return true;
    
    switch(tabName) {
      case 'general':
        return user?.can_access_general || user?.can_view_general_settings || true; // Everyone can see general
      case 'automations':
        return true; // Everyone can see Automations
      case 'vehicles':
        return user?.can_access_vehicles || user?.can_view_vehicles_settings || true; // Everyone can see vehicles
      case 'permissions':
        return user?.role === 'super_admin'; // ONLY super admin sees User Admin
      case 'crm-security':
        return user?.role === 'super_admin'; // ONLY super admin sees CRM Security
      case 'dashboard-manager':
        return user?.role === 'super_admin'; // ONLY super admin sees Dashboard Manager
      case 'files':
        return user?.can_view_file_manager || false; // Must have explicit permission
      case 'code':
        return user?.can_view_code_editor || false; // Must have explicit permission
      case 'backup':
        return user?.role === 'super_admin'; // ONLY super admin sees Backup
      case 'future-features':
        return user?.role === 'super_admin'; // ONLY super admin sees Future Features
      case 'user-emails':
        return user?.role === 'super_admin'; // ONLY super admin sees User Onboarding Emails
      case 'usage':
        return user?.role === 'super_admin'; // ONLY super admin sees Usage
      case 'cloudflare-incoming':
        return user?.role === 'super_admin'; // ONLY super admin sees Cloudflare Incoming
      case 'notification-bar':
        return user?.role === 'super_admin' || user?.can_manage_global_notifications || false;
      case 'pricing':
        return user?.role === 'super_admin' || user?.can_access_pricing || false;
      default:
        return false;
    }
  };

  // Clear AI usage logs
  const clearAiUsageLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all AI usage logs?')) return;
    try {
      await api.delete(`${API}/ai-usage/logs`);
      toast.success('AI usage logs cleared');
      fetchAiUsageLogs(aiLogPeriod);
    } catch (error) {
      toast.error('Failed to clear AI usage logs');
    }
  };

  return (
    <>
    <AppHeader
      user={user}
      scrollWithPage
      subHeaderLeft={
        <button
          onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/'); }}
          data-testid="back-button"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/90 hover:bg-white transition-colors"
          style={{ color: '#ee7203' }}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>
      }
      subHeaderCenter={
        <div className="flex items-center justify-center gap-2 flex-wrap" role="tablist" aria-label="Settings categories" data-testid="settings-categories">
          {SETTINGS_CATEGORIES.filter((category) => shownCategories.includes(category.id)).map((category) => (
            <button
              key={category.id}
              type="button"
              role="tab"
              aria-selected={settingsCategory === category.id}
              onClick={() => selectSettingsCategory(category.id)}
              data-testid={`settings-category-${category.id}`}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${settingsCategory === category.id ? 'bg-white text-[#ee7203]' : 'text-white/90 hover:bg-white/20'}`}
            >
              {category.label}
            </button>
          ))}
        </div>
      }
      subHeaderRight={
        <button
          onClick={saveSettings}
          disabled={saving}
          data-testid="save-settings-button"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-60"
          style={{ backgroundColor: '#ee7203' }}
        >
          <Save className="w-3.5 h-3.5" />
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
      }
    />
    <div className="v3-settings min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-2 md:p-4 lg:p-8" data-testid="settings-page">
      <div className="v3-settings-inner max-w-6xl mx-auto">
        {/* Page title (mobile-visible; desktop title now lives in the global sub-header) */}
        <div className="sm:hidden mb-4">
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#1d1d1b' }} data-testid="settings-title">
            System Settings
          </h1>
          <p className="text-xs text-gray-500 mt-1">Configure your transport system</p>
        </div>

        {/* Settings Tabs - High-Tech Slanted Design */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="v3-settings-layout space-y-6 mt-6" data-cat={settingsCategory}>
          
          {/* Mobile Dropdown Menu */}
          <div className="md:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full bg-white" style={{ borderColor: enconOrange }}>
                <SelectValue placeholder="Select setting" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {/* Account Section */}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">Account</div>
                {canViewTab('general') && <SelectItem value="general">General</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="dashboard-v3">Dashboard V3</SelectItem>}
                {canViewTab('general') && <SelectItem value="ui">User Interface</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="global-header">User Spaces</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="spaces-builder">Spaces Builder</SelectItem>}
                {canViewTab('automations') && <SelectItem value="automations">Automations</SelectItem>}
                {canViewTab('permissions') && <SelectItem value="permissions">User Admin</SelectItem>}
                {canViewTab('crm-security') && <SelectItem value="crm-security">CRM Security</SelectItem>}
                {canViewTab('user-emails') && <SelectItem value="user-emails">User Emails</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="email-mfa">Email & MFA</SelectItem>}
                {(user?.role === 'super_admin' || user?.can_view_mfa_overview) && <SelectItem value="mfa">MFA</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="notifications">Resend Notifications</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="teams">Teams</SelectItem>}
                {(user?.role === 'super_admin' || user?.can_manage_global_notifications) && <SelectItem value="notification-bar">Notification Bar</SelectItem>}
                
                {/* System Section */}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 mt-1">System</div>
                {user?.role === 'super_admin' && <SelectItem value="branches">Branches</SelectItem>}
                {canViewTab('vehicles') && <SelectItem value="vehicles">Vehicles</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="transport-settings">Transport Settings</SelectItem>}
                {canViewTab('dashboard-manager') && <SelectItem value="dashboard-manager">Dashboard</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="sales-back-office">Sales Office</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="adhoc">Adhoc</SelectItem>}
                
                {/* Files Section */}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 mt-1">Files</div>
                {canViewTab('files') && <SelectItem value="files">File Manager</SelectItem>}
                {canViewTab('code') && <SelectItem value="code">Code Editor</SelectItem>}
                {canViewTab('backup') && <SelectItem value="backup">Backup</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="files-admin">Files Admin</SelectItem>}
                
                {/* Data Section */}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 mt-1">Data</div>
                {user?.role === 'super_admin' && <SelectItem value="database">Database</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="stock-database">Stock Database</SelectItem>}
                {canViewTab('pricing') && <SelectItem value="pricing">Pricing</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="sos-settings">SoS Settings</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="projects-database">Projects DB</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="phocas-customer-database">Phocas Customer Database</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="phocas-supplier-profile">Phocas/CRM Supplier Data</SelectItem>}
                
                {/* Admin Section */}
                <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 mt-1">Admin</div>
                {user?.role === 'super_admin' && <SelectItem value="integrations">Integrations</SelectItem>}
                {user?.role === 'super_admin' && <SelectItem value="eight-x8-analytics">8x8 Analytics</SelectItem>}
                {canViewTab('future-features') && <SelectItem value="future-features">Future Features</SelectItem>}
                {canViewTab('usage') && <SelectItem value="usage">Usage</SelectItem>}
                {canViewTab('cloudflare-incoming') && <SelectItem value="cloudflare-incoming">Cloudflare Incoming</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          
          {/* Desktop Slanted Tab Navigation - Hidden on mobile */}
          <div ref={settingsNavRef} className="v3-settings-nav slanted-tabs-container hidden md:block" data-testid="settings-nav">
            <div className="v3-settings-flow slanted-tabs-flow">
              {/* Account & Users */}
              <div className="slanted-tabs-group" data-cat="account">
                <span className="slanted-tabs-group-title">Account</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {canViewTab('general') && (
                      <TabsTrigger value="general" data-testid="general-tab" className="slanted-tab">
                        General
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="dashboard-v3" data-testid="dashboard-v3-tab" className="slanted-tab">
                        Dashboard V3
                      </TabsTrigger>
                    )}
                    {canViewTab('permissions') && (
                      <TabsTrigger value="permissions" data-testid="permissions-tab" className="slanted-tab">
                        User Admin
                      </TabsTrigger>
                    )}
                    {canViewTab('crm-security') && (
                      <TabsTrigger value="crm-security" data-testid="crm-security-tab" className="slanted-tab">
                        CRM Security
                      </TabsTrigger>
                    )}
                    {canViewTab('user-emails') && (
                      <TabsTrigger value="user-emails" data-testid="user-emails-tab" className="slanted-tab">
                        User Emails
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="email-mfa" data-testid="email-mfa-tab" className="slanted-tab">
                        Email & MFA
                      </TabsTrigger>
                    )}
                    {(user?.role === 'super_admin' || user?.can_view_mfa_overview) && (
                      <TabsTrigger value="mfa" data-testid="mfa-tab" className="slanted-tab">
                        MFA
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="teams" data-testid="teams-tab" className="slanted-tab">
                        Teams
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* User Interface */}
              <div className="slanted-tabs-group" data-cat="user-interface">
                <span className="slanted-tabs-group-title">User Interface</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {canViewTab('general') && (
                      <TabsTrigger value="ui" data-testid="ui-tab" className="slanted-tab">
                        UI
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="global-header" data-testid="global-header-tab" className="slanted-tab">
                        User Spaces
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="spaces-builder" data-testid="spaces-builder-tab" className="slanted-tab">
                        Spaces Builder
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>

              <span className="slanted-tabs-separator">//</span>

              {/* System Configuration */}
              <div className="slanted-tabs-group" data-cat="system">
                <span className="slanted-tabs-group-title">System</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="branches" data-testid="branches-tab" className="slanted-tab">
                        Branches
                      </TabsTrigger>
                    )}
                    {canViewTab('automations') && (
                      <TabsTrigger value="automations" data-testid="automations-tab" className="slanted-tab">
                        Automations
                      </TabsTrigger>
                    )}
                    {canViewTab('dashboard-manager') && (
                      <TabsTrigger value="dashboard-manager" data-testid="dashboard-manager-tab" className="slanted-tab">
                        Dashboard
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="sales-back-office" data-testid="sales-back-office-tab" className="slanted-tab">
                        Sales Office
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="adhoc" data-testid="adhoc-tab" className="slanted-tab">
                        Adhoc
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* Transport */}
              <div className="slanted-tabs-group" data-cat="transport">
                <span className="slanted-tabs-group-title">Transport</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {canViewTab('vehicles') && (
                      <TabsTrigger value="vehicles" data-testid="vehicles-tab" className="slanted-tab">
                        Vehicles
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="transport-settings" data-testid="transport-settings-tab" className="slanted-tab">
                        Transport Settings
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* Notifications */}
              <div className="slanted-tabs-group" data-cat="notifications">
                <span className="slanted-tabs-group-title">Notifications</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="notifications" data-testid="notifications-tab" className="slanted-tab">
                        Resend Notifications
                      </TabsTrigger>
                    )}
                    {(user?.role === 'super_admin' || user?.can_manage_global_notifications) && (
                      <TabsTrigger value="notification-bar" data-testid="notification-bar-tab" className="slanted-tab">
                        Notification Bar
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* Files & Tools */}
              <div className="slanted-tabs-group" data-cat="files">
                <span className="slanted-tabs-group-title">Files</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {canViewTab('files') && (
                      <TabsTrigger value="files" data-testid="files-tab" className="slanted-tab">
                        File Manager
                      </TabsTrigger>
                    )}
                    {canViewTab('code') && (
                      <TabsTrigger value="code" data-testid="code-tab" className="slanted-tab">
                        Code Editor
                      </TabsTrigger>
                    )}
                    {canViewTab('backup') && (
                      <TabsTrigger value="backup" data-testid="backup-tab" className="slanted-tab">
                        Backup
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="files-admin" data-testid="files-admin-tab" className="slanted-tab">
                        Files Admin
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* Databases */}
              <div className="slanted-tabs-group" data-cat="data">
                <span className="slanted-tabs-group-title">Data</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="database" data-testid="database-tab" className="slanted-tab">
                        Database
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="stock-database" data-testid="stock-database-tab" className="slanted-tab">
                        Stock DB
                      </TabsTrigger>
                    )}
                    {canViewTab('pricing') && (
                      <TabsTrigger value="pricing" data-testid="pricing-tab" className="slanted-tab">
                        Pricing
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="sos-settings" data-testid="sos-settings-tab" className="slanted-tab">
                        SoS Settings
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="projects-database" data-testid="projects-database-tab" className="slanted-tab">
                        Projects DB
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="phocas-customer-database" data-testid="phocas-customer-database-tab" className="slanted-tab">
                        Phocas Customer DB
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="phocas-supplier-profile" data-testid="phocas-supplier-profile-tab" className="slanted-tab">
                        Phocas/CRM Supplier
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
              
              <span className="slanted-tabs-separator">//</span>
              
              {/* Admin & Security */}
              <div className="slanted-tabs-group" data-cat="admin">
                <span className="slanted-tabs-group-title">Admin</span>
                <div className="slanted-tabs-group-tabs">
                  <TabsList className="contents">
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="integrations" data-testid="integrations-tab" className="slanted-tab">
                        Integrations
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="eight-x8-analytics" data-testid="eight-x8-analytics-tab" className="slanted-tab">
                        8x8 Analytics
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="manage-changes" data-testid="manage-changes-tab" className="slanted-tab">
                        Manage Changes
                      </TabsTrigger>
                    )}
                    {(user?.role === 'super_admin' || user?.can_access_ai_assistant) && (
                      <TabsTrigger value="terra-assistant" data-testid="terra-assistant-tab" className="slanted-tab">
                        Terra
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="usage" data-testid="usage-tab" className="slanted-tab">
                        Usage
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="gdpr-protocol" data-testid="gdpr-protocol-tab" className="slanted-tab">
                        GDPR Protocol
                      </TabsTrigger>
                    )}
                    {user?.role === 'super_admin' && (
                      <TabsTrigger value="cloudflare-incoming" data-testid="cloudflare-incoming-tab" className="slanted-tab">
                        Cloudflare Incoming
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>
              </div>
            </div>
          </div>

          {/* Teams Configuration */}
          {user?.role === 'super_admin' && (
          <TabsContent value="teams" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Team Configuration</CardTitle>
                <CardDescription>
                  Create and manage teams. Assign a manager (with a title) and their team members.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TeamManagementSettings user={user} />
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {user?.role === 'super_admin' && (
          <TabsContent value="dashboard-v3" className="space-y-4">
            <DashboardV3Settings />
          </TabsContent>
          )}

          {/* User Interface > UI: every UI switch in one place (moved here from General), and the two home page switches */}
          {canViewTab('general') && (
          <TabsContent value="ui" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>UI</CardTitle>
                <CardDescription>
                  How the dashboard looks, and which version of the home page is shown
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Home page version — OG Dashboard / Super admin view (Super Admin Only) */}
                {user?.role === 'super_admin' && (
                  <div className="space-y-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-200" data-testid="og-dashboard-settings-section">
                    <div>
                      <Label className="text-base font-semibold text-purple-900">Home page version</Label>
                      <p className="text-xs text-purple-800 mt-1">
                        The home page as it was before the rebuild is saved as the OG Dashboard. While OG Dashboard is on, everyone keeps it, whatever is being built. Turn it off to show the new home page.
                      </p>
                    </div>
                    <div className="space-y-2 pb-3 mb-1 border-b border-purple-200">
                      <p className="text-sm font-semibold text-purple-900">OG Dashboard</p>
                      <div className="flex items-center justify-between gap-4 max-w-md">
                        <span className={`text-sm font-medium ${!ogDashboardOn ? 'text-gray-900' : 'text-gray-400'}`}>Off — new home page for everyone</span>
                        <Switch
                          checked={ogDashboardOn}
                          onCheckedChange={(checked) => handleOgToggle('og_dashboard', checked, checked ? 'OG Dashboard is on: everyone keeps the current home page' : 'OG Dashboard is off: the new home page is showing')}
                          disabled={savingOg}
                          data-testid="og-dashboard-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${ogDashboardOn ? 'text-gray-900' : 'text-gray-400'}`}>On — current home page</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-purple-900">Super admin view</p>
                      <p className="text-xs text-purple-800">
                        Turn this off to see the new home page yourself (super admins only) while OG Dashboard stays on for everyone else.
                        {!ogDashboardOn && ' OG Dashboard is off for everyone at the moment, so everyone is already seeing the new home page.'}
                      </p>
                      <div className="flex items-center justify-between gap-4 max-w-md">
                        <span className={`text-sm font-medium ${!ogSuperAdminOn ? 'text-gray-900' : 'text-gray-400'}`}>Off — new home page for me</span>
                        <Switch
                          checked={ogSuperAdminOn}
                          onCheckedChange={(checked) => handleOgToggle('og_dashboard_super_admin', checked, checked ? 'Super admins see the OG Dashboard again' : 'Super admins now see the new home page')}
                          disabled={savingOg}
                          data-testid="og-dashboard-super-admin-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${ogSuperAdminOn ? 'text-gray-900' : 'text-gray-400'}`}>On — OG Dashboard</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Home Page Background Theme (personal preference, all users) */}
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg border" data-testid="home-theme-settings-section">
                  <Label className="text-base font-semibold">Home Page Background</Label>
                  <p className="text-xs text-gray-500 mb-3">Choose how your Home page looks. This is saved to your account and only affects your own view.</p>
                  <div className="flex items-center justify-between gap-4 max-w-sm">
                    <span className={`text-sm font-medium ${homeTheme === 'light' ? 'text-gray-900' : 'text-gray-400'}`}>White</span>
                    <Switch
                      checked={homeTheme === 'dark'}
                      onCheckedChange={handleHomeThemeToggle}
                      disabled={savingHomeTheme}
                      data-testid="home-theme-toggle-switch"
                    />
                    <span className={`text-sm font-medium ${homeTheme === 'dark' ? 'text-gray-900' : 'text-gray-400'}`}>Dark Gradient</span>
                  </div>
                </div>

                {/* Global Sub-Header Style — My Personal Style (all users) */}
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border" data-testid="subheader-style-settings-section">
                  <Label className="text-base font-semibold">My Personal Style — Global Sub-Header</Label>
                  <p className="text-xs text-gray-500">
                    Choose how the main header bar and the thin bar underneath it look. This is saved to your account and only affects your own view.
                  </p>
                  <div className="flex items-center justify-between gap-4 max-w-sm pb-3 mb-1 border-b border-gray-200">
                    <span className={`text-sm font-medium ${!headerDarkGradient ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                    <Switch
                      checked={!!headerDarkGradient}
                      onCheckedChange={handleHeaderDarkGradientToggle}
                      disabled={savingHeaderDarkGradient}
                      data-testid="header-dark-gradient-toggle-switch"
                    />
                    <span className={`text-sm font-medium ${headerDarkGradient ? 'text-gray-900' : 'text-gray-400'}`}>Header: Dark Gradient</span>
                  </div>
                  <p className="text-xs text-gray-400 -mt-1">Sub-header (leave both off below to use the site's default):</p>
                  <div className="flex items-center justify-between gap-4 max-w-sm">
                    <span className={`text-sm font-medium ${!subheaderDarkGradient ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                    <Switch
                      checked={!!subheaderDarkGradient}
                      onCheckedChange={handleSubheaderDarkGradientToggle}
                      disabled={savingHeaderStyle}
                      data-testid="subheader-dark-gradient-toggle-switch"
                    />
                    <span className={`text-sm font-medium ${subheaderDarkGradient ? 'text-gray-900' : 'text-gray-400'}`}>Dark Gradient</span>
                  </div>
                  <div className="flex items-center justify-between gap-4 max-w-sm">
                    <span className={`text-sm font-medium ${!subheaderOrangeBar ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                    <Switch
                      checked={!!subheaderOrangeBar}
                      onCheckedChange={handleSubheaderOrangeBarToggle}
                      disabled={savingHeaderStyle}
                      data-testid="subheader-orange-bar-toggle-switch"
                    />
                    <span className={`text-sm font-medium ${subheaderOrangeBar ? 'text-gray-900' : 'text-gray-400'}`}>Orange Bar</span>
                  </div>
                  <div className="flex items-center justify-between gap-4 max-w-sm">
                    <span className={`text-sm font-medium ${!subheaderOrangeWhite ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                    <Switch
                      checked={!!subheaderOrangeWhite}
                      onCheckedChange={handleSubheaderOrangeWhiteToggle}
                      disabled={savingHeaderStyle}
                      data-testid="subheader-orange-white-toggle-switch"
                    />
                    <span className={`text-sm font-medium ${subheaderOrangeWhite ? 'text-gray-900' : 'text-gray-400'}`}>Orange to White</span>
                  </div>
                </div>
                {/* Global Sub-Header Style — Default For All Users (Super Admin Only) */}
                {user?.role === 'super_admin' && (
                  <div className="space-y-3 p-4 bg-orange-50 rounded-lg border-2 border-orange-200" data-testid="subheader-style-default-settings-section">
                    <Label className="text-base font-semibold text-orange-900">Default Style For All Users — Global Sub-Header</Label>
                    <p className="text-xs text-orange-800">
                      This is the starting style every user sees until they choose their own in "My Personal Style" above. It never overrides anyone who has already set a personal preference.
                    </p>
                    <div className="flex items-center justify-between gap-4 max-w-sm pb-3 mb-1 border-b border-orange-200">
                      <span className={`text-sm font-medium ${!defaultHeaderStyle.default_header_dark_gradient ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                      <Switch
                        checked={!!defaultHeaderStyle.default_header_dark_gradient}
                        onCheckedChange={handleDefaultHeaderDarkGradientToggle}
                        disabled={savingDefaultHeaderStyle || loadingDefaultHeaderStyle}
                        data-testid="default-header-dark-gradient-toggle-switch"
                      />
                      <span className={`text-sm font-medium ${defaultHeaderStyle.default_header_dark_gradient ? 'text-gray-900' : 'text-gray-400'}`}>Header: Dark Gradient</span>
                    </div>
                    <p className="text-xs text-orange-700 -mt-1">Sub-header:</p>
                    <div className="flex items-center justify-between gap-4 max-w-sm">
                      <span className={`text-sm font-medium ${!defaultHeaderStyle.default_subheader_dark_gradient ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                      <Switch
                        checked={!!defaultHeaderStyle.default_subheader_dark_gradient}
                        onCheckedChange={handleDefaultDarkGradientToggle}
                        disabled={savingDefaultHeaderStyle || loadingDefaultHeaderStyle}
                        data-testid="default-subheader-dark-gradient-toggle-switch"
                      />
                      <span className={`text-sm font-medium ${defaultHeaderStyle.default_subheader_dark_gradient ? 'text-gray-900' : 'text-gray-400'}`}>Dark Gradient</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 max-w-sm">
                      <span className={`text-sm font-medium ${!defaultHeaderStyle.default_subheader_orange_bar ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                      <Switch
                        checked={!!defaultHeaderStyle.default_subheader_orange_bar}
                        onCheckedChange={handleDefaultOrangeBarToggle}
                        disabled={savingDefaultHeaderStyle || loadingDefaultHeaderStyle}
                        data-testid="default-subheader-orange-bar-toggle-switch"
                      />
                      <span className={`text-sm font-medium ${defaultHeaderStyle.default_subheader_orange_bar ? 'text-gray-900' : 'text-gray-400'}`}>Orange Bar</span>
                    </div>
                    <div className="flex items-center justify-between gap-4 max-w-sm">
                      <span className={`text-sm font-medium ${!defaultHeaderStyle.default_subheader_orange_white ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                      <Switch
                        checked={!!defaultHeaderStyle.default_subheader_orange_white}
                        onCheckedChange={handleDefaultOrangeWhiteToggle}
                        disabled={savingDefaultHeaderStyle || loadingDefaultHeaderStyle}
                        data-testid="default-subheader-orange-white-toggle-switch"
                      />
                      <span className={`text-sm font-medium ${defaultHeaderStyle.default_subheader_orange_white ? 'text-gray-900' : 'text-gray-400'}`}>Orange to White</span>
                    </div>
                  </div>
                )}

                {/* UI Preview — Modal UI v2 / Nav-Header UI v2 (Super Admin Only, hidden from everyone else) */}
                {user?.role === 'super_admin' && (
                  <div className="space-y-4 p-4 bg-purple-50 rounded-lg border-2 border-purple-200" data-testid="ui-preview-settings-section">
                    <div>
                      <Label className="text-base font-semibold text-purple-900">UI Preview</Label>
                      <p className="text-xs text-purple-800 mt-1">
                        Feature flags for the in-progress full-site UI rebuilds. "On for me" previews it on your account only, while it's being built. "Live for everyone" releases it site-wide once ready.
                      </p>
                    </div>
                    <div className="space-y-2 pb-3 mb-1 border-b border-purple-200">
                      <p className="text-sm font-semibold text-purple-900">Modal UI v2</p>
                      <div className="flex items-center justify-between gap-4 max-w-sm">
                        <span className={`text-sm font-medium ${!modalUiV2Personal ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                        <Switch
                          checked={modalUiV2Personal}
                          onCheckedChange={handleModalUiV2PersonalToggle}
                          disabled={savingUiPreview}
                          data-testid="modal-ui-v2-personal-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${modalUiV2Personal ? 'text-gray-900' : 'text-gray-400'}`}>On for me</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 max-w-sm">
                        <span className={`text-sm font-medium ${!uiPreviewSiteWide.modal_ui_v2_site_wide ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                        <Switch
                          checked={!!uiPreviewSiteWide.modal_ui_v2_site_wide}
                          onCheckedChange={handleModalUiV2SiteWideToggle}
                          disabled={savingUiPreview || loadingUiPreview}
                          data-testid="modal-ui-v2-site-wide-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${uiPreviewSiteWide.modal_ui_v2_site_wide ? 'text-gray-900' : 'text-gray-400'}`}>Live for everyone</span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-purple-900">Nav / Header UI v2</p>
                      <div className="flex items-center justify-between gap-4 max-w-sm">
                        <span className={`text-sm font-medium ${!navUiV2Personal ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                        <Switch
                          checked={navUiV2Personal}
                          onCheckedChange={handleNavUiV2PersonalToggle}
                          disabled={savingUiPreview}
                          data-testid="nav-ui-v2-personal-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${navUiV2Personal ? 'text-gray-900' : 'text-gray-400'}`}>On for me</span>
                      </div>
                      <div className="flex items-center justify-between gap-4 max-w-sm">
                        <span className={`text-sm font-medium ${!uiPreviewSiteWide.nav_ui_v2_site_wide ? 'text-gray-900' : 'text-gray-400'}`}>Off</span>
                        <Switch
                          checked={!!uiPreviewSiteWide.nav_ui_v2_site_wide}
                          onCheckedChange={handleNavUiV2SiteWideToggle}
                          disabled={savingUiPreview || loadingUiPreview}
                          data-testid="nav-ui-v2-site-wide-toggle-switch"
                        />
                        <span className={`text-sm font-medium ${uiPreviewSiteWide.nav_ui_v2_site_wide ? 'text-gray-900' : 'text-gray-400'}`}>Live for everyone</span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* General Settings */}
          {canViewTab('general') && (
          <TabsContent value="general" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Personal account settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Change Name */}
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={user?.full_name || ''}
                    onChange={(e) => {
                      // Update user name via API
                      api.put(`${API}/users/${user.id}/permissions`, {
                        full_name: e.target.value
                      }).then(() => {
                        toast.success('Name updated');
                      }).catch(() => {
                        toast.error('Failed to update name');
                      });
                    }}
                    placeholder="Enter your full name"
                    className="text-lg"
                  />
                  <p className="text-xs text-gray-500">This is your display name used throughout the system</p>
                </div>

                {/* Change Password Section */}
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg border">
                  <Label className="text-base font-semibold">Password</Label>
                  <p className="text-xs text-gray-500 mb-3">Update your password to keep your account secure</p>
                  <Button
                    onClick={() => setShowChangePasswordModal(true)}
                    variant="outline"
                    className="gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Change Password
                  </Button>
                </div>

                {/* System Maintenance (Super Admin Only) */}
                {user?.role === 'super_admin' && (
                  <div className="space-y-4">
                    {/* P.O. Number Start Point Setting */}
                    <PONumberStartSetting />
                    
                    {/* Grant All Permissions */}
                    <div className="space-y-2 p-4 bg-orange-50 rounded-lg border-2 border-orange-300">
                      <Label className="text-base font-semibold text-orange-900">🔑 Fix Permission Issues</Label>
                      <p className="text-xs text-orange-800 mb-3">
                        <strong>If Budget or Sales Tracker are stuck loading:</strong> Click this to grant yourself all permissions. You'll need to log out and log back in after.
                      </p>
                      <Button
                        onClick={async () => {
                          try {
                            toast.info('Granting all permissions...');
                            const response = await api.post(`${API}/users/grant-all-permissions`);
                            toast.success(response.data.message, {
                              duration: 6000
                            });
                            // Show logout instruction
                            setTimeout(() => {
                              if (window.confirm('Permissions granted! You need to log out and log back in for changes to take effect.\n\nLog out now?')) {
                                window.location.href = '/login';
                              }
                            }, 2000);
                          } catch (error) {
                            toast.error('Failed to grant permissions: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="bg-orange-600 hover:bg-orange-700 gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Grant All Permissions to Me
                      </Button>
                    </div>

                    {/* Reinitialize Dashboard Cards */}
                    <div className="space-y-2 p-4 bg-red-50 rounded-lg border-2 border-red-300">
                      <Label className="text-base font-semibold text-red-900">🔧 Dashboard Maintenance</Label>
                      <p className="text-xs text-red-800 mb-3">
                        <strong>Reinitialize Dashboard Cards:</strong> Use this after deployment if home page cards are missing or incorrect. This will reset all dashboard cards to the latest configuration.
                      </p>
                      <Button
                        onClick={async () => {
                          if (window.confirm('⚠️ REINITIALIZE DASHBOARD CARDS?\n\nThis will:\n- Delete all current dashboard cards\n- Recreate them with latest configuration\n- Add Sales Tracker to home page\n\nThis is safe and only affects the dashboard layout.\n\nContinue?')) {
                            try {
                              toast.info('Reinitializing dashboard cards...');
                              const response = await api.post(`${API}/hub-apps/reinitialize`);
                              toast.success(response.data.message + `\nOld: ${response.data.old_count} cards\nNew: ${response.data.new_count} cards`, {
                                duration: 5000
                              });
                              // Reload page to show new cards
                              setTimeout(() => window.location.reload(), 2000);
                            } catch (error) {
                              toast.error('Failed to reinitialize: ' + (error.response?.data?.detail || error.message));
                            }
                          }
                        }}
                        variant="destructive"
                        className="gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reinitialize Dashboard Cards
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Branch Management - super_admin only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="branches" className="space-y-6">
            <BranchManager user={user} />
            
            {/* Branch Directory Section */}
            <div className="border-t pt-6">
              <BranchDirectory user={user} />
            </div>
          </TabsContent>
          )}

          {/* Default Vehicles */}
          {canViewTab('vehicles') && (
          <TabsContent value="vehicles" className="space-y-4">
            {/* Vehicles sub-tabs: default vehicle settings vs. Historical Data Centre */}
            <div className="flex gap-2 mb-2">
              <Button
                variant={vehiclesSubTab === 'settings' ? 'default' : 'outline'}
                onClick={() => setVehiclesSubTab('settings')}
                style={vehiclesSubTab === 'settings' ? { backgroundColor: '#ee7203', color: '#ffffff' } : { borderColor: '#ee7203', color: '#ee7203' }}
                data-testid="vehicles-subtab-settings"
              >
                Vehicle Settings
              </Button>
              <Button
                variant={vehiclesSubTab === 'history' ? 'default' : 'outline'}
                onClick={() => setVehiclesSubTab('history')}
                style={vehiclesSubTab === 'history' ? { backgroundColor: '#ee7203', color: '#ffffff' } : { borderColor: '#ee7203', color: '#ee7203' }}
                data-testid="vehicles-subtab-history"
              >
                Historical Data Centre
              </Button>
            </div>

            {vehiclesSubTab === 'history' ? (
              <HistoricalDataCentre />
            ) : (
            <>
            {/* Branch Tabs - Only show if user has access to both branches */}
            {hasAccessToBothBranches && (
              <div className="flex gap-2 mb-4">
                <Button
                  variant={vehicleBranch === 'C30' ? 'default' : 'outline'}
                  onClick={() => setVehicleBranch('C30')}
                  style={vehicleBranch === 'C30' ? { backgroundColor: '#ee7203' } : { borderColor: '#ee7203', color: '#ee7203' }}
                  data-testid="branch-tab-c30"
                >
                  C30 Vehicles
                </Button>
                <Button
                  variant={vehicleBranch === 'E70' ? 'default' : 'outline'}
                  onClick={() => setVehicleBranch('E70')}
                  style={vehicleBranch === 'E70' ? { backgroundColor: '#8b5cf6' } : { borderColor: '#8b5cf6', color: '#8b5cf6' }}
                  data-testid="branch-tab-e70"
                >
                  E70 Vehicles
                </Button>
              </div>
            )}
            
            {loadingBranchSettings ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#ee7203' }}></div>
              </div>
            ) : (
              <>
                {/* Base Postcode Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Base Location
                      <span className={`text-xs px-2 py-1 rounded ${vehicleBranch === 'E70' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                        {vehicleBranch}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      Set the default starting location for {vehicleBranch} route calculations
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <Label htmlFor="base_postcode">Base Postcode</Label>
                      <Input
                        id="base_postcode"
                        value={branchVehicleSettings[vehicleBranch]?.base_postcode || ''}
                        onChange={(e) => updateBranchVehicleField(vehicleBranch, 'base_postcode', e.target.value)}
                        placeholder={vehicleBranch === 'E70' ? 'B70 0TX' : 'B6 7AF'}
                        className="max-w-xs"
                      />
                      <p className="text-xs text-gray-500">
                        This postcode is used as the starting point for all {vehicleBranch} mileage calculations
                        {vehicleBranch === 'E70' && <span className="font-medium"> (Default: B70 0TX)</span>}
                      </p>
                    </div>
                    <div className="space-y-2 mt-4 pt-4 border-t">
                      <Label>Fine-Tune Exact Location (live tracking map)</Label>
                      <p className="text-xs text-gray-500 -mt-1">
                        A postcode's geocoded centre can sit noticeably off from where vehicles actually park —
                        drag the pin onto the real depot entrance/yard to line up the New Transport UI's live map.
                      </p>
                      <BaseLocationMapPicker
                        basePostcode={branchVehicleSettings[vehicleBranch]?.base_postcode}
                        value={branchVehicleSettings[vehicleBranch]?.base_location_override || null}
                        onChange={(coord) => updateBranchVehicleField(vehicleBranch, 'base_location_override', coord)}
                      />
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      Default Vehicles
                      <span className={`text-xs px-2 py-1 rounded ${vehicleBranch === 'E70' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'}`}>
                        {vehicleBranch}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      These vehicles will appear on all new {vehicleBranch} transport days
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {branchVehicleSettings[vehicleBranch]?.default_vehicles?.map((vehicle, index) => (
                      <Card key={vehicle.id || index} className="p-4 bg-gray-50" data-testid={`default-vehicle-${vehicleBranch}-${index}`}>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Vehicle Registration</Label>
                            <Input
                              value={vehicle.vehicle_registration || ''}
                              onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'vehicle_registration', e.target.value)}
                              data-testid={`vehicle-reg-${vehicleBranch}-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Driver Name</Label>
                            <Input
                              value={vehicle.driver_name || ''}
                              onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'driver_name', e.target.value)}
                              data-testid={`driver-name-${vehicleBranch}-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Driver Phone</Label>
                            <Input
                              value={vehicle.driver_phone || ''}
                              onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'driver_phone', e.target.value)}
                              data-testid={`driver-phone-${vehicleBranch}-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Vehicle Type</Label>
                            <Select
                              value={vehicle.vehicle_type || 'truck'}
                              onValueChange={(value) => updateBranchVehicle(vehicleBranch, index, 'vehicle_type', value)}
                            >
                              <SelectTrigger data-testid={`vehicle-type-${vehicleBranch}-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="truck">Truck</SelectItem>
                                <SelectItem value="van">Van</SelectItem>
                                <SelectItem value="flatbed">Flatbed</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Max Weight (kg)</Label>
                            <Input
                              type="number"
                              value={vehicle.max_weight || 1000}
                              onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'max_weight', parseInt(e.target.value) || 0)}
                              data-testid={`max-weight-${vehicleBranch}-${index}`}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Color</Label>
                            <Select
                              value={vehicle.color_code || 'blue'}
                              onValueChange={(value) => updateBranchVehicle(vehicleBranch, index, 'color_code', value)}
                            >
                              <SelectTrigger data-testid={`color-${vehicleBranch}-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="blue">Blue</SelectItem>
                                <SelectItem value="green">Green</SelectItem>
                                <SelectItem value="red">Red</SelectItem>
                                <SelectItem value="orange">Orange</SelectItem>
                                <SelectItem value="purple">Purple</SelectItem>
                                <SelectItem value="yellow">Yellow</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Has Moffet</Label>
                            <Select
                              value={vehicle.has_moffet ? 'yes' : 'no'}
                              onValueChange={(value) => updateBranchVehicle(vehicleBranch, index, 'has_moffet', value === 'yes')}
                            >
                              <SelectTrigger data-testid={`has-moffet-${vehicleBranch}-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">Default for this vehicle — can be overridden per-day on the board.</p>
                          </div>
                        </div>

                        {/* Cost Fields - Collapsible */}
                        <div className="mt-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowCosts(!showCosts)}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            {showCosts ? '▼' : '▶'} Cost Details
                          </Button>
                          
                          {showCosts && (
                            <div className="mt-3 pt-3 border-t">
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Contract Hire (£/week)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={vehicle.contract_hire || 0}
                                    onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'contract_hire', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Maint/Ins (£/week)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={vehicle.maintenance_insurance || 0}
                                    onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'maintenance_insurance', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Moffet (£/week)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={vehicle.moffet || 0}
                                    onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'moffet', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Cost/Mile (£)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={vehicle.cost_per_mile || 0}
                                    onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'cost_per_mile', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Driver Cost (£/year)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={vehicle.driver_cost || 0}
                                    onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'driver_cost', parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Live Tracking Link — shows a clickable tracking icon on this vehicle's
                            header on the Transport board; clicking it opens the URL below inside a
                            modal (auto-opens a new tab instead if the page refuses to be embedded). */}
                        <div className="mt-4 pt-3 border-t space-y-1.5">
                          <Label className="flex items-center gap-1.5 text-sm">
                            <MapPin className="w-3.5 h-3.5 text-orange-500" />
                            Live Tracking Link
                          </Label>
                          <Input
                            type="url"
                            placeholder="https://cloud.eu.samsara.com/o/.../fleet/viewer/..."
                            value={vehicle.tracking_url || ''}
                            onChange={(e) => updateBranchVehicle(vehicleBranch, index, 'tracking_url', e.target.value)}
                            data-testid={`tracking-url-${vehicleBranch}-${index}`}
                          />
                          <p className="text-xs text-gray-500">
                            Paste a live-tracking page link (e.g. Samsara fleet viewer). A tracking icon will
                            appear on this vehicle's header on the Transport board — clicking it opens this page in a modal.
                          </p>
                        </div>

                        {/* Samsara Vehicle ID mapping — powers the New Transport UI's live map
                            overlay (today) + completed-route overlay (past days) + automatic
                            drop-completion when a driver arrives at and leaves a stop. */}
                        <div className="mt-4 pt-3 border-t space-y-1.5">
                          <Label className="flex items-center gap-1.5 text-sm">
                            <Satellite className="w-3.5 h-3.5 text-sky-500" />
                            Samsara Vehicle ID
                          </Label>
                          <Select
                            value={vehicle.samsara_vehicle_id || ''}
                            onValueChange={(v) => updateBranchVehicle(vehicleBranch, index, 'samsara_vehicle_id', v)}
                            onOpenChange={(open) => open && fetchSamsaraVehicles()}
                          >
                            <SelectTrigger className="h-9 text-sm" data-testid={`samsara-vehicle-select-${vehicleBranch}-${index}`}>
                              <SelectValue placeholder={samsaraVehiclesLoaded && samsaraVehicles.length === 0 ? 'No Samsara vehicles found — check Integrations' : 'Select the matching Samsara vehicle…'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white max-h-72">
                              {samsaraVehicles.map((sv) => (
                                <SelectItem key={sv.id} value={sv.id}>
                                  {sv.license_plate || sv.name} {sv.license_plate && sv.name && sv.license_plate !== sv.name ? `(${sv.name})` : ''}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-500">
                            Links this vehicle to its real Samsara tracker for the live map overlay and
                            automatic drop-completion on the New Transport UI. Set the Samsara API key first in
                            Settings → Integrations → Samsara.
                          </p>
                        </div>

                        {/* Default Vehicle toggle — controls whether this registry vehicle auto-seeds
                            onto every NEW day's board. Turning it off keeps the vehicle in the registry
                            (still selectable via the per-day "swap vehicle" dropdown on the Transport
                            board) without it cluttering every day automatically — e.g. a spare/agency
                            vehicle that only covers occasionally. */}
                        <div className="mt-4 pt-3 border-t flex items-center justify-between gap-3">
                          <div>
                            <Label className="flex items-center gap-1.5 text-sm">
                              <Truck className="w-3.5 h-3.5 text-emerald-600" />
                              Default Vehicle
                            </Label>
                            <p className="text-xs text-gray-500 mt-0.5">
                              On: appears automatically on every new day. Off: stays in the registry, pick it
                              from the vehicle-swap dropdown on the board only when needed.
                            </p>
                          </div>
                          <Switch
                            checked={vehicle.is_default !== false}
                            onCheckedChange={(checked) => updateBranchVehicle(vehicleBranch, index, 'is_default', checked)}
                            data-testid={`is-default-vehicle-toggle-${vehicleBranch}-${index}`}
                          />
                        </div>

                        <div className="mt-4 flex justify-end">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteBranchVehicle(vehicleBranch, index)}
                            data-testid={`delete-vehicle-${vehicleBranch}-${index}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </Card>
                    ))}

                    {/* Effective Date Section */}
                    <div className={`p-4 rounded-lg border-2 ${vehicleBranch === 'E70' ? 'bg-purple-50 border-purple-200' : 'bg-orange-50 border-orange-200'}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <CalendarDays className={`w-5 h-5 ${vehicleBranch === 'E70' ? 'text-purple-600' : 'text-orange-600'}`} />
                        <Label htmlFor={`effective-date-${vehicleBranch}`} className="font-semibold">
                          Effective Date for Changes
                        </Label>
                      </div>
                      <Input
                        id={`effective-date-${vehicleBranch}`}
                        type="date"
                        value={vehicleDetailsDate}
                        onChange={(e) => setVehicleDetailsDate(e.target.value)}
                        className="max-w-xs mb-2"
                        data-testid={`effective-date-${vehicleBranch}`}
                      />
                      <p className="text-sm text-gray-600">
                        <strong>Important:</strong> Changes to driver names, phone numbers, and vehicle details will apply from <span className={`font-semibold ${vehicleBranch === 'E70' ? 'text-purple-700' : 'text-orange-700'}`}>{vehicleDetailsDate}</span> onwards.
                        Previous dates will keep their existing data.
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => addBranchDefaultVehicle(vehicleBranch)}
                        data-testid={`add-default-vehicle-${vehicleBranch}`}
                        className="flex-1 gap-2"
                        style={{ borderColor: vehicleBranch === 'E70' ? '#8b5cf6' : '#ee7203', color: vehicleBranch === 'E70' ? '#8b5cf6' : '#ee7203' }}
                      >
                        <Plus className="w-4 h-4" />
                        Add {vehicleBranch} Vehicle
                      </Button>
                      <Button
                        onClick={() => saveBranchVehicleSettings(vehicleBranch)}
                        disabled={saving}
                        data-testid={`save-vehicles-${vehicleBranch}`}
                        style={{ backgroundColor: vehicleBranch === 'E70' ? '#8b5cf6' : '#ee7203' }}
                        className="text-white gap-2"
                      >
                        <Save className="w-4 h-4" />
                        {saving ? 'Saving...' : `Save ${vehicleBranch} Settings`}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
            </>
            )}
          </TabsContent>
          )}

          {/* Transport Settings Tab */}
          {user?.role === 'super_admin' && (
          <TabsContent value="transport-settings" className="space-y-4">
            <TransportSettingsTab />
          </TabsContent>
          )}

          {/* Adhoc: odds and ends waiting to be sorted into a proper place. The display option has moved here from User Admin. */}
          {user?.role === 'super_admin' && (
          <TabsContent value="adhoc" className="space-y-4">
            <PermissionsManager view="adhoc" onImpersonate={onImpersonate} />
          </TabsContent>
          )}

          {/* Permissions Tab */}
          {canViewTab('permissions') && (
          <TabsContent value="permissions" className="space-y-4">
            <PermissionsManager onImpersonate={onImpersonate} />
          </TabsContent>
          )}

          {/* CRM Security Tab */}
          {canViewTab('crm-security') && (
          <TabsContent value="crm-security" className="space-y-4">
            <CRMSecurity />
          </TabsContent>
          )}

          {/* User Onboarding Emails Tab */}
          {canViewTab('user-emails') && (
          <TabsContent value="user-emails" className="space-y-4">
            <UserOnboardingEmails />
          </TabsContent>
          )}

          {/* File Manager Tab */}
          {canViewTab('files') && (
          <TabsContent value="files" className="space-y-4">
            <FileManager />
          </TabsContent>
          )}

          {/* Code Editor Tab */}
          {canViewTab('code') && (
          <TabsContent value="code" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Code Editor</CardTitle>
                <CardDescription>
                  View and browse application code, see recent changes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 md:p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
                  <h3 className="text-base md:text-lg font-semibold mb-2">Access Code Editor</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4">
                    View all application code files, browse recent changes, and track modifications across the system.
                  </p>
                  <Button
                    onClick={() => navigate('/editor')}
                    className="gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white w-full md:w-auto"
                  >
                    Open Editor
                  </Button>
                </div>

                {/* Email Integration Guide - Super Admin Only */}
                {user?.role === 'super_admin' && (
                  <div className="p-4 md:p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
                    <h3 className="text-base md:text-lg font-semibold mb-2">📧 Email Integration System</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4">
                      Complete implementation guide for Resend email integration: password reset, user approval notifications, and future automated email attachment processing. 100% free, no credit card required.
                    </p>
                    <Button
                      onClick={() => window.open('/email-integration-guide.html', '_blank')}
                      className="gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white w-full md:w-auto"
                    >
                      📖 View Email Integration Guide
                    </Button>
                  </div>
                )}

                {/* Photo Optimization Documentation - Super Admin Only */}
                {user?.role === 'super_admin' && (
                  <div className="p-4 md:p-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
                    <h3 className="text-base md:text-lg font-semibold mb-2">Photo Optimization System</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4">
                      View complete documentation and code for the advanced photo storage optimization system with GridFS and compression.
                    </p>
                    <Button
                      onClick={() => navigate('/photo-optimization')}
                      className="gap-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white w-full md:w-auto"
                    >
                      View Photo Optimization Details
                    </Button>
                  </div>
                )}

                {/* Transport Auto-Save Workflow Documentation - Super Admin Only */}
                {user?.role === 'super_admin' && (
                  <div className="p-4 md:p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-200">
                    <h3 className="text-base md:text-lg font-semibold mb-2">🚛 Transport Auto-Save Workflow</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4">
                      Complete technical breakdown of how my Transport Manager saves data, calculates mileage, and fetches contribution from CSV uploads. Critical reference for understanding data persistence.
                    </p>
                    <Button
                      onClick={() => navigate('/transport-auto-save-docs')}
                      className="gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white w-full md:w-auto"
                    >
                      📖 View Auto-Save Documentation
                    </Button>
                  </div>
                )}

                {/* Cross Deployment Backup - Super Admin Only */}
                {user?.role === 'super_admin' && (
                  <div className="p-4 md:p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-200">
                    <h3 className="text-base md:text-lg font-semibold mb-2">🔄 Cross Deployment Backup</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4">
                      Complete guide on how to safely deploy new code while preserving production data, copy data between environments, and understand database persistence. Learn MongoDB backup methods and data sync strategies.
                    </p>
                    <Button
                      onClick={() => navigate('/cross-deployment-backup')}
                      className="gap-2 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white w-full md:w-auto"
                    >
                      📚 View Cross Deployment Guide
                    </Button>
                  </div>
                )}

                {/* ProductLookup Component - Reusable Component Documentation - Super Admin Only */}
                {user?.role === 'super_admin' && (
                  <div className="p-4 md:p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-200">
                    <h3 className="text-base md:text-lg font-semibold mb-2">🔍 ProductLookup Component</h3>
                    <p className="text-sm md:text-base text-gray-600 mb-3">
                      A reusable product search component with TWO SEPARATE inputs (Code and Description) used across ProformaPreview, StockTransfer, and QuotePreview pages. Features include debounced search, CTRL toggle for all stock vs in-stock only, O.O.S badges, and on-order quantity display.
                    </p>
                    <div className="bg-white/80 rounded-lg p-3 mt-3 border border-blue-100">
                      <p className="text-xs font-mono text-gray-700 mb-2"><strong>File:</strong> /app/frontend/src/components/ProductLookup.jsx</p>
                      <p className="text-xs text-gray-600 mb-2"><strong>Usage:</strong></p>
                      <pre className="text-xs bg-gray-800 text-green-400 p-2 rounded overflow-x-auto">
{`<ProductLookup
  codeValue={item.product_code}
  descriptionValue={item.description}
  onSelect={(product) => handleSelectProduct(index, product)}
  onCodeChange={(val) => handleChange(index, 'product_code', val)}
  onDescriptionChange={(val) => handleChange(index, 'description', val)}
  showPrice={true}
  showStock={true}
  codeClassName="w-32"
  descriptionClassName="flex-1"
/>`}
                      </pre>
                      <p className="text-xs text-gray-600 mt-2"><strong>Features:</strong> Two separate inputs, CTRL toggles "show all" vs "in-stock only", red O.O.S badge, yellow on-order indicator</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Backup Tab */}
          {canViewTab('backup') && (
          <TabsContent value="backup">
            {/* Sub-tabs for Backup */}
            <div className="flex gap-2 mb-6">
              <Button
                variant={backupSubTab === 'system' ? 'default' : 'outline'}
                onClick={() => setBackupSubTab('system')}
                className="rounded-full"
                style={backupSubTab === 'system' ? { backgroundColor: enconOrange } : { borderColor: enconOrange, color: enconOrange }}
              >
                <Database className="w-4 h-4 mr-2" />
                System Backup
              </Button>
              <Button
                variant={backupSubTab === 'stock-history' ? 'default' : 'outline'}
                onClick={() => { setBackupSubTab('stock-history'); loadStockHistory(stockHistoryFilter); }}
                className="rounded-full"
                style={backupSubTab === 'stock-history' ? { backgroundColor: enconOrange } : { borderColor: enconOrange, color: enconOrange }}
              >
                <Activity className="w-4 h-4 mr-2" />
                Stock Management History
              </Button>
            </div>
            
            {/* System Backup Content */}
            {backupSubTab === 'system' && (
              <BackupManager user={user} />
            )}
            
            {/* Stock Management History Content */}
            {backupSubTab === 'stock-history' && (
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Activity className="w-5 h-5" style={{ color: enconOrange }} />
                      Stock Management History
                    </span>
                  </CardTitle>
                  <p className="text-sm text-gray-500">
                    Full audit log (last 7 days) and manual saves for Weekly Stock Reports.
                  </p>
                  
                  {/* Sub-tabs for History vs Manual Saves */}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant={stockHistoryTab === 'history' ? 'default' : 'outline'}
                      onClick={() => { setStockHistoryTab('history'); loadStockHistory(stockHistoryFilter); }}
                      className="rounded-full"
                      style={stockHistoryTab === 'history' ? { backgroundColor: enconOrange } : { borderColor: enconOrange, color: enconOrange }}
                    >
                      History Log
                    </Button>
                    <Button
                      variant={stockHistoryTab === 'manual-saves' ? 'default' : 'outline'}
                      onClick={() => { setStockHistoryTab('manual-saves'); loadManualSaves(); }}
                      className="rounded-full"
                      style={stockHistoryTab === 'manual-saves' ? { backgroundColor: '#3B82F6' } : { borderColor: '#3B82F6', color: '#3B82F6' }}
                    >
                      Manual Saves
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* History Log Tab */}
                  {stockHistoryTab === 'history' && (
                    <>
                      <div className="flex gap-2 mb-4">
                        <select
                          value={stockHistoryFilter}
                          onChange={(e) => { setStockHistoryFilter(e.target.value); loadStockHistory(e.target.value); }}
                          className="px-3 py-2 border-2 rounded-full text-sm"
                          style={{ borderColor: enconOrange }}
                        >
                          <option value="all">All History</option>
                          <option value="weekly">Weekly Stock Report</option>
                          <option value="daily">Daily Stock Ordering</option>
                        </select>
                        <Button
                          variant="outline"
                          onClick={() => loadStockHistory(stockHistoryFilter)}
                          className="rounded-full"
                          style={{ borderColor: enconOrange, color: enconOrange }}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      </div>
                      
                      {loadingStockHistory ? (
                        <div className="text-center py-8 text-gray-500">Loading history...</div>
                      ) : stockHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No history found (showing last 7 days)</div>
                      ) : (
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {stockHistory.map((entry, idx) => (
                            <div 
                              key={entry.id || idx} 
                              className={`p-4 border-2 rounded-lg ${
                                entry.action === 'save_point' ? 'bg-blue-50 border-blue-200' :
                                entry.action === 'create' ? 'bg-green-50 border-green-200' :
                                entry.action === 'update' ? 'bg-yellow-50 border-yellow-200' :
                                entry.action === 'delete' ? 'bg-red-50 border-red-200' :
                                entry.action === 'restore' ? 'bg-purple-50 border-purple-200' :
                                'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      entry.type === 'weekly' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                                    }`}>
                                      {entry.type === 'weekly' ? 'Weekly Stock' : 'Daily Ordering'}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      entry.action === 'save_point' ? 'bg-blue-200 text-blue-700' :
                                      entry.action === 'create' ? 'bg-green-200 text-green-700' :
                                      entry.action === 'update' ? 'bg-yellow-200 text-yellow-700' :
                                      entry.action === 'delete' ? 'bg-red-200 text-red-700' :
                                      entry.action === 'restore' ? 'bg-purple-200 text-purple-700' :
                                      'bg-gray-200 text-gray-700'
                                    }`}>
                                      {entry.action === 'save_point' ? 'Save Point' :
                                       entry.action === 'create' ? 'Created' :
                                       entry.action === 'update' ? 'Updated' :
                                       entry.action === 'delete' ? 'Deleted' :
                                       entry.action === 'restore' ? 'Restored' : entry.action}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(entry.timestamp).toLocaleString('en-GB')}
                                    </span>
                                  </div>
                                  <p className="font-medium text-sm">{entry.description}</p>
                                  {entry.details && (
                                    <p className="text-xs text-gray-600 mt-1">{entry.details}</p>
                                  )}
                                  {entry.notes_added && (
                                    <p className="text-xs text-blue-600 mt-1">Notes: "{entry.notes_added}"</p>
                                  )}
                                  {entry.status_changed && (
                                    <p className="text-xs mt-1">
                                      Status: <span className={`font-medium ${
                                        entry.status_changed === 'critical' ? 'text-red-600' :
                                        entry.status_changed === 'concern' ? 'text-yellow-600' :
                                        entry.status_changed === 'ok' ? 'text-green-600' : ''
                                      }`}>{entry.status_changed === 'concern' ? 'Not of Concern' : entry.status_changed}</span>
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">By: {entry.user_email || 'Unknown'}</p>
                                </div>
                                {/* Restore button for entries with snapshots */}
                                {entry.snapshot && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => restoreFromHistoryEntry(entry.id, entry.description)}
                                    className="rounded-full text-xs"
                                    style={{ borderColor: '#10B981', color: '#10B981' }}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Restore
                                  </Button>
                                )}
                                {entry.action === 'save_point' && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => restoreStockSavePoint(entry.id, entry.type)}
                                    className="rounded-full text-xs"
                                    style={{ borderColor: '#3B82F6', color: '#3B82F6' }}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Restore
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Manual Saves Tab */}
                  {stockHistoryTab === 'manual-saves' && (
                    <>
                      <div className="flex gap-2 mb-4">
                        <input
                          ref={manualSaveFileInputRef}
                          type="file"
                          accept=".json"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              uploadManualSave(e.target.files[0]);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => manualSaveFileInputRef.current?.click()}
                          className="rounded-full"
                          style={{ borderColor: '#3B82F6', color: '#3B82F6' }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Save
                        </Button>
                        <Button
                          variant="outline"
                          onClick={loadManualSaves}
                          className="rounded-full"
                          style={{ borderColor: enconOrange, color: enconOrange }}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Refresh
                        </Button>
                      </div>
                      
                      <p className="text-sm text-gray-500 mb-4">
                        Manual saves created from the Weekly Stock Report page. Maximum 5 saves stored.
                      </p>
                      
                      {loadingManualSaves ? (
                        <div className="text-center py-8 text-gray-500">Loading saves...</div>
                      ) : manualSaves.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No manual saves found. Use the "Save" button on the Weekly Stock Report page to create backups.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {manualSaves.map((save, idx) => (
                            <div 
                              key={save.id || idx} 
                              className="p-4 border-2 rounded-lg bg-blue-50 border-blue-200"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-200 text-blue-700">
                                      Manual Save
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(save.timestamp).toLocaleString('en-GB')}
                                    </span>
                                  </div>
                                  <p className="font-medium text-sm">{save.name}</p>
                                  <p className="text-xs text-gray-600 mt-1">
                                    W/C: {save.week_commencing} | Items: {save.total_items} | Notes: {save.items_with_notes}
                                  </p>
                                  <div className="flex gap-2 mt-1">
                                    {save.items_critical > 0 && (
                                      <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                        {save.items_critical} Critical
                                      </span>
                                    )}
                                    {save.items_concern > 0 && (
                                      <span className="px-2 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700">
                                        {save.items_concern} Not of Concern
                                      </span>
                                    )}
                                    {save.items_ok > 0 && (
                                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                        {save.items_ok} OK
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-400 mt-1">By: {save.user_email || 'Unknown'}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadManualSave(save.id, save.name)}
                                    className="rounded-full text-xs"
                                    style={{ borderColor: '#6B7280', color: '#6B7280' }}
                                  >
                                    <Download className="w-3 h-3 mr-1" />
                                    Download
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => restoreManualSave(save.id, save.name)}
                                    className="rounded-full text-xs"
                                    style={{ borderColor: '#10B981', color: '#10B981' }}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Restore
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteManualSave(save.id)}
                                    className="rounded-full text-xs"
                                    style={{ borderColor: '#EF4444', color: '#EF4444' }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
          )}

          {/* Stock Database Tab */}
          {user?.role === 'super_admin' && (
          <TabsContent value="stock-database" className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Stock Database Management</span>
                  <Button
                    onClick={() => setShowInventoryModal(true)}
                    variant="outline"
                    style={{ borderColor: enconOrange, color: enconOrange }}
                  >
                    <Search className="w-4 h-4 mr-2" />
                    Search Inventory
                  </Button>
                </CardTitle>
                <p className="text-sm text-gray-500">
                  Manage product groups and supplier categorization
                </p>
              </CardHeader>
              <CardContent className="space-y-6 bg-white">
                
                {/* NOTE: Master Stock Database and Add Product sections moved to bottom */}
                
                <div className="hidden">
                  <h3 className="font-semibold text-lg mb-3 hidden" style={{ color: enconOrange }}>
                    Add New Product (Hidden - moved to Master Database section)
                  </h3>
                  <div className="grid grid-cols-2 gap-3 hidden">
                    <div>
                      <Label>Product Code</Label>
                      <Input
                        value={newProduct.code}
                        onChange={(e) => setNewProduct({ ...newProduct, code: e.target.value })}
                        placeholder="e.g., 02BA017025"
                      />
                    </div>
                    <div>
                      <Label>Product Name</Label>
                      <Input
                        value={newProduct.name}
                        onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                        placeholder="e.g., RW PROROX PS 971"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={addNewProduct}
                    className="mt-3 hidden"
                    style={{ backgroundColor: enconOrange, color: 'white' }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </div>

                {/* Stock Management Section */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Stock Management
                    </h3>
                  </div>
                  <div className="space-y-4">
                    {/* Weekly Stock Report Settings */}
                    <div className="p-4 border-2 rounded-lg" style={{ borderColor: enconLightGrey }}>
                      <h4 className="font-medium mb-2">Weekly Stock Report</h4>
                      <p className="text-sm text-gray-500 mb-3">
                        Re-organise the weekly stock report table to match the original uploaded spreadsheet order.
                      </p>
                      <Button
                        onClick={async () => {
                          try {
                            const response = await api.post(`${API}/stock-management/weekly-stock/reorganise`);
                            if (response.data.success) {
                              toast.success(`Re-organised ${response.data.items_updated || 0} items to original order`);
                            }
                          } catch (error) {
                            toast.error('Failed to re-organise items');
                          }
                        }}
                        variant="outline"
                        className="gap-2"
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        <ArrowUpDown className="w-4 h-4" />
                        Re-organise to Original Order
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Product Grouping (prefix-based groups like 02A, 02B) */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Product Grouping ({productGroups.length})
                    </h3>
                  </div>
                  <Input
                    placeholder="Search product grouping..."
                    value={productGroupSearchQuery}
                    onChange={(e) => setProductGroupSearchQuery(e.target.value)}
                    className="mb-3"
                  />
                  <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                    {productGroups
                      .filter(group => 
                        group.prefix.toLowerCase().includes(productGroupSearchQuery.toLowerCase()) ||
                        group.name.toLowerCase().includes(productGroupSearchQuery.toLowerCase())
                      )
                      .map((group) => (
                      <div
                        key={group.id}
                        className="p-3 border-2 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderColor: enconLightGrey }}
                        onClick={() => viewGroupDetails(group)}
                      >
                        <p className="font-semibold text-sm" style={{ color: enconOrange }}>
                          {group.prefix}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">{group.name}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {group.product_count || 0} products
                        </p>
                      </div>
                    ))}
                  </div>
                  {ungroupedCount > 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                      <p className="text-sm font-semibold text-yellow-800">
                        ⚠️ {ungroupedCount} products need grouping
                      </p>
                      <Button
                        onClick={loadUngroupedProducts}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        Review Ungrouped Products
                      </Button>
                    </div>
                  )}
                </div>

                {/* Product Analysis Groups (formerly Supplier Groups) */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Product Analysis Groups ({supplierGroups.length})
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowMarketSectorsModal(true)}
                        variant="outline"
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Market Sectors
                      </Button>
                      <Button
                        onClick={() => setShowSupplierModal(true)}
                        style={{ backgroundColor: enconOrange, color: 'white' }}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Analysis Group
                      </Button>
                    </div>
                  </div>
                  <Input
                    placeholder="Search product analysis groups..."
                    value={supplierSearchQuery}
                    onChange={(e) => setSupplierSearchQuery(e.target.value)}
                    className="mb-3"
                  />
                  <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                    {supplierGroups
                      .filter(supplier => {
                        const searchLower = supplierSearchQuery.toLowerCase();
                        // Search by name, category, or prefixes within the group
                        const prefixMatch = supplier.product_group_ids?.some(groupId => {
                          const group = productGroups.find(g => g.id === groupId);
                          return group?.prefix?.toLowerCase().includes(searchLower) || 
                                 group?.name?.toLowerCase().includes(searchLower);
                        });
                        return supplier.name.toLowerCase().includes(searchLower) ||
                               supplier.category.toLowerCase().includes(searchLower) ||
                               prefixMatch;
                      })
                      .map((supplier) => (
                      <div
                        key={supplier.id}
                        className="p-3 border-2 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderColor: enconLightGrey }}
                        onClick={() => {
                          setEditingSupplier(supplier);
                          setNewSupplier({
                            name: supplier.name,
                            category: supplier.category,
                            product_group_ids: supplier.product_group_ids || []
                          });
                          setShowSupplierModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-sm" style={{ color: enconOrange }}>
                              {supplier.name}
                            </p>
                            <p className="text-xs text-gray-600 mt-1">
                              <span className={`px-2 py-1 rounded text-xs ${
                                supplier.category === 'structural' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : supplier.category === 'industrial'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-orange-100 text-orange-800'
                              }`}>
                                {supplier.category}
                              </span>
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {supplier.product_group_ids?.length || 0} analysis groups
                            </p>
                            {/* Show prefixes within this supplier group */}
                            {supplier.product_group_ids?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {supplier.product_group_ids.slice(0, 5).map(groupId => {
                                  const group = productGroups.find(g => g.id === groupId);
                                  return group ? (
                                    <span 
                                      key={groupId} 
                                      className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                                    >
                                      {group.prefix}
                                    </span>
                                  ) : null;
                                })}
                                {supplier.product_group_ids.length > 5 && (
                                  <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                    +{supplier.product_group_ids.length - 5}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* File Manager - MOVED TO MASTER DATABASE SECTION AT BOTTOM */}

                {/* Supplier Groups - Groups multiple Product Analysis Groups (under Product Analysis Groups) */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Supplier Groups ({manualSupplierGroups.length})
                    </h3>
                    <Button
                      onClick={() => setShowNewSupplierGroupModal(true)}
                      style={{ backgroundColor: enconOrange, color: 'white' }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Supplier Group
                    </Button>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    Group multiple Product Analysis Groups together into Supplier Groups for PI Counts.
                  </p>
                  <Input
                    placeholder="Search supplier groups..."
                    value={newSupplierGroupSearchQuery}
                    onChange={(e) => setNewSupplierGroupSearchQuery(e.target.value)}
                    className="mb-3"
                  />
                  <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto">
                    {manualSupplierGroups
                      .filter(sg =>
                        sg.name.toLowerCase().includes((newSupplierGroupSearchQuery || '').toLowerCase()) ||
                        sg.category.toLowerCase().includes((newSupplierGroupSearchQuery || '').toLowerCase())
                      )
                      .map((sg) => (
                      <div
                        key={sg.id}
                        className="p-3 border-2 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                        style={{ borderColor: enconLightGrey }}
                        onClick={() => {
                          setEditingManualSupplierGroup(sg);
                          setNewManualSupplierGroup({
                            name: sg.name,
                            category: sg.category,
                            product_analysis_group_ids: sg.product_analysis_group_ids || [],
                            crm_supplier_name: sg.crm_supplier_name || ''
                          });
                          setShowNewSupplierGroupModal(true);
                        }}
                      >
                        <p className="font-semibold text-sm" style={{ color: enconOrange }}>
                          {sg.name}
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          <span className={`px-2 py-1 rounded text-xs ${
                            sg.category === 'structural' 
                              ? 'bg-blue-100 text-blue-800' 
                              : sg.category === 'industrial'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {sg.category}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {sg.product_analysis_group_ids?.length || 0} analysis groups
                        </p>
                        {/* Show included analysis groups */}
                        {sg.product_analysis_group_ids?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {sg.product_analysis_group_ids.slice(0, 3).map(groupId => {
                              const analysisGroup = supplierGroups.find(g => g.id === groupId);
                              return analysisGroup ? (
                                <span 
                                  key={groupId} 
                                  className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded truncate max-w-[80px]"
                                  title={analysisGroup.name}
                                >
                                  {analysisGroup.name.substring(0, 10)}...
                                </span>
                              ) : null;
                            })}
                            {sg.product_analysis_group_ids.length > 3 && (
                              <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 text-xs rounded">
                                +{sg.product_analysis_group_ids.length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                    {manualSupplierGroups.length === 0 && (
                      <div className="col-span-3 text-center py-6 text-gray-500">
                        <p>No supplier groups created yet.</p>
                        <p className="text-sm">Click "Create Supplier Group" to get started.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Product Data Section (M4 Product Codes) */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Additional Product Data
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({additionalProductDataCount.toLocaleString()} products)
                      </span>
                    </h3>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowAdditionalProductUploadModal(true)}
                        variant="outline"
                        size="sm"
                        style={{ borderColor: enconOrange, color: enconOrange }}
                        data-testid="additional-product-data-upload-btn"
                      >
                        <Upload className="w-4 h-4 mr-1" />
                        Upload
                      </Button>
                      <Button
                        onClick={handleDownloadAdditionalProductData}
                        variant="outline"
                        size="sm"
                        style={{ borderColor: '#22c55e', color: '#16a34a' }}
                        disabled={additionalProductDataCount === 0}
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-blue-800 mb-1">📦 M4 Product Codes &amp; Cost Factor Master</p>
                    <p className="text-xs text-blue-700">
                      Contains product weights (prod_net_wt), search keywords (keyword1-6) and M2/coverage
                      cost factors. Used for transfer weight calculations, keyword-based stock search, and
                      M2-priced line totals on Create Quote &amp; Pro Forma. Upload either file — they merge
                      into the same product records instead of replacing each other.
                    </p>
                  </div>
                  
                  {/* Search and Product Cards */}
                  {additionalProductDataCount > 0 && (
                    <div>
                      <div className="flex gap-2 mb-3">
                        <Input
                          placeholder="Search by product code, description, or keyword..."
                          value={additionalProductDataSearch}
                          onChange={(e) => setAdditionalProductDataSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              loadAdditionalProductData(1, additionalProductDataSearch);
                            }
                          }}
                          className="flex-1"
                        />
                        <Button
                          onClick={() => loadAdditionalProductData(1, additionalProductDataSearch)}
                          size="sm"
                          style={{ backgroundColor: enconOrange }}
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            setAdditionalProductDataSearch('');
                            loadAdditionalProductData(1, '');
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Clear
                        </Button>
                      </div>
                      
                      {/* Product Cards Grid */}
                      <div className="grid grid-cols-4 gap-2 max-h-[300px] overflow-y-auto">
                        {additionalProductData.map((product) => (
                          <div
                            key={product.prod_code}
                            className="p-2 bg-white border rounded-lg hover:shadow-md cursor-pointer transition-shadow"
                            style={{ borderColor: '#e5e7eb' }}
                            onClick={() => {
                              setSelectedAdditionalProduct(product);
                              setAdditionalProductEditWeight(product.prod_net_wt?.toString() || '0');
                              setAdditionalProductEditKeywords(product.additional_keywords || '');
                              setAdditionalProductEditCostFactor(product.cost_factor?.toString() ?? '');
                              setAdditionalProductSafetyWord('');
                              setShowAdditionalProductModal(true);
                            }}
                          >
                            <div className="text-xs font-bold" style={{ color: enconOrange }}>
                              {product.prod_code}
                            </div>
                            <div className="text-[10px] text-gray-600 truncate" title={product.prod_desc}>
                              {product.prod_desc || 'No description'}
                            </div>
                            <div className="flex items-center justify-between mt-1">
                              <span className="text-[10px] text-gray-500">
                                {product.prod_net_wt > 0 ? `${product.prod_net_wt.toFixed(2)} kg` : 'No weight'}
                              </span>
                              <div className="flex items-center gap-1">
                                {product.cost_factor != null && (
                                  <span className="text-[9px] bg-orange-100 text-orange-700 px-1 rounded font-semibold" title="Cost Factor">
                                    CF {product.cost_factor}
                                  </span>
                                )}
                                {product.keyword1 && (
                                  <span className="text-[9px] bg-gray-100 px-1 rounded">
                                    {product.keyword1}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Pagination */}
                      {additionalProductDataTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-3">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={additionalProductDataPage <= 1}
                            onClick={() => loadAdditionalProductData(additionalProductDataPage - 1, additionalProductDataSearch)}
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-gray-600">
                            Page {additionalProductDataPage} of {additionalProductDataTotalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={additionalProductDataPage >= additionalProductDataTotalPages}
                            onClick={() => loadAdditionalProductData(additionalProductDataPage + 1, additionalProductDataSearch)}
                          >
                            Next
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {additionalProductDataCount === 0 && (
                    <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                      <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p>No additional product data uploaded yet.</p>
                      <p className="text-sm">Upload an M4 Product Code Excel file to get started.</p>
                    </div>
                  )}
                </div>

                {/* PI Counts Management Section */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      PI Counts Management
                    </h3>
                  </div>
                  
                  {/* Fix Warehouse Counts Button */}
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                    <h4 className="font-semibold text-yellow-800 mb-2">Fix Missing Descriptions</h4>
                    <p className="text-sm text-yellow-700 mb-3">
                      Update product descriptions for counts currently at the warehouse. Use this if descriptions are missing or were not populated correctly.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-yellow-700 border-yellow-500 hover:bg-yellow-100"
                      onClick={async () => {
                        try {
                          toast.loading('Fixing warehouse count descriptions...', { id: 'fix-warehouse-desc' });
                          const response = await api.post(`${API}/pi-counts/fix-warehouse-descriptions`);
                          const data = response.data;
                          toast.success(
                            `Fixed! Updated ${data.counts_updated} counts with ${data.products_updated} products.`,
                            { id: 'fix-warehouse-desc', duration: 5000 }
                          );
                        } catch (error) {
                          console.error('Fix warehouse descriptions error:', error);
                          toast.error(error.response?.data?.detail || 'Failed to fix descriptions', { id: 'fix-warehouse-desc' });
                        }
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Fix Warehouse Count Descriptions
                    </Button>
                  </div>
                  
                  {/* Free Stock Reports */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold" style={{ color: enconOrange }}>Free Stock Reports</h4>
                      <Button
                        onClick={() => setShowValuationsModal(true)}
                        variant="outline"
                        size="sm"
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        View All ({stockValuations.length})
                      </Button>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      Latest: {stockValuations.length > 0 && stockValuations[0].upload_date ? format(new Date(stockValuations[0].upload_date), 'dd MMM yyyy') : 'None uploaded'}
                    </p>
                    <p className="text-xs text-gray-500">
                      These GFST0000.csv files contain daily free stock data used for PI counts and inventory search.
                    </p>
                  </div>
                </div>

                {/* Import/Export Product & Supplier Groups */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-lg mb-3" style={{ color: enconOrange }}>
                    Import/Export Grouping Data
                  </h3>
                  
                  {/* Info box showing what's included */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-blue-800 mb-2">📦 Export/Import includes:</p>
                    <ul className="text-xs text-blue-700 space-y-1 ml-4 list-disc">
                      <li><strong>Product Groups</strong> - Prefix-based product groupings</li>
                      <li><strong>Product Analysis Groups</strong> - Analysis group configurations (~622)</li>
                      <li><strong>Supplier Groups</strong> - Manual supplier group definitions (~36)</li>
                      <li><strong>Master Stock</strong> - PI COUNT MASTER.csv data</li>
                      <li><strong>PI Count Schedules</strong> - Weekly schedule configuration</li>
                      <li><strong>Settings</strong> - Auto-run and other stock database settings</li>
                    </ul>
                    <p className="text-xs text-blue-600 mt-2 italic">
                      Use this to transfer all stock database configuration between environments (pre-production → production)
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <Button
                      onClick={handleExportGroups}
                      variant="outline"
                      className="bg-green-50 hover:bg-green-100"
                      style={{ borderColor: '#22c55e', color: '#16a34a' }}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export All Data
                    </Button>
                    <Button
                      onClick={() => document.getElementById('import-groups-input').click()}
                      variant="outline"
                      className="bg-orange-50 hover:bg-orange-100"
                      style={{ borderColor: enconOrange, color: enconOrange }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Import All Data
                    </Button>
                    <input
                      id="import-groups-input"
                      type="file"
                      accept=".json"
                      style={{ display: 'none' }}
                      onChange={handleImportGroups}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ⚠️ Importing will <strong>replace</strong> all existing grouping data with the imported data.
                  </p>
                </div>

                {/* Master Database Section - Hidden by default */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg" style={{ color: enconOrange }}>
                      Master Database Files
                    </h3>
                    {!showMasterDatabaseSection ? (
                      <Button
                        onClick={() => setShowMasterDatabaseSection(true)}
                        variant="outline"
                        style={{ borderColor: enconOrange, color: enconOrange }}
                      >
                        Show Master Database Files
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setShowMasterDatabaseSection(false)}
                        variant="outline"
                        className="text-gray-600"
                      >
                        Hide Section
                      </Button>
                    )}
                  </div>
                  
                  {showMasterDatabaseSection && (
                    <div className="space-y-6 p-4 border-2 rounded-lg" style={{ borderColor: enconOrange }}>
                      {/* Master Stock Database Upload */}
                      <div className="space-y-3">
                        <h4 className="font-semibold" style={{ color: enconOrange }}>
                          Master Stock Database
                        </h4>
                        <p className="text-sm text-gray-600">
                          Upload your PI COUNT MASTER.csv file to establish your complete product database
                        </p>
                        <Button
                          onClick={() => setShowUploadMasterModal(true)}
                          style={{ backgroundColor: enconOrange, color: 'white' }}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Master Stock
                        </Button>
                        <p className="text-xs text-gray-500">
                          {masterStockCount > 0 ? `${masterStockCount} products in database` : 'No master stock uploaded yet'}
                        </p>
                      </div>
                      
                      {/* Additional Stock CSV Upload (Free Stock Reports) */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-2" style={{ color: enconOrange }}>
                          Upload Additional Stock Items
                        </h4>
                        <p className="text-sm text-gray-600 mb-3">
                          Upload Free Stock Report CSVs to add new products not in the master list. 
                          Only new product codes will be added - existing products are preserved.
                        </p>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                          <p className="text-xs text-blue-700">
                            <strong>How it works:</strong> Upload any Free Stock Report CSV. The system will scan for product codes 
                            not already in the database and add them to the master stock, product groups, and supplier groups.
                            This makes them available in PI Counts, Global Search, Quotes, and Pro Formas.
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <label className="flex-1">
                            <input
                              type="file"
                              accept=".csv"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                
                                setUploadingAdditional(true);
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                try {
                                  const response = await api.post(`${API}/stock-database/upload-additional-stock`, formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                  });
                                  
                                  const data = response.data;
                                  let message = `Added ${data.products_added} new products! (${data.products_skipped} already existed)`;
                                  
                                  // Add info about Product Groups and Product Analysis Groups created
                                  if (data.new_groups_created > 0 || data.product_analysis_groups_created > 0) {
                                    const extras = [];
                                    if (data.new_groups_created > 0) {
                                      extras.push(`${data.new_groups_created} Product Groups`);
                                    }
                                    if (data.product_analysis_groups_created > 0) {
                                      extras.push(`${data.product_analysis_groups_created} Product Analysis Groups`);
                                    }
                                    if (extras.length > 0) {
                                      message += ` Created: ${extras.join(', ')}`;
                                    }
                                  }
                                  
                                  toast.success(message, { duration: 5000 });
                                  
                                  // Refresh data
                                  loadStockDatabaseData();
                                  loadAdditionalStockFiles();
                                } catch (error) {
                                  console.error('Upload error:', error);
                                  toast.error(error.response?.data?.detail || 'Failed to upload file');
                                } finally {
                                  setUploadingAdditional(false);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <Button
                              asChild
                              disabled={uploadingAdditional}
                              className="w-full cursor-pointer"
                              style={{ backgroundColor: '#3b82f6', color: 'white' }}
                            >
                              <span>
                                {uploadingAdditional ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                ) : (
                                  <Upload className="w-4 h-4 mr-2" />
                                )}
                                {uploadingAdditional ? 'Processing...' : 'Upload Free Stock Report CSV'}
                              </span>
                            </Button>
                          </label>
                        </div>
                        
                        {/* List of uploaded additional stock files */}
                        {additionalStockFiles.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold text-gray-600 mb-2">Previously Uploaded Files:</p>
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {additionalStockFiles.map((file) => (
                                <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-500" />
                                    <span className="font-medium">{file.filename}</span>
                                  </div>
                                  <div className="text-gray-500">
                                    +{file.products_added} products • {new Date(file.uploaded_at).toLocaleDateString('en-GB')}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Fix Unassigned Groups Button */}
                        <div className="mt-4 pt-4 border-t border-dashed">
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-xs text-yellow-800 mb-2">
                              <strong>Having issues with products not appearing in PI Counts?</strong><br/>
                              Click the button below to ensure all product groups are properly assigned to Product Analysis Groups.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-yellow-700 border-yellow-400 hover:bg-yellow-100"
                              onClick={async () => {
                                try {
                                  toast.loading('Fixing unassigned groups...', { id: 'fix-groups' });
                                  const response = await api.post(`${API}/stock-database/fix-unassigned-groups`);
                                  const data = response.data;
                                  toast.success(
                                    `Fixed! Found ${data.unassigned_found} unassigned groups. Created ${data.pags_created} new PAGs, updated ${data.pags_updated} existing.`,
                                    { id: 'fix-groups', duration: 5000 }
                                  );
                                  loadStockDatabaseData();
                                } catch (error) {
                                  console.error('Fix groups error:', error);
                                  toast.error(error.response?.data?.detail || 'Failed to fix groups', { id: 'fix-groups' });
                                }
                              }}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Fix Unassigned Product Groups
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="ml-2 text-blue-700 border-blue-400 hover:bg-blue-100"
                              onClick={async () => {
                                try {
                                  toast.loading('Rebuilding all product groups from master stock...', { id: 'rebuild-groups' });
                                  const response = await api.post(`${API}/stock-database/rebuild-product-groups`);
                                  const data = response.data;
                                  toast.success(
                                    `Rebuild complete! Processed ${data.products_processed} products. Created ${data.groups_created} new groups, ${data.pags_created} new PAGs.`,
                                    { id: 'rebuild-groups', duration: 5000 }
                                  );
                                  loadStockDatabaseData();
                                } catch (error) {
                                  console.error('Rebuild groups error:', error);
                                  toast.error(error.response?.data?.detail || 'Failed to rebuild groups', { id: 'rebuild-groups' });
                                }
                              }}
                            >
                              <Database className="w-4 h-4 mr-2" />
                              Rebuild All Groups
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="ml-2 text-green-700 border-green-400 hover:bg-green-100"
                              onClick={async () => {
                                try {
                                  toast.loading('Fixing PI Counts groups...', { id: 'fix-pi-counts' });
                                  const response = await api.post(`${API}/stock-database/fix-pi-counts-groups`);
                                  const data = response.data;
                                  toast.success(
                                    `PI Counts fixed! Found ${data.pags_unassigned} unassigned PAGs, created ${data.manual_groups_created} new Supplier Groups for PI Counts.`,
                                    { id: 'fix-pi-counts', duration: 5000 }
                                  );
                                  loadStockDatabaseData();
                                } catch (error) {
                                  console.error('Fix PI Counts error:', error);
                                  toast.error(error.response?.data?.detail || 'Failed to fix PI Counts groups', { id: 'fix-pi-counts' });
                                }
                              }}
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Fix PI Counts Groups
                            </Button>
                            
                            <Button
                              variant="outline"
                              size="sm"
                              className="ml-2 text-purple-700 border-purple-400 hover:bg-purple-100"
                              onClick={async () => {
                                try {
                                  toast.loading('Running debug...', { id: 'debug-arc' });
                                  const response = await api.get(`${API}/pi-counts/debug-arc`);
                                  const data = response.data;
                                  
                                  // Show results in an alert/modal
                                  let message = "=== DEBUG RESULTS ===\n\n";
                                  
                                  // Step 1
                                  const s1 = data.step1_free_stock_60n_products;
                                  message += `1. 60N in Free Stock: ${s1?.count || 0} products\n`;
                                  if (s1?.samples?.length > 0) {
                                    message += `   Samples: ${s1.samples.slice(0,3).join(', ')}\n`;
                                  }
                                  
                                  // Step 2
                                  const s2 = data.step2_product_group_60n;
                                  message += `\n2. Product Group 60N: ${s2 ? 'EXISTS (ID: ' + s2.id?.substring(0,8) + '...)' : 'NOT FOUND'}\n`;
                                  
                                  // Step 3
                                  const s3 = data.step3_pags_containing_60n;
                                  if (Array.isArray(s3)) {
                                    message += `\n3. PAGs with 60N: ${s3.length} found\n`;
                                    s3.forEach(p => message += `   - ${p.name}\n`);
                                  } else {
                                    message += `\n3. PAGs with 60N: ${s3?.error || 'Error'}\n`;
                                  }
                                  
                                  // Step 4
                                  const s4 = data.step4_manual_supplier_groups;
                                  if (Array.isArray(s4)) {
                                    message += `\n4. Supplier Groups: ${s4.length} found\n`;
                                    s4.forEach(p => message += `   - ${p.name}\n`);
                                  } else {
                                    message += `\n4. Supplier Groups: ${s4?.error || 'Error'}\n`;
                                  }
                                  
                                  // Step 5 - ARC groups with debug
                                  const s5 = data.step5_arc_manual_supplier_groups;
                                  if (Array.isArray(s5)) {
                                    message += `\n5. ARC Supplier Groups: ${s5.length} found\n`;
                                    s5.forEach(g => {
                                      message += `   - ${g.name}:\n`;
                                      if (g._debug_pags) {
                                        g._debug_pags.forEach(p => {
                                          message += `     PAG: ${p.name}, Prefixes: [${p.prefixes?.join(', ') || 'NONE'}]\n`;
                                        });
                                      }
                                    });
                                  }
                                  
                                  toast.dismiss('debug-arc');
                                  alert(message);
                                  console.log("Debug data:", data);
                                } catch (error) {
                                  console.error('Debug error:', error);
                                  toast.error('Debug failed: ' + (error.response?.data?.detail || error.message), { id: 'debug-arc' });
                                }
                              }}
                            >
                              <Search className="w-4 h-4 mr-2" />
                              Debug 60N/ARC
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Add New Product */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3" style={{ color: enconOrange }}>
                          Add New Product
                        </h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label>Product Code</Label>
                            <Input
                              value={newProduct.code}
                              onChange={(e) => setNewProduct({ ...newProduct, code: e.target.value })}
                              placeholder="e.g., 02BA017025"
                            />
                          </div>
                          <div>
                            <Label>Product Name</Label>
                            <Input
                              value={newProduct.name}
                              onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                              placeholder="e.g., RW PROROX PS 971"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={addNewProduct}
                          className="mt-3"
                          style={{ backgroundColor: enconOrange, color: 'white' }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Product
                        </Button>
                      </div>
                      
                      {/* File Manager */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3" style={{ color: enconOrange }}>
                          Stock Files
                        </h4>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {stockFiles.length === 0 ? (
                            <p className="text-sm text-gray-500">No stock files uploaded yet.</p>
                          ) : (
                            stockFiles.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className="w-5 h-5 text-gray-500" />
                                  <div>
                                    <p className="text-sm font-medium">{file.filename}</p>
                                    <p className="text-xs text-gray-500">
                                      Uploaded {format(new Date(file.uploaded_at), 'dd MMM yyyy HH:mm')}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  onClick={() => deleteStockFile(file.id)}
                                  variant="outline"
                                  size="sm"
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Pricing Tab */}
          {canViewTab('pricing') && (
          <TabsContent value="pricing" className="space-y-6">
            <Card className="bg-white">
              <CardContent className="p-6">
                <PricingSettings />
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Customer Database Tab - RETIRED for GDPR compliance */}
          {false && (
          <TabsContent value="customer-database">
          </TabsContent>
          )}

          {/* SoS Settings Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="sos-settings">
            <SoSSettingsPanel />
          </TabsContent>
          )}

          {/* Projects Database Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="projects-database">
            <ProjectsDatabaseManager />
          </TabsContent>
          )}

          {/* Phocas Customer Database Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="phocas-customer-database">
            <PhocasCustomerDatabase />
          </TabsContent>
          )}

          {/* Phocas/CRM Supplier Data Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="phocas-supplier-profile">
            <PhocasSupplierProfile />
          </TabsContent>
          )}

          {/* Future Features Tab */}
          {canViewTab('future-features') && (
          <TabsContent value="future-features">
            <FutureFeatures />
          </TabsContent>
          )}

          {/* Integrations Tab (super_admin only) */}
          {user?.role === 'super_admin' && (
          <TabsContent value="integrations" className="space-y-6">
            <ZohoIntegrationSettings />
            <div className="border-t border-gray-200 pt-6">
              <EightXEightSettings />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <SamsaraSettings />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <TerraIntegrationSettings />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <BarbourIntegrationSettings />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <McpCodeAccessSettings />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <McpDataAccessSettings />
            </div>
          </TabsContent>
          )}

          {/* Global Header (Nav UI v2) Tab (super_admin only) */}
          {user?.role === 'super_admin' && (
          <TabsContent value="global-header">
            <ManageGlobalHeaderSettings />
          </TabsContent>
          )}

          {/* Spaces Builder (super_admin only): arrange the home page of a Space from elements */}
          {user?.role === 'super_admin' && (
          <TabsContent value="spaces-builder">
            <SpacesBuilderPanel />
          </TabsContent>
          )}

          {/* 8x8 Analytics Tab (super_admin only) */}
          {user?.role === 'super_admin' && (
          <TabsContent value="eight-x8-analytics">
            <EightXEightAnalyticsSettings />
          </TabsContent>
          )}

          {/* Manage Changes Tab (super_admin only) */}
          {user?.role === 'super_admin' && (
          <TabsContent value="manage-changes">
            <ManageChangesSettings user={user} />
          </TabsContent>
          )}

          {/* Terra Tab — read-only AI data assistant, gated by can_access_ai_assistant */}
          {(user?.role === 'super_admin' || user?.can_access_ai_assistant) && (
          <TabsContent value="terra-assistant">
            <TerraAssistantPanel user={user} />
          </TabsContent>
          )}

          {/* Dashboard Manager Tab */}
          {canViewTab('dashboard-manager') && (
          <TabsContent value="dashboard-manager">
            <DashboardManager user={user} />
          </TabsContent>
          )}

          {/* Sales Back Office Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="sales-back-office" className="space-y-4">
            {/* Sales Info Bar Zero Toggle */}
            <Card className="border-2 border-red-200">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Sales Info Bar Display Control
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Zero All Sales Info Bar Figures</p>
                    <p className="text-sm text-gray-500">When enabled, all figures on the Sales Info Bar will display as £0.00</p>
                  </div>
                  <Switch
                    id="zero-sales-bar"
                    checked={settings?.zero_sales_info_bar || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await api.patch(`${API}/settings`, { zero_sales_info_bar: checked });
                        setSettings(prev => ({ ...prev, zero_sales_info_bar: checked }));
                        toast.success(checked ? 'Sales Info Bar figures will now show as zero' : 'Sales Info Bar figures restored');
                      } catch (error) {
                        toast.error('Failed to update setting');
                      }
                    }}
                  />
                </div>
                
                {/* Hide Sales Info Bar Entirely */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div>
                    <p className="font-medium">Hide Sales Info Bar Entirely</p>
                    <p className="text-sm text-gray-500">When enabled, the Sales Info Bar will be completely hidden from the dashboard</p>
                  </div>
                  <Switch
                    id="hide-sales-bar"
                    checked={settings?.hide_sales_info_bar || false}
                    onCheckedChange={async (checked) => {
                      try {
                        await api.patch(`${API}/settings`, { hide_sales_info_bar: checked });
                        setSettings(prev => ({ ...prev, hide_sales_info_bar: checked }));
                        toast.success(checked ? 'Sales Info Bar is now hidden' : 'Sales Info Bar is now visible');
                      } catch (error) {
                        toast.error('Failed to update setting');
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sales Back Office Management</CardTitle>
                <p className="text-sm text-gray-500">
                  Manage financial years, budgets, and growth projections for the sales dashboard.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Financial Year Section */}
                <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-semibold text-lg">Financial Years (Aug-July)</h3>
                    <Button
                      onClick={createNextFinancialYear}
                      className="bg-blue-600 hover:bg-blue-700"
                      size="sm"
                    >
                      Add Next Year
                    </Button>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Year Selector - Current/Next */}
                    {financialYears.length === 0 ? (
                      <div className="bg-white rounded-lg p-4 border">
                        <p className="text-sm text-gray-500">No financial years found. Create one to get started.</p>
                      </div>
                    ) : (
                      <>
                        {/* Year Toggle */}
                        <div className="bg-white rounded-lg p-4 border">
                          <Label className="mb-2 block">View Year Settings For:</Label>
                          <div className="flex gap-2">
                            {financialYears.filter(y => y.is_current).map(year => (
                              <Button
                                key={year.id}
                                onClick={() => {
                                  setSelectedYear(year);
                                  setGrowthRates(year?.growth_rates || {
                                    "Structural": 0.0,
                                    "Merchants": 0.0,
                                    "Technical": 0.0,
                                    "Neville Long": 0.0
                                  });
                                }}
                                variant={selectedYear?.id === year.id ? "default" : "outline"}
                                className={selectedYear?.id === year.id ? "bg-blue-600" : ""}
                              >
                                Current Year ({year.year_label})
                              </Button>
                            ))}
                            
                            {/* Show next year if exists */}
                            {(() => {
                              const currentYear = financialYears.find(y => y.is_current);
                              if (!currentYear) return null;
                              
                              const currentYearLabel = parseInt(currentYear.year_label.split('-')[0]);
                              const nextYear = financialYears.find(y => {
                                const yearLabel = parseInt(y.year_label.split('-')[0]);
                                return yearLabel === currentYearLabel + 1;
                              });
                              
                              if (!nextYear) return null;
                              
                              return (
                                <Button
                                  key={nextYear.id}
                                  onClick={() => {
                                    setSelectedYear(nextYear);
                                    setGrowthRates(nextYear?.growth_rates || {
                                      "Structural": 0.0,
                                      "Merchants": 0.0,
                                      "Technical": 0.0,
                                      "Neville Long": 0.0
                                    });
                                  }}
                                  variant={selectedYear?.id === nextYear.id ? "default" : "outline"}
                                  className={selectedYear?.id === nextYear.id ? "bg-green-600" : ""}
                                >
                                  Next Year ({nextYear.year_label})
                                </Button>
                              );
                            })()}
                          </div>
                          
                          {selectedYear && (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                              <p><strong>Viewing:</strong> {selectedYear.year_label} ({selectedYear.year_start} to {selectedYear.year_end})</p>
                              <p><strong>Budget Status:</strong> {selectedYear.has_budget ? '✅ Uploaded' : '❌ Not uploaded'}</p>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                    {/* Budget Upload Section */}
                    <div className="bg-white rounded-lg p-4 border">
                      <h4 className="font-medium mb-4">Upload Budget PDF</h4>
                      
                      <div className="space-y-4">
                        {!selectedYear ? (
                          <p className="text-sm text-orange-600">Please select a financial year first.</p>
                        ) : (
                          <>
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">
                                <strong>Note:</strong> Upload the full year budget PDF. The AI will extract:
                                budget amounts, KPIs, market sectors, monthly breakdowns, and business unit data.
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <Input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    uploadBudgetPDF(file);
                                    e.target.value = ''; // Reset input
                                  }
                                }}
                                disabled={uploadingBudget}
                              />
                              
                              {uploadingBudget && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span>Uploading and analyzing...</span>
                                    <span>{uploadProgress}%</span>
                                  </div>
                                  <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                      style={{ width: `${uploadProgress}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            <p className="text-xs text-gray-500">
                              Only PDF files are accepted. File will be encrypted and stored securely in MongoDB GridFS.
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Growth Rates Section - Only for Next Year */}
                    {selectedYear && !selectedYear.is_current && (
                    <div className="bg-white rounded-lg p-4 border border-green-300 bg-green-50">
                      <h4 className="font-medium mb-2 text-green-800">Growth Projections for Next Year (%)</h4>
                      <p className="text-sm text-green-700 mb-4">
                        Set growth percentages for {selectedYear.year_label} budget projection (used when budget PDF not yet uploaded).
                        These percentages will be applied to the current year's budget to generate projections.
                      </p>
                      
                      {selectedYear ? (
                        <>
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <Label>Structural</Label>
                              <Input 
                                type="number" 
                                step="0.1" 
                                value={growthRates["Structural"]}
                                onChange={(e) => setGrowthRates({...growthRates, "Structural": parseFloat(e.target.value) || 0})}
                                placeholder="0.0"
                              />
                            </div>
                            <div>
                              <Label>Merchants</Label>
                              <Input 
                                type="number" 
                                step="0.1" 
                                value={growthRates["Merchants"]}
                                onChange={(e) => setGrowthRates({...growthRates, "Merchants": parseFloat(e.target.value) || 0})}
                                placeholder="0.0"
                              />
                            </div>
                            <div>
                              <Label>Technical</Label>
                              <Input 
                                type="number" 
                                step="0.1" 
                                value={growthRates["Technical"]}
                                onChange={(e) => setGrowthRates({...growthRates, "Technical": parseFloat(e.target.value) || 0})}
                                placeholder="0.0"
                              />
                            </div>
                            <div>
                              <Label>Neville Long</Label>
                              <Input 
                                type="number" 
                                step="0.1" 
                                value={growthRates["Neville Long"]}
                                onChange={(e) => setGrowthRates({...growthRates, "Neville Long": parseFloat(e.target.value) || 0})}
                                placeholder="0.0"
                              />
                            </div>
                          </div>
                          
                          <Button
                            onClick={updateGrowthRates}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            Save Growth Rates for {selectedYear?.year_label}
                          </Button>
                        </>
                      ) : null}
                    </div>
                    )}
                  </div>
                </div>
                
                {/* Access Control Info */}
                <div className="border rounded-lg p-4 bg-gradient-to-r from-orange-50 to-pink-50">
                  <h3 className="font-semibold text-lg mb-2">Access Control</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    New permissions have been added to control access to sales data:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                    <li><strong>can_view_budget</strong> - Access to Budget page in Sales Dashboard</li>
                    <li><strong>can_view_sales_tracker</strong> - Access to Sales Tracker page in Reports</li>
                    <li><strong>can_view_error_log</strong> - Access to Error Log page</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-3">
                    Configure these in the User Admin tab to control who can view sales data.
                  </p>
                </div>

                {/* Sales Tracker Month Re-initialization */}
                <div className="p-4 bg-yellow-50 rounded-lg border-2 border-yellow-400">
                  <Label className="text-base font-semibold text-yellow-900 mb-2 block">🔄 Re-initialize Sales Tracker Month</Label>
                  <p className="text-sm text-yellow-800 mb-3">
                    Delete and recreate all entries for a specific month with default declining weights (1.30 to 0.00).
                    This will fix broken months and reset all data to zero values with proper weighted forecasts.
                  </p>
                  
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Select Financial Year</Label>
                      <select 
                        value={selectedYear?.id || ''}
                        onChange={(e) => {
                          const year = financialYears.find(y => y.id === e.target.value);
                          setSelectedYear(year);
                        }}
                        className="w-full p-2 border rounded-lg"
                      >
                        <option value="">-- Select Year --</option>
                        {financialYears.map(year => (
                          <option key={year.id} value={year.id}>
                            {year.year_name} ({year.year_start} to {year.year_end})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium mb-1 block">Select Month</Label>
                      <select 
                        id="reinit-month-select"
                        className="w-full p-2 border rounded-lg"
                        defaultValue=""
                      >
                        <option value="">-- Select Month --</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>
                    
                    <Button
                      onClick={async () => {
                        if (!selectedYear) {
                          toast.error('Please select a financial year first');
                          return;
                        }
                        
                        const monthSelect = document.getElementById('reinit-month-select');
                        const monthNumber = monthSelect.value;
                        if (!monthNumber) {
                          toast.error('Please select a month');
                          return;
                        }
                        
                        const monthName = monthSelect.options[monthSelect.selectedIndex].text;
                        
                        const confirmation = prompt(`⚠️ WARNING: This will DELETE all existing data for ${monthName} and recreate with zero values.\n\nType "REINITIALIZE" to confirm:`);
                        if (confirmation !== 'REINITIALIZE') {
                          toast.error('Cancelled - incorrect confirmation');
                          return;
                        }
                        
                        try {
                          toast.info(`Re-initializing ${monthName}...`);
                          const response = await api.post(
                            `${API}/sales-tracker/reinitialize-month/${selectedYear.id}/${monthNumber}`
                          );
                          
                          toast.success(
                            `✅ ${monthName} re-initialized!\n` +
                            `Deleted: ${response.data.deleted_entries} entries\n` +
                            `Created: ${response.data.created_entries} entries\n` +
                            `Working days: ${response.data.working_days}`
                          );
                        } catch (error) {
                          toast.error('Re-initialization failed: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white w-full"
                    >
                      🔄 Re-initialize Selected Month
                    </Button>
                    
                    <p className="text-xs text-yellow-700 mt-2">
                      ⚠️ <strong>Warning:</strong> This action will permanently delete all existing entries, photos, and manual data for the selected month. 
                      The month will be recreated with zero values and default declining weights from 1.30 to 0.00.
                    </p>
                  </div>
                </div>

                {/* Sales Tracker Backup Download/Upload */}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-400 mt-4">
                  <Label className="text-base font-semibold text-blue-900 mb-2 block">💾 Sales Tracker Data - Download/Upload</Label>
                  <p className="text-sm text-blue-800 mb-3">
                    Download ONLY your sales tracker data (entries, financial years, budgets) from pre-production and upload it to production. 
                    This will NOT affect users, vehicles, or other data - only sales tracker information.
                  </p>
                  
                  <div className="space-y-3">
                    {/* Download Sales Tracker Backup */}
                    <div className="p-3 bg-white rounded border border-blue-300">
                      <Label className="text-sm font-medium mb-1 block text-blue-900">📥 Download Sales Tracker Data</Label>
                      <p className="text-xs text-gray-600 mb-2">
                        Downloads ONLY sales tracker entries, financial years, budgets, and related data. Safe for production upload.
                      </p>
                      <Button
                        onClick={async () => {
                          try {
                            toast.info('Downloading sales tracker data...');
                            
                            // Use axios to download with authentication
                            const response = await api.get(`${API}/backups/sales-tracker/download`, {
                              responseType: 'blob'
                            });
                            
                            // Create blob URL and trigger download
                            const blob = new Blob([response.data], { type: 'application/json' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            const filename = `sales_tracker_backup_${new Date().toISOString().split('T')[0]}.json`;
                            link.download = filename;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                            toast.success('✅ Sales tracker data downloaded successfully!');
                          } catch (error) {
                            toast.error('Download failed: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                      >
                        📥 Download Sales Tracker Data
                      </Button>
                    </div>

                    {/* Upload and Restore Sales Tracker Data */}
                    <div className="p-3 bg-white rounded border border-orange-300">
                      <Label className="text-sm font-medium mb-1 block text-orange-900">📤 Upload & Restore Sales Tracker Data</Label>
                      <p className="text-xs text-gray-600 mb-2">
                        Upload a sales tracker backup file and restore it. 
                        <strong className="text-orange-600"> This will REPLACE all sales tracker data only!</strong>
                      </p>
                      
                      <input
                        type="file"
                        id="sales-tracker-upload-input"
                        accept=".json"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          
                          const confirmation = prompt(
                            `⚠️ WARNING: Uploading this backup will REPLACE all sales tracker data!\n\n` +
                            `File: ${file.name}\n` +
                            `Size: ${(file.size / 1024 / 1024).toFixed(2)} MB\n\n` +
                            `This will NOT affect users, vehicles, or transport logs.\n\n` +
                            `Type "RESTORE SALES TRACKER" to confirm:`
                          );
                          
                          if (confirmation !== 'RESTORE SALES TRACKER') {
                            toast.error('Cancelled - incorrect confirmation');
                            e.target.value = ''; // Reset file input
                            return;
                          }
                          
                          try {
                            toast.info('Uploading and restoring sales tracker data...');
                            
                            const formData = new FormData();
                            formData.append('file', file);
                            
                            const response = await api.post(
                              `${API}/backups/sales-tracker/upload`,
                              formData,
                              {
                                headers: {
                                  'Content-Type': 'multipart/form-data',
                                },
                              }
                            );
                            
                            toast.success(
                              `✅ Sales tracker data restored successfully!\n` +
                              `Collections: ${response.data.collections_restored || 'N/A'}\n` +
                              `Documents: ${response.data.documents_restored || 'N/A'}`
                            );
                            
                            // Reload page after successful restore
                            setTimeout(() => {
                              window.location.reload();
                            }, 2000);
                          } catch (error) {
                            toast.error('Restore failed: ' + (error.response?.data?.detail || error.message));
                          } finally {
                            e.target.value = ''; // Reset file input
                          }
                        }}
                      />
                      
                      <Button
                        onClick={() => {
                          document.getElementById('sales-tracker-upload-input')?.click();
                        }}
                        className="bg-orange-600 hover:bg-orange-700 text-white w-full"
                      >
                        📤 Upload & Restore Sales Tracker Data
                      </Button>
                      
                      <p className="text-xs text-orange-600 mt-2 font-semibold">
                        ⚠️ This will permanently replace sales tracker data only! Users and vehicles are safe.
                      </p>
                    </div>
                  </div>
                </div>

                {/* M4 Images File Manager */}
                <div className="p-4 bg-green-50 rounded-lg border-2 border-green-400 mt-4">
                  <Label className="text-base font-semibold text-green-900 mb-2 block">📸 M4 Upload Images</Label>
                  <p className="text-sm text-green-800 mb-3">
                    View and download all M4 upload images. Images are named: Upload(date)-data for(date)
                  </p>
                  
                  <Button
                    onClick={async () => {
                      try {
                        toast.info('Loading M4 images...');
                        const response = await api.get(`${API}/m4-images/list`);
                        
                        if (response.data.images && response.data.images.length > 0) {
                          setM4Images(response.data.images);
                          setM4ImagesModalOpen(true);
                          toast.success(`Found ${response.data.images.length} M4 images`);
                        } else {
                          toast.info('No M4 images found');
                        }
                      } catch (error) {
                        toast.error('Failed to load M4 images: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white w-full"
                  >
                    📸 View All M4 Images
                  </Button>
                </div>

                {/* Daily Sales Tracker Backups */}
                <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-400 mt-4">
                  <Label className="text-base font-semibold text-purple-900 mb-2 block">🕐 Daily Automatic Backups</Label>
                  <p className="text-sm text-purple-800 mb-3">
                    Automated daily backups at 11:00 PM UK time. Download any backup from the last 30 days.
                  </p>
                  
                  <Button
                    onClick={async () => {
                      try {
                        toast.info('Loading daily backups...');
                        const response = await api.get(`${API}/backups/sales-tracker/daily/list`);
                        
                        if (response.data.backups && response.data.backups.length > 0) {
                          setDailyBackups(response.data.backups);
                          setDailyBackupsModalOpen(true);
                          toast.success(`Found ${response.data.backups.length} daily backups`);
                        } else {
                          toast.info('No daily backups found yet');
                        }
                      } catch (error) {
                        toast.error('Failed to load backups: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                  >
                    🕐 View Daily Backups
                  </Button>
                </div>

                
              </CardContent>
            </Card>
          </TabsContent>
          )}


          {/* Database Management Tab */}
          {user?.role === 'super_admin' && (
          <TabsContent value="database" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>🗄️ Database Management</CardTitle>
                <p className="text-sm text-gray-600">
                  Direct access to database collections for super admins. Export, import, and manage collections.
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Collection Selector & Info */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                  <Label className="text-base font-semibold mb-3 block">Select Collection</Label>
                  <select 
                    value={selectedCollection}
                    onChange={(e) => {
                      setSelectedCollection(e.target.value);
                      loadCollectionInfo(e.target.value);
                    }}
                    className="w-full p-2 border rounded-lg mb-3"
                  >
                    <option value="">-- Select a collection --</option>
                    <option value="daily_logs">daily_logs (Transport Log Data)</option>
                    <option value="financial_years">financial_years (Budget & Sales Tracker)</option>
                    <option value="budgets">budgets (Budget Data)</option>
                    <option value="sales_tracker_entries">sales_tracker_entries (Sales Tracker)</option>
                    <option value="sales_tracker_weights">sales_tracker_weights (Sales Forecast Weights)</option>
                    <option value="friday_forecasts">friday_forecasts (Friday Forecasts)</option>
                    <option value="users">users (User Accounts)</option>
                    <option value="weekly_backups">weekly_backups (Weekly Backup Metadata)</option>
                    <option value="two_week_backups">two_week_backups (2-Week Backup Metadata)</option>
                    <option value="history_snapshots">history_snapshots (History Backup)</option>
                    <option value="hub_apps">hub_apps (Dashboard Cards)</option>
                    <option value="dashboard_links">dashboard_links (Dashboard Links)</option>
                    <option value="dashboard_groups">dashboard_groups (Dashboard Groups)</option>
                  </select>
                  
                  {selectedCollection && collectionInfo && (
                    <div className="p-3 bg-white rounded border">
                      <p className="text-sm font-semibold text-gray-700">Collection: <span className="text-purple-600">{selectedCollection}</span></p>
                      <p className="text-sm text-gray-600">Documents: <span className="font-mono">{collectionInfo.count}</span></p>
                      <p className="text-xs text-gray-500 mt-1">Last checked: {new Date().toLocaleTimeString()}</p>
                    </div>
                  )}
                </div>

                {/* Export/Import Actions */}
                {selectedCollection && (
                  <div className="space-y-4">
                    {/* Export */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <Label className="text-base font-semibold text-green-900 mb-2 block">📥 Export Collection</Label>
                      <p className="text-sm text-green-800 mb-3">
                        Download all documents from <strong>{selectedCollection}</strong> as JSON file.
                      </p>
                      <Button
                        onClick={async () => {
                          try {
                            toast.info(`Exporting ${selectedCollection}...`);
                            const response = await api.get(`${API}/admin/database/export/${selectedCollection}`);
                            
                            // Download as JSON
                            const dataStr = JSON.stringify(response.data, null, 2);
                            const dataBlob = new Blob([dataStr], { type: 'application/json' });
                            const url = URL.createObjectURL(dataBlob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${selectedCollection}_${new Date().toISOString().split('T')[0]}.json`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            
                            toast.success(`Exported ${response.data.count} documents from ${selectedCollection}`);
                          } catch (error) {
                            toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        📥 Export {selectedCollection}
                      </Button>
                    </div>

                    {/* Import */}
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <Label className="text-base font-semibold text-orange-900 mb-2 block">📤 Import Collection</Label>
                      <p className="text-sm text-orange-800 mb-3">
                        Upload JSON file to <strong>replace</strong> all documents in <strong>{selectedCollection}</strong>.
                      </p>
                      <input
                        type="file"
                        accept=".json"
                        id={`import-${selectedCollection}`}
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          if (!window.confirm(`⚠️ REPLACE ${selectedCollection}?\n\nThis will DELETE all existing documents and replace with uploaded data.\n\nAre you absolutely sure?`)) {
                            e.target.value = '';
                            return;
                          }
                          
                          try {
                            toast.info('Reading file...');
                            const text = await file.text();
                            const data = JSON.parse(text);
                            
                            toast.info(`Importing to ${selectedCollection}...`);
                            const response = await api.post(`${API}/admin/database/import/${selectedCollection}`, data);
                            
                            toast.success(response.data.message);
                            loadCollectionInfo(selectedCollection);
                          } catch (error) {
                            toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button
                        onClick={() => document.getElementById(`import-${selectedCollection}`).click()}
                        variant="outline"
                        className="border-2 border-orange-600 text-orange-600"
                      >
                        📤 Import to {selectedCollection}
                      </Button>
                    </div>

                    {/* Danger Zone */}
                    <div className="p-4 bg-red-50 rounded-lg border-2 border-red-300">
                      <Label className="text-base font-semibold text-red-900 mb-2 block">⚠️ Danger Zone</Label>
                      <p className="text-sm text-red-800 mb-3">
                        Clear all documents from <strong>{selectedCollection}</strong>. This cannot be undone!
                      </p>
                      <Button
                        onClick={async () => {
                          if (!window.confirm(`🚨 DELETE ALL DATA FROM ${selectedCollection}?\n\nThis will permanently delete all ${collectionInfo?.count} documents.\n\nThis CANNOT be undone!\n\nType DELETE to confirm.`)) {
                            return;
                          }
                          
                          const userInput = prompt('Type DELETE to confirm:');
                          if (userInput !== 'DELETE') {
                            toast.error('Cancelled - incorrect confirmation');
                            return;
                          }
                          
                          try {
                            toast.info(`Clearing ${selectedCollection}...`);
                            const response = await api.delete(`${API}/admin/database/clear/${selectedCollection}`);
                            toast.success(response.data.message);
                            loadCollectionInfo(selectedCollection);
                          } catch (error) {
                            toast.error('Clear failed: ' + (error.response?.data?.detail || error.message));
                          }
                        }}
                        variant="destructive"
                      >
                        🗑️ Clear {selectedCollection}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Export All Collections */}
                <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                  <Label className="text-base font-semibold text-blue-900 mb-2 block">📦 Export All Collections</Label>
                  <p className="text-sm text-blue-800 mb-3">
                    Download complete database backup (all collections in one file).
                  </p>
                  <Button
                    onClick={async () => {
                      try {
                        toast.info('Exporting entire database...');
                        const response = await api.get(`${API}/admin/database/export-all`);
                        
                        // Download as JSON
                        const dataStr = JSON.stringify(response.data, null, 2);
                        const dataBlob = new Blob([dataStr], { type: 'application/json' });
                        const url = URL.createObjectURL(dataBlob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `full_database_${new Date().toISOString().split('T')[0]}.json`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        
                        toast.success(`Exported ${response.data.metadata.total_collections} collections with ${response.data.metadata.total_documents} documents`);
                      } catch (error) {
                        toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    📦 Export All Collections
                  </Button>
                </div>

                {/* Error Log Database Export */}
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <Label className="text-base font-semibold text-orange-900 mb-2 block">📤 Export Error Log Database</Label>
                  <p className="text-sm text-orange-800 mb-3">
                    Export all error log entries as JSON to transfer between preview and production sites.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          toast.info('Exporting error log entries...');
                          const response = await api.get(`${API}/admin/database/export/error_log_entries`);
                          
                          // Download as JSON
                          const dataStr = JSON.stringify(response.data, null, 2);
                          const dataBlob = new Blob([dataStr], { type: 'application/json' });
                          const url = URL.createObjectURL(dataBlob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `error_log_entries_${new Date().toISOString().split('T')[0]}.json`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          
                          toast.success(`Exported ${response.data.count} error log entries`);
                        } catch (error) {
                          toast.error('Export failed: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                    >
                      📥 Export Error Log Collection
                    </Button>

                    <label className="cursor-pointer">
                      <Button
                        type="button"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => document.getElementById('error-log-import-file').click()}
                      >
                        📤 Import Error Log Collection
                      </Button>
                      <input
                        id="error-log-import-file"
                        type="file"
                        accept=".json"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          
                          try {
                            toast.info('Importing error log entries...');
                            
                            // Read the JSON file
                            const text = await file.text();
                            const data = JSON.parse(text);
                            
                            // Upload to server
                            const response = await api.post(`${API}/admin/database/import/error_log_entries`, data);
                            
                            toast.success(`Imported ${response.data.inserted} error log entries`);
                            e.target.value = ''; // Reset file input
                          } catch (error) {
                            toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-orange-600 mt-2">
                    <strong>Export:</strong> Download all error log entries from preview site.<br/>
                    <strong>Import:</strong> Upload error log JSON file to production site after deployment.
                  </p>
                </div>

              </CardContent>
            </Card>
            <HOSupportSeedButton />
          </TabsContent>
          )}

          {/* Files Admin - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="files-admin" className="space-y-4">
            <FilesAdmin />
          </TabsContent>
          )}

          {/* Usage Tab - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="usage" className="space-y-4">
            {/* Zoho API Usage Section (moved from Integrations) */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" style={{ color: '#ee7203' }} />
                  Zoho API Usage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ZohoApiUsagePanel />
              </CardContent>
            </Card>

            {/* AI Usage Logs Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5" style={{ color: '#ee7203' }} />
                    AI Usage Logs
                    <span className="text-xs font-normal text-gray-500 ml-1">
                      {aiLogPeriod.type === 'all_time' ? '— All Time'
                        : aiLogPeriod.type === 'year' ? `— Full Year ${aiLogPeriod.year}`
                        : `— ${new Date(aiLogPeriod.year, (aiLogPeriod.month||1)-1).toLocaleString('en-GB',{month:'long',year:'numeric'})}`}
                    </span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Month Picker */}
                    <Popover open={periodPickerOpen} onOpenChange={setPeriodPickerOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-1">
                          <CalendarDays className="w-4 h-4" />
                          {aiLogPeriod.type === 'all_time' ? 'All Time'
                            : aiLogPeriod.type === 'year' ? `${aiLogPeriod.year}`
                            : new Date(aiLogPeriod.year, (aiLogPeriod.month||1)-1).toLocaleString('en-GB',{month:'short',year:'numeric'})}
                          <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-3" align="end">
                        <div className="space-y-3">
                          <button
                            onClick={() => { setAiLogPeriod({type:'all_time'}); setPeriodPickerOpen(false); }}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${aiLogPeriod.type==='all_time' ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-100'}`}
                          >All Time</button>
                          <div className="flex items-center justify-between">
                            <button onClick={() => setPickerYear(y => y-1)} className="p-1 hover:bg-gray-100 rounded">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setAiLogPeriod({type:'year', year: pickerYear}); setPeriodPickerOpen(false); }}
                              className={`text-sm font-semibold px-3 py-1 rounded-md transition-colors ${aiLogPeriod.type==='year' && aiLogPeriod.year===pickerYear ? 'bg-orange-100 text-orange-700' : 'hover:bg-gray-100'}`}
                            >{pickerYear} (Full Year)</button>
                            <button onClick={() => setPickerYear(y => y+1)} className="p-1 hover:bg-gray-100 rounded">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-1">
                            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, idx) => {
                              const now = new Date();
                              const isFuture = pickerYear > now.getFullYear() || (pickerYear === now.getFullYear() && idx > now.getMonth());
                              const isSelected = aiLogPeriod.type === 'month' && aiLogPeriod.year === pickerYear && aiLogPeriod.month === idx+1;
                              return (
                                <button key={m} disabled={isFuture}
                                  onClick={() => { setAiLogPeriod({type:'month', year:pickerYear, month:idx+1}); setPeriodPickerOpen(false); }}
                                  className={`text-xs py-1.5 rounded-md font-medium transition-colors ${isFuture ? 'text-gray-300 cursor-not-allowed' : ''} ${isSelected ? 'bg-orange-500 text-white' : !isFuture ? 'hover:bg-orange-100 hover:text-orange-700' : ''}`}
                                >{m}</button>
                              );
                            })}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="sm" onClick={() => fetchAiUsageLogs(aiLogPeriod)} disabled={loadingAiLogs}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingAiLogs ? 'animate-spin' : ''}`} />Refresh
                    </Button>
                    <Button variant="destructive" size="sm" onClick={clearAiUsageLogs} disabled={loadingAiLogs || aiUsageLogs.length === 0}>
                      <Trash2 className="w-4 h-4 mr-2" />Clear All
                    </Button>
                  </div>
                </CardTitle>
                <CardDescription>
                  AI usage tracked by type, location and user. Costs are <strong>estimates</strong> based on typical token usage — the actual credits charged by the Emergent LLM key may differ slightly. All cost (£) values are shown in pence (p). Credit rate: £0.15 per credit (£15 / 100 credits).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-xs text-blue-500 font-medium uppercase tracking-wide">Total Calls</div>
                    <div className="text-2xl font-bold text-blue-700 mt-1">{aiUsageStats.total_logs}</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="text-xs text-orange-500 font-medium uppercase tracking-wide">Est. Credits</div>
                    <div className="text-2xl font-bold text-orange-700 mt-1">~{(() => { const c = aiUsageStats.total_estimated_credits||0; return c < 0.01 ? c.toFixed(5) : c < 0.1 ? c.toFixed(4) : c < 1 ? c.toFixed(3) : c.toFixed(2); })()}</div>
                    <div className="text-xs text-orange-400 mt-0.5">
                      {(() => { const pence=(aiUsageStats.total_estimated_gbp||0)*100; return pence < 5 ? `~${pence.toFixed(3)}p` : pence < 100 ? `~${pence.toFixed(2)}p` : `~£${(aiUsageStats.total_estimated_gbp||0).toFixed(3)}`; })()}
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-xs text-green-500 font-medium uppercase tracking-wide">AI Features</div>
                    <div className="text-2xl font-bold text-green-700 mt-1">{aiUsageStats.usage_by_feature.length}</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-xs text-purple-500 font-medium uppercase tracking-wide">Active Users</div>
                    <div className="text-2xl font-bold text-purple-700 mt-1">{aiUsageStats.usage_by_user.length}</div>
                  </div>
                </div>

                {/* Per-AI Model Breakdown */}
                {aiUsageStats.usage_by_model && aiUsageStats.usage_by_model.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-orange-500" />Usage by AI Model
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {aiUsageStats.usage_by_model.map((m, idx) => {
                        const modelName = (m._id || 'Unknown').replace(/^(openai|anthropic|gemini)\//i, '');
                        const pence = (m.total_gbp||0)*100;
                        const gbpDisplay = pence === 0 ? '0.000p' : pence < 5 ? `${pence.toFixed(3)}p` : pence < 100 ? `${pence.toFixed(2)}p` : `£${(m.total_gbp||0).toFixed(4)}`;
                        const colors = ['border-blue-200 bg-blue-50','border-green-200 bg-green-50','border-purple-200 bg-purple-50','border-amber-200 bg-amber-50','border-red-200 bg-red-50','border-teal-200 bg-teal-50'];
                        return (
                          <div key={idx} className={`p-3 rounded-lg border ${colors[idx % colors.length]}`}>
                            <div className="font-mono text-sm font-semibold text-gray-800 truncate">{modelName || 'Unknown'}</div>
                            <div className="mt-2 space-y-1">
                              <div className="flex justify-between text-xs"><span className="text-gray-500">Calls</span><span className="font-bold text-gray-800">{m.count}</span></div>
                              <div className="flex justify-between text-xs"><span className="text-gray-500">Credits</span><span className="font-bold text-orange-600">~{(() => { const c = m.total_credits||0; return c < 0.01 ? c.toFixed(5) : c < 0.1 ? c.toFixed(4) : c < 1 ? c.toFixed(3) : c.toFixed(2); })()}</span></div>
                              <div className="flex justify-between text-xs"><span className="text-gray-500">Cost</span><span className="font-bold text-emerald-700">~{gbpDisplay}</span></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Available AI Features */}
                <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-purple-50">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-blue-600" />Available AI Features
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(aiUsageStats.available_ai_features || {}).map(([key, feature]) => {
                      const usageData = aiUsageStats.usage_by_feature.find(f => f._id === key);
                      const usageCount = usageData?.count || 0;
                      const totalCredits = usageData?.total_credits || 0;
                      const totalGbp = usageData?.total_gbp || 0;
                      const totalPence = totalGbp * 100;
                      const gbpDisplay = totalPence < 5 ? `${totalPence.toFixed(3)}p` : totalPence < 100 ? `${totalPence.toFixed(2)}p` : `£${totalGbp.toFixed(4)}`;
                      const perCallPence = (feature.gbp_cost || 0) * 100;
                      const perCallDisplay = perCallPence === 0 ? '0.000p' : perCallPence < 5 ? `${perCallPence.toFixed(3)}p` : `${perCallPence.toFixed(2)}p`;
                      const perCallCr = feature.credits || 0;
                      const perCallCrDisplay = perCallCr < 0.01 ? perCallCr.toFixed(5) : perCallCr < 0.1 ? perCallCr.toFixed(4) : perCallCr < 1 ? perCallCr.toFixed(3) : perCallCr.toFixed(2);
                      const totalCrDisplay = (() => { const c = totalCredits; return c < 0.01 ? c.toFixed(5) : c < 0.1 ? c.toFixed(4) : c < 1 ? c.toFixed(3) : c.toFixed(2); })();
                      return (
                        <div key={key} className="p-3 bg-white rounded-lg border border-blue-100 shadow-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-gray-800 text-sm">{feature.name}</div>
                              <div className="text-xs text-gray-500 mt-0.5">{feature.description}</div>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-xs font-mono text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">{feature.model}</span>
                                <span className="text-xs text-gray-400">{feature.location}</span>
                              </div>
                            </div>
                            <div className="text-right ml-3 flex-shrink-0">
                              <div className="text-xs font-bold text-orange-600">~{perCallCrDisplay} cr/call</div>
                              <div className="text-xs font-bold text-emerald-700">~{perCallDisplay}/call</div>
                              {usageCount > 0 ? (
                                <div className="text-xs text-green-600 mt-1">
                                  <div>{usageCount} uses</div>
                                  <div>~{totalCrDisplay} cr / ~{gbpDisplay}</div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400 mt-1">Not used yet</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(aiUsageStats.available_ai_features || {}).length === 0 && (
                      <div className="col-span-2 text-center py-4 text-gray-500">
                        <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Click "Refresh" to load AI features</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Monthly History */}
                {aiUsageStats.usage_by_month && aiUsageStats.usage_by_month.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <CalendarDays className="w-4 h-4" />Monthly History (last 12 months)
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {aiUsageStats.usage_by_month.map((month, idx) => {
                        const monthName = month._id ? new Date(month._id + '-01').toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : 'Unknown';
                        const pence = (month.total_gbp||0)*100;
                        const gbpDisplay = pence < 5 ? `${pence.toFixed(3)}p` : pence < 100 ? `${pence.toFixed(2)}p` : `£${(month.total_gbp||0).toFixed(3)}`;
                        return (
                          <div key={idx} className="p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-orange-50 hover:border-orange-200 transition-colors"
                            onClick={() => {
                              if (!month._id) return;
                              const [y, mo] = month._id.split('-');
                              setAiLogPeriod({type:'month', year:parseInt(y), month:parseInt(mo)});
                            }}>
                            <div className="text-sm font-semibold text-gray-700">{monthName}</div>
                            <div className="mt-1 flex items-baseline gap-1">
                              <span className="text-lg font-bold text-gray-900">{month.count}</span>
                              <span className="text-xs text-gray-400">calls</span>
                            </div>
                            <div className="text-xs text-orange-600 font-medium">~{(() => { const c = month.total_credits||0; return c < 0.01 ? c.toFixed(5) : c < 0.1 ? c.toFixed(4) : c < 1 ? c.toFixed(3) : c.toFixed(2); })()} cr</div>
                            <div className="text-xs text-emerald-700 font-medium">~{gbpDisplay}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Usage by Feature */}
                {aiUsageStats.usage_by_feature.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Usage by Feature</h4>
                    <div className="space-y-2">
                      {aiUsageStats.usage_by_feature.map((feature, idx) => {
                        const pence = (feature.total_gbp||0)*100;
                        const gbpDisplay = pence === 0 ? '0.000p' : pence < 5 ? `${pence.toFixed(3)}p` : pence < 100 ? `${pence.toFixed(2)}p` : `£${(feature.total_gbp||0).toFixed(4)}`;
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0"></div>
                              <div>
                                <span className="font-medium text-sm">{feature._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                <div className="text-xs text-gray-400 mt-0.5">
                                  {feature.location && <span>{feature.location}</span>}
                                  {feature.model && <span className="ml-2 font-mono text-blue-500">{feature.model.replace(/^(openai|anthropic|gemini)\//i, '')}</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4 text-right">
                              <span className="text-sm text-gray-500">{feature.count} calls</span>
                              <span className="text-sm font-medium text-orange-600">~{(() => { const c = feature.total_credits||0; return c < 0.01 ? c.toFixed(5) : c < 0.1 ? c.toFixed(4) : c < 1 ? c.toFixed(3) : c.toFixed(2); })()} cr</span>
                              <span className="text-sm font-medium text-emerald-700">~{gbpDisplay}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Usage by User */}
                {aiUsageStats.usage_by_user.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Usage by User</h4>
                    <div className="space-y-2">
                      {aiUsageStats.usage_by_user.map((userStat, idx) => {
                        const pence = (userStat.total_gbp||0)*100;
                        const gbpDisplay = pence === 0 ? '0.000p' : pence < 1 ? `${pence.toFixed(3)}p` : pence < 100 ? `${pence.toFixed(2)}p` : `£${(userStat.total_gbp||0).toFixed(4)}`;
                        return (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-400" />
                              <span className="font-medium">{userStat._id}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-gray-500">{userStat.count} calls</span>
                              <span className="text-sm font-medium text-orange-600">~{(userStat.total_credits||0).toFixed(1)} cr</span>
                              <span className="text-sm font-medium text-emerald-700">~{gbpDisplay}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent Activity Table */}
                <div>
                  <h4 className="font-semibold mb-3">Recent Activity ({aiUsageLogs.length} logs)</h4>
                  {aiUsageLogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No AI usage logs for this period. Use AI features or select a different period.</p>
                      <Button variant="outline" size="sm" onClick={() => fetchAiUsageLogs(aiLogPeriod)} className="mt-4">Load Logs</Button>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[400px]">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Timestamp</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Feature</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Location</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Model</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Credits</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Cost (£)</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {aiUsageLogs.map((log, idx) => {
                              const gbp = log.estimated_gbp || 0;
                              const pence = gbp * 100;
                              const gbpStr = pence === 0 ? '0.000p' : pence < 1 ? `${pence.toFixed(3)}p` : pence < 100 ? `${pence.toFixed(2)}p` : `£${gbp.toFixed(4)}`;
                              return (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-4 py-2 text-gray-500 whitespace-nowrap text-xs">{new Date(log.timestamp || log.created_at).toLocaleString('en-GB')}</td>
                                  <td className="px-4 py-2 font-medium text-xs">{log.feature_name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                                  <td className="px-4 py-2 text-gray-500 text-xs">{log.location || '-'}</td>
                                  <td className="px-4 py-2 text-xs">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-mono text-xs">
                                      {(log.model || log.details?.model || '-').replace(/^(openai|anthropic|gemini)\//i, '')}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-gray-600 text-xs">{log.user_email?.split('@')[0]}</td>
                                  <td className="px-4 py-2 text-orange-600 font-medium text-xs">~{(log.estimated_credits || 0).toFixed(1)}</td>
                                  <td className="px-4 py-2 text-emerald-700 font-medium text-xs">~{gbpStr}</td>
                                  <td className="px-4 py-2">
                                    {log.success ? (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700"><Check className="w-3 h-3 mr-1" />OK</span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Failed</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Non-AI Usage Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5" style={{ color: '#10b981' }} />
                    Non-AI Feature Usage (No Credits)
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchNonAiUsageLogs}
                    disabled={loadingNonAiLogs}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingNonAiLogs ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </CardTitle>
                <CardDescription>
                  Track usage of features that don't consume AI credits (basic PDF extraction, exports, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Non-AI Stats Summary */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-600 font-medium">Total Calls</div>
                    <div className="text-2xl font-bold text-green-700">{nonAiUsageStats.total_logs}</div>
                  </div>
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <div className="text-sm text-teal-600 font-medium">Features Used</div>
                    <div className="text-2xl font-bold text-teal-700">{nonAiUsageStats.usage_by_feature.length}</div>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <div className="text-sm text-emerald-600 font-medium">Credits Used</div>
                    <div className="text-2xl font-bold text-emerald-700">0 (Free)</div>
                  </div>
                </div>
                
                {/* Available Non-AI Features */}
                <div className="border rounded-lg p-4 bg-gradient-to-r from-green-50 to-teal-50">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-green-600" />
                    Available Non-AI Features (Free)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(nonAiUsageStats.available_non_ai_features || {}).map(([key, feature]) => {
                      const usageData = nonAiUsageStats.usage_by_feature.find(f => f._id === key);
                      const usageCount = usageData?.count || 0;
                      
                      return (
                        <div key={key} className="p-3 bg-white rounded-lg border border-green-100 shadow-sm">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-medium text-gray-800">{feature.name}</div>
                              <div className="text-xs text-gray-500 mt-1">{feature.description}</div>
                            </div>
                            <div className="text-right ml-3">
                              <div className="text-sm font-bold text-green-600">Free</div>
                              {usageCount > 0 ? (
                                <div className="text-xs text-green-600 font-medium mt-1">
                                  {usageCount} uses
                                </div>
                              ) : (
                                <div className="text-xs text-gray-400 mt-1">Not used yet</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {Object.keys(nonAiUsageStats.available_non_ai_features || {}).length === 0 && (
                      <div className="col-span-2 text-center py-4 text-gray-500">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">Click "Refresh" to load available non-AI features</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Non-AI Usage by Feature */}
                {nonAiUsageStats.usage_by_feature.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Usage by Feature</h4>
                    <div className="space-y-2">
                      {nonAiUsageStats.usage_by_feature.map((feature, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-green-500"></div>
                            <span className="font-medium">{feature._id?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-500">{feature.count} calls</span>
                            <span className="text-sm font-medium text-green-600">Free</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Recent Non-AI Activity */}
                {nonAiUsageLogs.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3">Recent Activity ({nonAiUsageLogs.length} logs)</h4>
                    <div className="border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[300px]">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Timestamp</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Feature</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">User</th>
                              <th className="px-4 py-2 text-left font-medium text-gray-600">Details</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {nonAiUsageLogs.slice(0, 20).map((log, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                                  {new Date(log.timestamp).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 font-medium">
                                  {log.feature_name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                </td>
                                <td className="px-4 py-2 text-gray-600">
                                  {log.user_email?.split('@')[0]}
                                </td>
                                <td className="px-4 py-2 text-gray-500 text-xs max-w-[200px] truncate">
                                  {log.details?.filename || log.details?.items_extracted ? `${log.details.items_extracted} items` : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                
                {nonAiUsageLogs.length === 0 && nonAiUsageStats.total_logs === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No non-AI feature usage logged yet.</p>
                    <p className="text-sm mt-1">Use features like Quote PDF extraction to see activity here.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Storage Monitor Dropdown */}
            <Card>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowStorageMonitor(!showStorageMonitor)}
              >
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <HardDrive className="w-5 h-5" style={{ color: '#ee7203' }} />
                    Storage Monitor
                  </div>
                  <ChevronDown className={`w-5 h-5 transition-transform ${showStorageMonitor ? 'rotate-180' : ''}`} />
                </CardTitle>
                <CardDescription>Monitor database storage and file usage</CardDescription>
              </CardHeader>
              {showStorageMonitor && (
                <CardContent>
                  <StorageMonitor />
                </CardContent>
              )}
            </Card>
          </TabsContent>
          )}

          {/* GDPR Protocol - Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="gdpr-protocol" className="space-y-4">
            <Card className="border-red-300 border-2">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-red-700 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  GDPR Protocol - Database Cleanup Actions
                </CardTitle>
                <CardDescription className="text-red-600">
                  These actions permanently delete data from the deployed database. Use with caution.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                {/* Remove Customer Management Database */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-semibold text-gray-900 mb-2">Remove Customer Management Database</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently deletes all customer records from the database. This action cannot be undone.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to delete ALL customer records? This action CANNOT be undone.')) return;
                      if (window.prompt('Type "DELETE CUSTOMERS" to confirm:') !== 'DELETE CUSTOMERS') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/customers`);
                        toast.success(response.data.message || 'Customer database cleared');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to delete customers');
                      }
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete All Customers
                  </Button>
                </div>

                {/* Remove Pricing Data */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-semibold text-gray-900 mb-2">Remove All Pricing Data</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Removes all pricing information from products across the system.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to remove ALL pricing data? This action CANNOT be undone.')) return;
                      if (window.prompt('Type "DELETE PRICES" to confirm:') !== 'DELETE PRICES') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/pricing`);
                        toast.success(response.data.message || 'Pricing data cleared');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to delete pricing');
                      }
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove All Pricing
                  </Button>
                </div>

                {/* Erase Quotes and Pro Formas */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-semibold text-gray-900 mb-2">Erase Quotes & Pro Formas</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Permanently deletes all stored quotes and pro forma documents.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to delete ALL quotes and pro formas? This action CANNOT be undone.')) return;
                      if (window.prompt('Type "DELETE DOCUMENTS" to confirm:') !== 'DELETE DOCUMENTS') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/quotes-proformas`);
                        toast.success(response.data.message || 'Quotes and Pro Formas cleared');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to delete documents');
                      }
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete Quotes & Pro Formas
                  </Button>
                </div>

                {/* Reset Sales Tracker Figures */}
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <h4 className="font-semibold text-gray-900 mb-2">Reset Sales Tracker Figures</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Clears all previous figures from the Sales Tracker page.
                  </p>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      if (!window.confirm('Are you sure you want to reset ALL Sales Tracker figures? This action CANNOT be undone.')) return;
                      if (window.prompt('Type "RESET SALES" to confirm:') !== 'RESET SALES') {
                        toast.error('Confirmation text did not match');
                        return;
                      }
                      try {
                        const response = await api.delete(`${API}/gdpr/sales-tracker`);
                        toast.success(response.data.message || 'Sales Tracker figures cleared');
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to reset sales tracker');
                      }
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Reset Sales Tracker
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          )}

          {/* Email & MFA Settings Tab */}
          {user?.role === 'super_admin' && (
          <TabsContent value="email-mfa" className="space-y-4">
            <EmailMfaSettings />
          </TabsContent>
          )}

          {/* MFA Register Tab */}
          {(user?.role === 'super_admin' || user?.can_view_mfa_overview) && (
          <TabsContent value="mfa" className="space-y-4">
            {/* The MFA settings box (it used to be in User Admin). Only a super admin can change MFA settings; people with MFA Overview Access see the register below it. */}
            {user?.role === 'super_admin' && <PermissionsManager view="mfa" onImpersonate={onImpersonate} />}
            <MfaAdminPage />
          </TabsContent>
          )}

          {/* Notifications Settings Tab */}
          {user?.role === 'super_admin' && (
          <TabsContent value="notifications" className="space-y-4">
            <NotificationsManager />
          </TabsContent>
          )}

          {/* Notification Bar Tab — super_admin or can_manage_global_notifications */}
          {(user?.role === 'super_admin' || user?.can_manage_global_notifications) && (
          <TabsContent value="notification-bar" className="space-y-4">
            <NotificationBarSettings user={user} />
          </TabsContent>
          )}

          {/* Automations — All users */}
          <TabsContent value="automations" className="space-y-4">
            <AutomationsPanel user={user} />
          </TabsContent>

          {/* Cloudflare Incoming — Super Admin Only */}
          {user?.role === 'super_admin' && (
          <TabsContent value="cloudflare-incoming" className="space-y-4">
            <CloudflareIncomingSettings user={user} />
          </TabsContent>
          )}

        </Tabs>
      </div>
      
      {/* Change Password Modal */}
      <Dialog open={showChangePasswordModal} onOpenChange={setShowChangePasswordModal}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Update your account password. You'll need to enter your current password to confirm the change.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">Current Password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm-new-password">Confirm New Password</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowChangePasswordModal(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={!currentPassword || !newPassword || !confirmNewPassword}
              style={{ backgroundColor: '#ee7203', color: 'white' }}
            >
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Effective Date Dialog */}
      <Dialog open={showDateDialog} onOpenChange={setShowDateDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Apply Cost Changes From Date</DialogTitle>
            <DialogDescription>
              Choose the date from which these cost changes should take effect.
              Historical data before this date will remain unchanged.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="effective-date">Effective Date</Label>
              <Input
                id="effective-date"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="w-full"
              />
              <p className="text-sm text-gray-500">
                All dates on or after {effectiveDate} will use the new costs
              </p>
            </div>
            
            {selectedVehicleIndex !== null && settings?.default_vehicles?.[selectedVehicleIndex] && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold mb-2">
                  {settings.default_vehicles[selectedVehicleIndex].vehicle_registration}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Contract Hire: <span className="font-semibold">£{settings.default_vehicles[selectedVehicleIndex].contract_hire || 0}</span></div>
                  <div>Maintenance: <span className="font-semibold">£{settings.default_vehicles[selectedVehicleIndex].maintenance_insurance || 0}</span></div>
                  <div>Moffet: <span className="font-semibold">£{settings.default_vehicles[selectedVehicleIndex].moffet || 0}</span></div>
                  <div>Cost/Mile: <span className="font-semibold">£{settings.default_vehicles[selectedVehicleIndex].cost_per_mile || 0}</span></div>
                  <div className="col-span-2">Driver: <span className="font-semibold">£{settings.default_vehicles[selectedVehicleIndex].driver_cost || 0}/year</span></div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDateDialog(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveVehicleCostsWithDate}
              disabled={saving}
              style={{ backgroundColor: '#ee7203', color: 'white' }}
            >
              {saving ? 'Saving...' : 'Save & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Vehicle Details Date Dialog */}
      <Dialog open={showVehicleDetailsDialog} onOpenChange={setShowVehicleDetailsDialog}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Apply Vehicle Details From Date</DialogTitle>
            <DialogDescription>
              Choose the date from which these vehicle detail changes should take effect.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle-details-date">Effective Date</Label>
              <Input
                id="vehicle-details-date"
                type="date"
                value={vehicleDetailsDate}
                onChange={(e) => setVehicleDetailsDate(e.target.value)}
                className="w-full"
              />
              <p className="text-sm text-gray-500">
                All dates on or after {vehicleDetailsDate} will use the new vehicle details
              </p>
            </div>
            
            {selectedVehicleIndex !== null && settings?.default_vehicles?.[selectedVehicleIndex] && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <h4 className="font-semibold mb-2">
                  {settings.default_vehicles[selectedVehicleIndex].vehicle_registration}
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Driver: <span className="font-semibold">{settings.default_vehicles[selectedVehicleIndex].driver_name}</span></div>
                  <div>Phone: <span className="font-semibold">{settings.default_vehicles[selectedVehicleIndex].driver_phone}</span></div>
                  <div>Type: <span className="font-semibold">{settings.default_vehicles[selectedVehicleIndex].vehicle_type}</span></div>
                  <div>Max Weight: <span className="font-semibold">{settings.default_vehicles[selectedVehicleIndex].max_weight} kg</span></div>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowVehicleDetailsDialog(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveVehicleDetails}
              disabled={saving}
              style={{ backgroundColor: '#ee7203', color: 'white' }}
            >
              {saving ? 'Saving...' : 'Save & Apply'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copyright & Terms Links Footer */}
      <div className="text-center py-6 mt-8">
        <a 
          href="/terms-conditions.html" 
          target="_blank"
          className="text-sm text-gray-500 underline hover:text-gray-700"
        >
          Terms & Conditions
        </a>
      </div>
    </div>

    {/* M4 Images Modal */}
    <Dialog open={m4ImagesModalOpen} onOpenChange={setM4ImagesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>M4 Upload Images</DialogTitle>
            <p className="text-sm text-gray-500">
              All M4 images uploaded to the sales tracker. Click to download.
            </p>
          </DialogHeader>
          
          <div className="space-y-2">
            {m4Images.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No M4 images found</p>
            ) : (
              <div className="grid gap-3">
                {m4Images.map((image, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100">
                    <div>
                      <p className="font-medium text-sm">{image.title}</p>
                      <p className="text-xs text-gray-500">Data Date: {image.data_date_formatted} | Upload: {image.upload_date_formatted}</p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const response = await api.get(`${API}/m4-images/download/${image.photo_id}`);
                          // Create download link for base64 image
                          const link = document.createElement('a');
                          link.href = response.data.photo;
                          link.download = `${image.title}.jpg`;
                          link.click();
                          toast.success('Image downloaded');
                        } catch (error) {
                          toast.error('Failed to download: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Daily Backups Modal */}
      <Dialog open={dailyBackupsModalOpen} onOpenChange={setDailyBackupsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Daily Sales Tracker Backups</DialogTitle>
            <p className="text-sm text-gray-500">
              Automated backups created daily at 11:00 PM UK time. Download any backup to restore or transfer data.
            </p>
          </DialogHeader>
          
          <div className="space-y-2">
            {dailyBackups.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No daily backups available yet</p>
            ) : (
              <div className="grid gap-3">
                {dailyBackups.map((backup, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border hover:bg-gray-100">
                    <div>
                      <p className="font-medium text-sm">{backup.filename}</p>
                      <p className="text-xs text-gray-500">
                        Created: {new Date(backup.created_at).toLocaleString()} | 
                        Size: {(backup.size / 1024).toFixed(0)} KB
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          const response = await api.get(
                            `${API}/backups/sales-tracker/daily/download/${backup.filename}`,
                            { responseType: 'blob' }
                          );
                          
                          // Create download link
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = backup.filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          
                          toast.success('Backup downloaded');
                        } catch (error) {
                          toast.error('Failed to download: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Master Stock Modal */}
      <Dialog open={showUploadMasterModal} onOpenChange={setShowUploadMasterModal}>
        <DialogContent 
          className="max-w-2xl"
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #ee7203, #f97316) 1'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Upload Master Stock
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload your PI COUNT MASTER.csv file containing all products. The system will automatically create product groups based on prefix codes.
            </p>
            
            {uploading ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Uploading...</span>
                  <span className="text-sm text-gray-500">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${uploadProgress}%`,
                      background: 'linear-gradient(90deg, #ee7203, #f97316)'
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleMasterUpload}
                  className="hidden"
                  id="master-upload"
                />
                <label
                  htmlFor="master-upload"
                  className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
                  style={{ borderColor: enconOrange }}
                >
                  <Upload className="w-12 h-12 mb-3" style={{ color: enconOrange }} />
                  <p className="text-sm font-medium" style={{ color: enconOrange }}>
                    Click to upload CSV file
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Expected format: Prefix Code, Code, Analysis Group Code, Description
                  </p>
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inventory Search Modal */}
      <Dialog open={showInventoryModal} onOpenChange={setShowInventoryModal}>
        <DialogContent 
          className="max-w-4xl max-h-[80vh]"
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #ee7203, #f97316) 1'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Search Inventory
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search by code, name, or prefix..."
                value={inventorySearchQuery}
                onChange={(e) => setInventorySearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchInventory()}
                className="flex-1"
              />
              <Button
                onClick={searchInventory}
                style={{ backgroundColor: enconOrange, color: 'white' }}
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              {inventoryResults.length > 0 ? (
                <div className="space-y-2">
                  {inventoryResults.map((product) => (
                    <div
                      key={product.id}
                      className="p-3 border-2 rounded-lg hover:shadow-md transition-shadow"
                      style={{ borderColor: enconLightGrey }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold" style={{ color: enconOrange }}>
                            {product.code}
                          </p>
                          <p className="text-sm text-gray-700 mt-1">{product.name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Prefix: {product.prefix}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>Enter search terms to find products</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier Group Creation Modal */}
      <Dialog open={showSupplierModal} onOpenChange={(open) => {
        setShowSupplierModal(open);
        if (!open) {
          setSupplierModalGroupSearch('');
          setShowAllGroups(false);
        }
      }}>
        <DialogContent 
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #ee7203, #f97316) 1'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              {editingSupplier ? 'Edit Product Analysis Group' : 'Create Product Analysis Group'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Category</Label>
              <div className="flex gap-3">
                <button
                  onClick={() => setNewSupplier({ ...newSupplier, category: 'structural' })}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newSupplier.category === 'structural'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <p className="font-semibold text-blue-600">Structural</p>
                </button>
                <button
                  onClick={() => setNewSupplier({ ...newSupplier, category: 'industrial' })}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newSupplier.category === 'industrial'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <p className="font-semibold text-green-600">Industrial</p>
                </button>
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Select Product Analysis Groups</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-all-groups"
                    checked={showAllGroups}
                    onCheckedChange={setShowAllGroups}
                  />
                  <Label htmlFor="show-all-groups" className="text-xs cursor-pointer">
                    Show all groups
                  </Label>
                </div>
              </div>
              <Input
                placeholder="Search product analysis groups..."
                value={supplierModalGroupSearch}
                onChange={(e) => setSupplierModalGroupSearch(e.target.value)}
                className="mb-2"
              />
              <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-3 border-2 rounded-lg">
                {productGroups
                  .filter(group => {
                    // First apply the show all groups filter
                    if (!showAllGroups) {
                      if (group.assigned_supplier && (!editingSupplier || group.assigned_supplier.supplier_id !== editingSupplier.id)) {
                        return false;
                      }
                    }
                    // Then apply search filter
                    if (supplierModalGroupSearch) {
                      const searchLower = supplierModalGroupSearch.toLowerCase();
                      return group.prefix.toLowerCase().includes(searchLower) || 
                             group.name.toLowerCase().includes(searchLower);
                    }
                    return true;
                  })
                  .slice(0, 100) // Limit displayed items for performance
                  .map((group) => {
                    const isAssigned = group.assigned_supplier && (!editingSupplier || group.assigned_supplier.supplier_id !== editingSupplier.id);
                    const isSelected = newSupplier.product_group_ids.includes(group.id);
                    
                    return (
                      <div
                        key={group.id}
                        onClick={() => {
                          if (isAssigned && showAllGroups) {
                            // Allow moving from one supplier to another
                            toggleProductGroup(group.id);
                          } else if (!isAssigned) {
                            toggleProductGroup(group.id);
                          }
                        }}
                        className={`p-2 border-2 rounded transition-all ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50'
                            : isAssigned && showAllGroups
                            ? 'border-gray-400 bg-gray-100 cursor-pointer opacity-60 hover:opacity-80'
                            : 'border-gray-300 hover:border-orange-300 cursor-pointer'
                        }`}
                      >
                        <p className="text-sm font-semibold">{group.prefix}</p>
                        <p className="text-xs text-gray-600">{group.name}</p>
                        {isAssigned && showAllGroups && (
                          <p className="text-xs text-gray-500 mt-1">
                            Currently in: {group.assigned_supplier.supplier_name}
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Supplier Name</Label>
                <Button
                  onClick={autoGenerateSupplierName}
                  size="sm"
                  variant="outline"
                  style={{ borderColor: enconOrange, color: enconOrange }}
                >
                  Auto Generate
                </Button>
              </div>
              <Input
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
                placeholder="e.g., RW PROROCKS"
              />
            </div>
            
            <Button
              onClick={createSupplierGroup}
              className="w-full"
              style={{ backgroundColor: enconOrange, color: 'white' }}
            >
              {editingSupplier ? 'Update Analysis Group' : 'Create Analysis Group'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ungrouped Products Modal */}
      <Dialog open={showUngroupedModal} onOpenChange={setShowUngroupedModal}>
        <DialogContent 
          className="max-w-4xl max-h-[80vh]"
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #ee7203, #f97316) 1'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Ungrouped Products
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              These products don't have a group assignment. Select a group for each product.
            </p>
            
            <div className="max-h-96 overflow-y-auto space-y-2">
              {ungroupedProducts.map((product) => (
                <div
                  key={product.id}
                  className="p-3 border-2 rounded-lg"
                  style={{ borderColor: enconLightGrey }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold">{product.code}</p>
                      <p className="text-sm text-gray-600">{product.name}</p>
                      <p className="text-xs text-gray-500 mt-1">Prefix: {product.prefix}</p>
                    </div>
                    <Select
                      value={product.selected_group || ''}
                      onValueChange={(value) => assignGroup(product.id, value)}
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        {productGroups
                          .filter(g => g.prefix === product.prefix)
                          .map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Market Sectors Modal */}
      <Dialog open={showMarketSectorsModal} onOpenChange={(open) => {
        setShowMarketSectorsModal(open);
        if (!open) {
          setSelectedSupplierIds([]);
          setNewMarketSector('structural');
        }
      }}>
        <DialogContent
          className="max-w-4xl max-h-[80vh] overflow-y-auto rounded-2xl"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Edit Market Sectors
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select supplier groups to change their market sector (Structural or Industrial)
            </p>
            
            {/* Selected count and category selector */}
            <div className="p-4 bg-gray-50 rounded-lg border-2" style={{ borderColor: enconLightGrey }}>
              <div className="flex flex-col gap-3">
                <p className="font-semibold">
                  {selectedSupplierIds.length} supplier group{selectedSupplierIds.length !== 1 ? 's' : ''} selected
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewMarketSector('structural')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                      newMarketSector === 'structural'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Structural
                  </button>
                  <button
                    onClick={() => setNewMarketSector('industrial')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                      newMarketSector === 'industrial'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Industrial
                  </button>
                  <button
                    onClick={() => setNewMarketSector('admin')}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold transition-all ${
                      newMarketSector === 'admin'
                        ? 'text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                    style={newMarketSector === 'admin' ? { backgroundColor: enconOrange } : {}}
                  >
                    Admin
                  </button>
                </div>
              </div>
            </div>
            
            {/* Supplier groups list */}
            <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto p-3 border-2 rounded-lg">
              {supplierGroups.map((supplier) => {
                const isSelected = selectedSupplierIds.includes(supplier.id);
                
                return (
                  <div
                    key={supplier.id}
                    onClick={() => toggleSupplierSelection(supplier.id)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{supplier.name}</p>
                        <p className="text-xs mt-1">
                          <span className={`px-2 py-1 rounded ${
                            supplier.category === 'structural' 
                              ? 'bg-blue-100 text-blue-800' 
                              : supplier.category === 'industrial'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {supplier.category}
                          </span>
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {supplier.product_group_ids?.length || 0} groups
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Action buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={() => setShowMarketSectorsModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={updateMarketSectors}
                disabled={selectedSupplierIds.length === 0}
                className="flex-1"
                style={{ backgroundColor: enconOrange, color: 'white' }}
              >
                Change Market Sector
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Free Stock Reports Modal */}
      <Dialog open={showValuationsModal} onOpenChange={setShowValuationsModal}>
        <DialogContent
          className="max-w-3xl max-h-[80vh] rounded-2xl overflow-y-auto"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Free Stock Reports (GFST0000)
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3">
            {stockValuations.length > 0 ? (
              stockValuations.map((valuation) => (
                <div
                  key={valuation.id}
                  className="p-4 border-2 rounded-lg hover:bg-orange-50 transition-colors"
                  style={{ borderColor: enconLightGrey }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-semibold text-lg">
                        {valuation.upload_date ? format(new Date(valuation.upload_date), 'EEEE, dd MMMM yyyy') : 'Unknown date'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {valuation.products_count || 0} products • Uploaded {valuation.uploaded_at ? format(new Date(valuation.uploaded_at), 'HH:mm') : 'Unknown time'}
                      </p>
                      {valuation.filename && (
                        <p className="text-xs text-gray-500 mt-1">
                          File: {valuation.filename}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                        Available
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-semibold mb-2">No Free Stock Reports uploaded</p>
                <p className="text-sm">Upload GFST0000.csv files from the PI Counts page to get started.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW Supplier Groups Modal - Groups Product Analysis Groups */}
      <Dialog open={showNewSupplierGroupModal} onOpenChange={(open) => {
        setShowNewSupplierGroupModal(open);
        if (!open) {
          setEditingManualSupplierGroup(null);
          setNewManualSupplierGroup({ name: '', category: 'structural', product_analysis_group_ids: [], crm_supplier_name: '' });
          setAnalysisGroupSearchInModal('');
          setShowAssignedAnalysisGroups(false); // Reset toggle
        }
      }}>
        <DialogContent
          className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              {editingManualSupplierGroup ? 'Edit Supplier Group' : 'Create Supplier Group'}
            </DialogTitle>
          </DialogHeader>
          
          {/* Selected Analysis Groups Summary */}
          {newManualSupplierGroup.product_analysis_group_ids.length > 0 && (
            <div className="p-3 bg-orange-50 border-2 border-orange-200 rounded-lg">
              <p className="text-sm font-semibold text-orange-700 mb-2">
                Selected ({newManualSupplierGroup.product_analysis_group_ids.length}):
              </p>
              <div className="flex flex-wrap gap-1">
                {newManualSupplierGroup.product_analysis_group_ids.slice(0, 8).map(id => {
                  const ag = supplierGroups.find(g => g.id === id);
                  return ag ? (
                    <span key={id} className="px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                      {ag.name.length > 20 ? ag.name.substring(0, 20) + '...' : ag.name}
                    </span>
                  ) : null;
                })}
                {newManualSupplierGroup.product_analysis_group_ids.length > 8 && (
                  <span className="px-2 py-0.5 bg-orange-200 text-orange-800 text-xs rounded font-semibold">
                    +{newManualSupplierGroup.product_analysis_group_ids.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            {/* Category Selection */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Market Sector</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setNewManualSupplierGroup(prev => ({ ...prev, category: 'structural' }))}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newManualSupplierGroup.category === 'structural'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <p className="font-semibold text-blue-600">Structural</p>
                </button>
                <button
                  type="button"
                  onClick={() => setNewManualSupplierGroup(prev => ({ ...prev, category: 'industrial' }))}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newManualSupplierGroup.category === 'industrial'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <p className="font-semibold text-green-600">Industrial</p>
                </button>
              </div>
            </div>

            {/* Select Product Analysis Groups */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">
                  Select Product Analysis Groups ({newManualSupplierGroup.product_analysis_group_ids.length} selected)
                </Label>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show-assigned-groups"
                    checked={showAssignedAnalysisGroups}
                    onCheckedChange={setShowAssignedAnalysisGroups}
                  />
                  <Label htmlFor="show-assigned-groups" className="text-xs cursor-pointer">
                    Show assigned
                  </Label>
                </div>
              </div>
              <Input
                placeholder="Search by name, category, or product prefix (e.g. 02A, 02B)..."
                value={analysisGroupSearchInModal}
                onChange={(e) => setAnalysisGroupSearchInModal(e.target.value)}
                className="mb-2"
              />
              <div className="max-h-72 overflow-y-auto border-2 rounded-lg p-2">
                {supplierGroups
                  .filter(ag => {
                    // First check if this analysis group is already assigned to another supplier group
                    const isAssignedToOther = manualSupplierGroups.some(sg => 
                      sg.id !== editingManualSupplierGroup?.id && 
                      sg.product_analysis_group_ids?.includes(ag.id)
                    );
                    
                    // If not showing assigned and it's assigned to another, hide it
                    // BUT always show if it's currently selected in this form
                    const isCurrentlySelected = newManualSupplierGroup.product_analysis_group_ids.includes(ag.id);
                    if (!showAssignedAnalysisGroups && isAssignedToOther && !isCurrentlySelected) {
                      return false;
                    }
                    
                    // Then apply search filter - include prefix search (use debounced value)
                    if (debouncedAnalysisSearch) {
                      const searchLower = debouncedAnalysisSearch.toLowerCase();
                      // Search by name or category
                      const nameMatch = ag.name.toLowerCase().includes(searchLower);
                      const categoryMatch = ag.category.toLowerCase().includes(searchLower);
                      // Search by product group prefixes within this analysis group
                      const prefixMatch = ag.product_group_ids?.some(pgId => {
                        const pg = productGroups.find(g => g.id === pgId);
                        return pg?.prefix?.toLowerCase().includes(searchLower);
                      });
                      return nameMatch || categoryMatch || prefixMatch;
                    }
                    return true;
                  })
                  .slice(0, 200) // Increased limit for better visibility
                  .map((analysisGroup) => {
                    const isSelected = newManualSupplierGroup.product_analysis_group_ids.includes(analysisGroup.id);
                    const isAssignedToOther = manualSupplierGroups.some(sg => 
                      sg.id !== editingManualSupplierGroup?.id && 
                      sg.product_analysis_group_ids?.includes(analysisGroup.id)
                    );
                    const assignedToGroup = isAssignedToOther ? manualSupplierGroups.find(sg => 
                      sg.id !== editingManualSupplierGroup?.id && 
                      sg.product_analysis_group_ids?.includes(analysisGroup.id)
                    ) : null;
                    
                    // Get actual prefixes from product groups
                    const prefixes = analysisGroup.product_group_ids?.map(pgId => {
                      const pg = productGroups.find(g => g.id === pgId);
                      return pg?.prefix;
                    }).filter(Boolean).slice(0, 5) || [];
                    
                    return (
                      <div
                        key={analysisGroup.id}
                        onClick={() => {
                          setNewManualSupplierGroup(prev => ({
                            ...prev,
                            product_analysis_group_ids: isSelected
                              ? prev.product_analysis_group_ids.filter(id => id !== analysisGroup.id)
                              : [...prev.product_analysis_group_ids, analysisGroup.id]
                          }));
                        }}
                        className={`p-2 mb-1 border-2 rounded cursor-pointer transition-all ${
                          isSelected
                            ? 'border-orange-500 bg-orange-50'
                            : isAssignedToOther
                            ? 'border-gray-300 bg-gray-50 opacity-60'
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{analysisGroup.name}</p>
                            <div className="text-xs text-gray-500 flex flex-wrap items-center gap-1">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                analysisGroup.category === 'structural' ? 'bg-blue-100 text-blue-700' :
                                analysisGroup.category === 'industrial' ? 'bg-green-100 text-green-700' :
                                'bg-orange-100 text-orange-700'
                              }`}>
                                {analysisGroup.category}
                              </span>
                              {/* Show actual prefixes */}
                              {prefixes.length > 0 && (
                                <span className="ml-1 text-gray-600">
                                  Prefixes: {prefixes.join(', ')}{analysisGroup.product_group_ids?.length > 5 ? `, +${analysisGroup.product_group_ids.length - 5} more` : ''}
                                </span>
                              )}
                              {isAssignedToOther && assignedToGroup && (
                                <span className="ml-2 text-orange-600 font-medium">
                                  • In: {assignedToGroup.name}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Auto-generate Name */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium">Supplier Group Name</Label>
                <Button
                  type="button"
                  onClick={autoGenerateManualSupplierName}
                  size="sm"
                  variant="outline"
                  style={{ borderColor: enconOrange, color: enconOrange }}
                >
                  Auto Generate
                </Button>
              </div>
              <Input
                value={newManualSupplierGroup.name}
                onChange={(e) => setNewManualSupplierGroup(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., RW Insulation Products"
              />
            </div>

            {/* CRM Supplier Name — overrides what we send to Zoho when this
                supplier is auto-suggested on a quote. Defaults to the
                Supplier Group Name above; the system also learns this from
                rep picks in the Add to CRM modal. */}
            <div className="border-2 border-blue-200 rounded-lg p-3 bg-blue-50/40">
              <Label className="text-sm font-medium flex items-center gap-2">
                CRM Supplier Name
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">Used by Add-to-CRM</span>
              </Label>
              <Input
                value={newManualSupplierGroup.crm_supplier_name}
                onChange={(e) => setNewManualSupplierGroup(prev => ({ ...prev, crm_supplier_name: e.target.value }))}
                placeholder={newManualSupplierGroup.name || 'e.g., KNAUF UK'}
                className="mt-1"
                data-testid="crm-supplier-name-input"
              />
              <p className="text-[11px] text-gray-600 mt-1">
                The exact supplier name as it appears in Zoho CRM. Leave blank to fall back to <span className="font-mono">{newManualSupplierGroup.name || 'Supplier Group Name'}</span>. The system automatically updates this whenever a rep picks a different Zoho supplier on a quote.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                type="button"
                onClick={saveManualSupplierGroup}
                className="flex-1"
                style={{ backgroundColor: enconOrange, color: 'white' }}
              >
                {editingManualSupplierGroup ? 'Update Supplier Group' : 'Create Supplier Group'}
              </Button>
              {editingManualSupplierGroup && (
                <Button
                  type="button"
                  onClick={deleteManualSupplierGroup}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Additional Product Data Edit Modal */}
      <Dialog open={showAdditionalProductModal} onOpenChange={setShowAdditionalProductModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: enconOrange }}>
              Edit Product Data
            </DialogTitle>
            <DialogDescription>
              {selectedAdditionalProduct?.prod_code}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAdditionalProduct && (
            <div className="space-y-4">
              {/* Product Description */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <label className="text-xs font-semibold text-gray-500 uppercase">Description</label>
                <p className="text-sm font-medium">{selectedAdditionalProduct.prod_desc || 'No description'}</p>
              </div>
              
              {/* Current Keywords */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <label className="text-xs font-semibold text-blue-700 uppercase">Uploaded Keywords</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {[selectedAdditionalProduct.keyword1, selectedAdditionalProduct.keyword2, 
                    selectedAdditionalProduct.keyword3, selectedAdditionalProduct.keyword4,
                    selectedAdditionalProduct.keyword5, selectedAdditionalProduct.keyword6]
                    .filter(k => k)
                    .map((keyword, i) => (
                      <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">
                        {keyword}
                      </span>
                    ))}
                  {![selectedAdditionalProduct.keyword1, selectedAdditionalProduct.keyword2, 
                    selectedAdditionalProduct.keyword3, selectedAdditionalProduct.keyword4,
                    selectedAdditionalProduct.keyword5, selectedAdditionalProduct.keyword6]
                    .some(k => k) && (
                      <span className="text-xs text-gray-500 italic">No keywords</span>
                    )}
                </div>
              </div>
              
              {/* Cost Factor Edit */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Cost Factor (M2 coverage per stock unit)
                </label>
                <Input
                  type="number"
                  step="0.001"
                  value={additionalProductEditCostFactor}
                  onChange={(e) => setAdditionalProductEditCostFactor(e.target.value)}
                  placeholder="e.g. 2.88"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used to calculate quantity/price when this product is quoted per M2
                </p>
              </div>

              {/* Weight Edit */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Product Weight (kg)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={additionalProductEditWeight}
                  onChange={(e) => setAdditionalProductEditWeight(e.target.value)}
                  placeholder="Enter weight in kg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Type "OVERRIDE" below to change weight
                </p>
              </div>
              
              {/* Safety Word for Weight */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Safety Word (to update weight)
                </label>
                <Input
                  value={additionalProductSafetyWord}
                  onChange={(e) => setAdditionalProductSafetyWord(e.target.value)}
                  placeholder="Type OVERRIDE to confirm weight change"
                />
              </div>
              
              {/* Additional Keywords */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">
                  Additional Keywords
                </label>
                <Input
                  value={additionalProductEditKeywords}
                  onChange={(e) => setAdditionalProductEditKeywords(e.target.value)}
                  placeholder="Add extra search keywords (comma-separated)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  These keywords will be searchable in Global Search
                </p>
              </div>
              
              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleUpdateAdditionalProduct}
                  className="flex-1"
                  style={{ backgroundColor: enconOrange }}
                >
                  Save Changes
                </Button>
                <Button
                  onClick={() => setShowAdditionalProductModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Additional Product Data Upload Modal (drag & drop + browse, accepts either file format) */}
      <AdditionalProductDataUploadModal
        isOpen={showAdditionalProductUploadModal}
        onClose={() => setShowAdditionalProductUploadModal(false)}
        onUploaded={(data) => {
          setAdditionalProductDataCount(data.count || 0);
          loadAdditionalProductData(1, additionalProductDataSearch);
        }}
      />

    </>
  );
};

export default Settings;
